// src/pages/AODetail.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Device } from '@twilio/voice-sdk';
import { useParams, useNavigate } from 'react-router-dom';
import { kanbanAPI, aoAPI, candidateAPI, pipelineAPI, matchAPI, resourcesAPI, candidatePositioningAPI, companiesAPI, rtAPI, emailTemplatesAPI } from '../services/api';
import { PhoneLink } from '../utils/helpers';
import api from '../services/api';
import { Search, BrainCircuit } from 'lucide-react';
import { fmtDate, initials } from '../utils/helpers';
import TracabilityInfo from '../components/common/TracabilityInfo';
import { useAuth, extractError } from '../context/AuthContext';
import {
  ArrowLeft, MapPin, Building2, Calendar, Briefcase,
  Users, Star, Award, CheckCircle, XCircle, AlertCircle,
  Upload, ChevronDown, ChevronUp, UserCheck, MessageSquare,
  Play, RefreshCw, Phone, Mail, ExternalLink, Download,
  FileText, Trash2, FileDown, X, Edit3,
  ChevronRight, Zap, Save, Send, Plus,
  Globe, Info, AlertTriangle, Video, ClipboardCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import FichePoste from '../components/ao/FichePoste';
import { AssignIAModal, SendToTAModal } from '../components/ao/WorkflowModals';
import SearchProgressModal from '../components/ao/SearchProgressModal';
import RichTextEditor from '../components/common/RichTextEditor';
import CvOriginalModal from '../components/common/CvOriginalModal';
import CvConvertModal from '../components/common/CvConvertModal';
import ScoreCvModal from '../components/ao/ScoreCvModal';
import './AODetail.css';
import { AssignResponsableModal } from '../components/ao/WorkflowModals';

/* Texte qui défile si trop long — Web Animations API (pas de CSS keyframe) */
function ScrollText({ children, style }) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return;

    const apply = () => {
      // Annuler avant de mesurer pour éviter de mesurer pendant l'animation
      if (animRef.current) { animRef.current.cancel(); animRef.current = null; }

      const overflow = inner.scrollWidth - wrap.offsetWidth;
      if (overflow > 4) {
        const duration = Math.max(2000, overflow * 35);
        animRef.current = inner.animate(
          [{ transform: 'translateX(0px)' }, { transform: `translateX(-${overflow}px)` }],
          { duration, easing: 'ease-in-out', direction: 'alternate', iterations: Infinity, delay: 600, fill: 'both' }
        );
      }
    };

    const raf = requestAnimationFrame(apply);
    // Ne pas observer inner — ça crée une boucle infinie (animation → resize → apply → annuler → resize → ...)
    const ro = new ResizeObserver(() => requestAnimationFrame(apply));
    ro.observe(wrap);

    return () => { cancelAnimationFrame(raf); ro.disconnect(); if (animRef.current) animRef.current.cancel(); };
  }, []);

  return (
    <div ref={wrapRef} style={{ overflow: 'hidden', whiteSpace: 'nowrap', ...style }}>
      <span ref={innerRef} style={{ display: 'inline-block' }}>{children}</span>
    </div>
  );
}

const STATUS_LABELS = {
  NOUVEAU:                            { label: 'Nouveau',              color: '#60A5FA' },
  EN_COURS_DE_TRAITEMENT:             { label: 'En traitement',        color: '#d97706' },
  QUALIFICATION:                      { label: 'Qualification',        color: '#d97706' },
  ENVOYE_A_EQUIPE_TALENT_ACQUISITION: { label: 'Envoyé TA',            color: '#0891b2' },
  EN_COURS_DE_SOURCING:               { label: 'En sourcing',          color: '#d97706' },
  SOURCING:                           { label: 'Sourcing',             color: '#d97706' },
  SHORT_LIST_ENVOYEE:                 { label: 'Shortlist',            color: '#6366f1' },
  PROPOSE:                            { label: 'Shortlist validée',    color: '#6366f1' },
  TOP_PROFIL_ENVOYE_AU_CLIENT:        { label: 'Top profil',           color: '#059669' },
  REPONDU:                            { label: 'Répondu',              color: '#16a34a' },
  ENTRETIEN:                          { label: 'Entretien client',     color: '#f97316' },
  GAGNE:                              { label: 'Gagné',                color: '#4ADE80' },
  PERDU:                              { label: 'Perdu',                color: '#dc2626' },
  ANNULE:                             { label: 'Annulé',               color: '#475569' },
  SUPPRIME:                           { label: 'Supprimé',             color: 'var(--text-muted)' },
  TERMINE:                            { label: 'Clôturé',              color: 'var(--text-primary)' },
  ABANDONNE:                          { label: 'Abandonné',            color: '#78350f' },
};

const SOURCING_STATUSES = ['EN_COURS_DE_SOURCING', 'SOURCING'];
const TRAITEMENT_STATUSES = ['EN_COURS_DE_TRAITEMENT', 'QUALIFICATION'];
const SHORTLIST_VALIDATED_STATUSES = ['PROPOSE', 'SHORT_LIST_ENVOYEE', 'TOP_PROFIL_ENVOYE_AU_CLIENT', 'REPONDU', 'ENTRETIEN', 'GAGNE'];

const HIDE_STATS_STATUSES = [
  'NOUVEAU',
  'EN_COURS_DE_TRAITEMENT',
  'QUALIFICATION',
  'ENVOYE_A_EQUIPE_TALENT_ACQUISITION',
];


const scoreColor  = (s) => s >= 75 ? '#4ADE80' : s >= 50 ? '#d97706' : '#dc2626';
const scoreBg     = (s) => s >= 75 ? '#dcfce7' : s >= 50 ? '#fef9c3' : '#fee2e2';
const scoreBorder = (s) => s >= 75 ? '#86efac' : s >= 50 ? '#fcd34d' : '#fca5a5';


export default function AODetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();


  const [ao, setAO] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('tous');
  const [expanded, setExpanded] = useState({});
  const [scoring, setScoring] = useState(false);
  const [showScoreCvModal, setShowScoreCvModal] = useState(false);
  const [pipelining, setPipelining] = useState(false);
  const [searching, setSearching] = useState(null);
  const [searchProgressSource, setSearchProgressSource] = useState(null);
  const [showFiche, setShowFiche] = useState(false);
  const [cvModal, setCvModal] = useState(null);
  const [showEditAO, setShowEditAO] = useState(false);
  const [assignIAModal, setAssignIAModal] = useState(false);
  const [sendTAModal, setSendTAModal] = useState(false);
  const [rtList,      setRtList]      = useState([]);
  const [assigningRT, setAssigningRT] = useState(false);
  const [aoReferent,  setAoReferent]  = useState(null);
  const [addingToPipe, setAddingToPipe] = useState({});
  const [pipelineMap,  setPipelineMap]  = useState({}); // { candidateId: pipelineEntryId }
  const [twilioDevice, setTwilioDevice] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  const [callingCandidateId, setCallingCandidateId] = useState(null);


  const fetchData = async () => {
    setLoading(true);
    try {
      const [aoRes, matchRes] = await Promise.allSettled([
        kanbanAPI.getDetail(id),
        api.get(`/ao/${id}/matches`),
      ]);


      let aoData = null;
      let kanbanCandidates = [];
      let rawMatches = [];


      if (aoRes.status === 'fulfilled') {
        aoData = aoRes.value.data;
        kanbanCandidates = aoData?.candidates || [];
      }


      if (matchRes.status === 'fulfilled') {
        rawMatches = matchRes.value.data || [];
      }


      setAO(aoData);


      const matchMap = {};
      rawMatches.forEach((m) => {
        matchMap[String(m.candidate_id)] = m;
      });


      const kanbanIds = new Set(kanbanCandidates.map((kc) => String(kc.candidate_id)));


      let merged = kanbanCandidates.map((kc) => {
        const raw = matchMap[String(kc.candidate_id)] || {};
        return {
          match_id: kc.match_id,
          candidate_id: kc.candidate_id,
          full_name: kc.full_name,
          title: kc.title,
          source: kc.source,
          location: kc.location,
          score_overall: kc.score_overall ?? raw.score_overall,
          shortlist: kc.shortlist ?? false,
          top_profil: kc.top_profil ?? false,
          shortlist_brouillon: kc.shortlist_brouillon ?? false,
          top_profil_brouillon: kc.top_profil_brouillon ?? false,
          prequalified_soft: raw.prequalified_soft ?? false,
          prequalified_hard: raw.prequalified_hard ?? false,
          disqualifie: raw.disqualifie ?? false,
          softskills_report: raw.softskills_report || null,
          hardskills_report: raw.hardskills_report || null,
          call_transcript: raw.call_transcript || null,
          call_summary_ai: raw.call_summary_ai || null,
          report: kc.report || raw.report,
          strengths: kc.strengths || [],
          gaps: kc.gaps || [],
          missing_skills: kc.missing_skills || [],
          interview_questions: kc.interview_questions || [],
          final_recommendation: kc.final_recommendation,
          score_breakdown: raw.score_breakdown,
          match_status: raw.match_status,
          email: null,
          phone: null,
          linkedin_link: null,
          cv_minio_path: null,
          cv_link: null,
          _loaded: false,
        };
      });


      rawMatches.forEach((m) => {
        if (!kanbanIds.has(String(m.candidate_id))) {
          const report = m.report || {};
          merged.push({
            match_id: m.id,
            candidate_id: m.candidate_id,
            full_name: report.full_name || `Candidat #${String(m.candidate_id).slice(0, 8)}`,
            title: report.title || '',
            source: m.source_site,
            location: report.location || '',
            score_overall: m.score_overall,
            shortlist: false,
            top_profil: false,
            shortlist_brouillon: false,
            top_profil_brouillon: false,
            prequalified_soft: m.prequalified_soft ?? false,
            prequalified_hard: m.prequalified_hard ?? false,
            disqualifie: m.disqualifie ?? false,
            softskills_report: m.softskills_report || null,
            hardskills_report: m.hardskills_report || null,
            call_transcript: m.call_transcript || null,
            call_summary_ai: m.call_summary_ai || null,
            report: m.report,
            strengths: report.strengths || [],
            gaps: report.gaps || [],
            missing_skills: report.missing_skills || [],
            interview_questions: report.interview_questions || [],
            final_recommendation: report.final_recommendation,
            score_breakdown: m.score_breakdown,
            match_status: m.match_status,
            email: null,
            phone: null,
            linkedin_link: null,
            cv_minio_path: null,
            cv_link: null,
            _loaded: false,
          });
        }
      });


      setCandidates(merged);

      // Charger la map pipeline pour savoir lesquels sont déjà dans le pipe
      try {
        const candidateIds = merged.map(c => String(c.candidate_id)).filter(Boolean);
        if (candidateIds.length > 0) {
          const pipeRes = await pipelineAPI.checkCandidates(candidateIds);
          setPipelineMap(pipeRes.data || {});
        }
      } catch {}

      // Charger référent technique de cet AO
      try {
        const refRes = await rtAPI.getAOReferent(id);
        setAoReferent(refRes.data?.referent_technique || null);
      } catch {}

      // Charger liste RTs disponibles (une fois)
      try {
        const rtRes = await rtAPI.listRTs();
        setRtList(rtRes.data || []);
      } catch {}

      // Charger rapports RT pour cet AO (map candidateId -> reports[])
      try {
        const rtRepRes = await rtAPI.getAOReports(id);
        const byCandidate = {};
        (rtRepRes.data || []).forEach(r => {
          if (!byCandidate[r.candidate_id]) byCandidate[r.candidate_id] = [];
          byCandidate[r.candidate_id].push(r);
        });
        setRtReportsByCandidate(byCandidate);
      } catch {}
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchData();
  }, [id]);


  const loadCandidateDetails = async (candidateId) => {
    try {
      const res = await candidateAPI.detail(candidateId);
      const d = res.data;

      setCandidates((prev) =>
        prev.map((c) =>
          String(c.candidate_id) === String(candidateId)
            ? {
                ...c,
                email: d.email,
                phone: d.phone,
                linkedin_link: d.linkedin_link,
                cv_minio_path: d.cv_minio_path,
                cv_link: d.cv_link,
                profile_url: d.profile_url,
                experience: d.experience || c.experience,
                availability: d.availability,
                salary: d.salary,
                summary_ai: d.summary_ai,
                skills: d.skills,
                languages: d.languages,
                _loaded: true,
              }
            : c
        )
      );
    } catch {}
  };


  const handleDownloadCV = async (c) => {
    try {
      if (c.cv_minio_path) {
        const res = await candidateAPI.getCvUrl(c.candidate_id);
        if (res.data?.cv_url) {
          window.open(res.data.cv_url, '_blank');
          return;
        }
      }


      if (c.cv_link?.startsWith('http')) {
        window.open(c.cv_link, '_blank');
        return;
      }


      if (c.profile_url?.startsWith('http')) {
        window.open(c.profile_url, '_blank');
        return;
      }


      toast.error('CV non disponible');
    } catch {
      toast.error('Erreur téléchargement CV');
    }
  };


  const handleReplaceCV = async (candidateId, file) => {
    try {
      const res = await candidateAPI.uploadCv(candidateId, file);
      setCandidates((prev) =>
        prev.map((c) =>
          String(c.candidate_id) === String(candidateId)
            ? { ...c, cv_minio_path: res.data.cv_minio_path, _loaded: true }
            : c
        )
      );
      toast.success('CV remplacé — pensez à rescorer le profil');
      return res.data;
    } catch (e) {
      toast.error(extractError(e));
      throw e;
    }
  };

  const handleDeleteCV = async (candidateId) => {
    if (!window.confirm('Supprimer le CV de ce candidat ?')) return;
    try {
      await candidateAPI.deleteCv(candidateId);
      setCandidates((prev) =>
        prev.map((c) =>
          String(c.candidate_id) === String(candidateId)
            ? { ...c, cv_minio_path: null, cv_link: null }
            : c
        )
      );
      toast.success('CV supprimé');
      setCvOriginalModal(null);
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const handleAction = async (action) => {
    try {
      if (action === 'take-charge') {
        await api.post(`/ao-management/${id}/take`);
      }
      if (action === 'take-sourcing') {
        await api.post(`/ao-management/${id}/take-sourcing`);
      }


      toast.success('Action effectuée');
      fetchData();
    } catch (e) {
      toast.error(extractError(e));
    }
  };


  const handleShortlist = async (candidateId, candidateObj) => {
    const current = candidateObj.shortlist || candidateObj.shortlist_brouillon;
    try {
      if (!current) {
        await kanbanAPI.shortlist(id, [candidateId]);
        toast.success('Ajouté à la shortlist');
      } else {
        await kanbanAPI.unshortlist(id, [candidateId]);
        toast.success('Retiré de la shortlist');
      }
      fetchData();
    } catch (e) {
      toast.error(extractError(e));
    }
  };


  const handleTopProfil = async (candidateId, current) => {
    try {
      if (!current) {
        await kanbanAPI.topProfil(id, [candidateId]);
        toast.success('Marqué top profil');
      } else {
        await kanbanAPI.untopProfil(id, [candidateId]);
        toast.success('Tag top profil retiré');
      }
      fetchData();
    } catch (e) {
      toast.error(extractError(e));
    }
  };


  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('Retirer ce candidat de cet AO ?')) return;
    try {
      await aoAPI.deleteMatch(id, matchId);
      toast.success('Candidat retiré');
      setCandidates((prev) => prev.filter((c) => String(c.match_id) !== String(matchId)));
    } catch (e) {
      toast.error(extractError(e));
    }
  };


  const handleValidateShortlist = async () => {
    try {
      await kanbanAPI.validateShortlist({ ao_id: id });
      toast.success('Shortlist validée');
      fetchData();
    } catch (e) {
      toast.error(extractError(e));
    }
  };


  const handleValidateTopProfil = async () => {
    try {
      await kanbanAPI.validateTopProfil({ ao_id: id });
      toast.success('Top profils validés');
      fetchData();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const handleMarkRepondu = async (selectedIds) => {
    try {
      await aoAPI.changeStatus(id, 'REPONDU', selectedIds);
      const n = selectedIds.length;
      toast.success(`AO marqué Répondu — ${n} profil${n > 1 ? 's' : ''} positionné${n > 1 ? 's' : ''}`);
      setReponduModal(false);
      fetchData();
    } catch (e) {
      toast.error(extractError(e));
    }
  };


  const handleScoreManualFile = async (file) => {
    if (!file) return;

    setScoring(true);
    const toastId = toast.loading('Scoring IA en cours… (15-30 sec)');

    try {
      const res = await aoAPI.scoreCvManual(id, file);
      toast.dismiss(toastId);
      await fetchData();
      toast.success(`${res.data.candidate_name || 'Candidat'} — score ajouté`);
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(extractError(err));
    } finally {
      setScoring(false);
    }
  };


  const handleAddToPipeline = async (candidate) => {
    const cid = candidate.candidate_id;
    if (addingToPipe[cid]) return;
    setAddingToPipe(prev => ({ ...prev, [cid]: true }));
    try {
      const res = await pipelineAPI.addFromCandidate(cid, { source_type: 'MANUEL', ao_id: id });
      toast.success(`${candidate.full_name || 'Candidat'} ajouté au pipe`);
      // Mettre à jour la map pipeline pour afficher "Consulter le pipe"
      if (res?.data?.id) {
        setPipelineMap(prev => ({ ...prev, [String(cid)]: String(res.data.id) }));
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg    = e?.response?.data?.detail || '';
      if (status === 409) {
        // Déjà dans le pipe — extraire l'ID depuis le message backend
        const match = msg.match(/entrée\s+([\w-]{36})/i);
        if (match) {
          // Basculer le bouton vers "Dans le pipe"
          setPipelineMap(prev => ({ ...prev, [String(cid)]: match[1] }));
          toast(`${candidate.full_name || 'Candidat'} est déjà dans le pipe`, { icon: '✓' });
        } else {
          // Pas d'ID — recharger la map pour retrouver l'entrée
          try {
            const pipeRes = await pipelineAPI.checkCandidates([String(cid)]);
            if (pipeRes.data?.[String(cid)]) {
              setPipelineMap(prev => ({ ...prev, [String(cid)]: pipeRes.data[String(cid)] }));
            }
          } catch {}
          toast(`${candidate.full_name || 'Candidat'} est déjà dans le pipe`, { icon: '✓' });
        }
      } else {
        toast.error(extractError(e));
      }
    } finally {
      setAddingToPipe(prev => ({ ...prev, [cid]: false }));
    }
  };

  const handleTwilioCall = async (candidate) => {
    if (!candidate.phone) { toast.error('Numéro de téléphone non disponible'); return; }
    try {
      const tokenRes = await api.get('/twilio/token');
      const device = new Device(tokenRes.data.token, { logLevel: 1 });
      await device.register();
      const cleanPhone = candidate.phone.replace(/\s+/g, '');
      const call = await device.connect({ params: { To: cleanPhone } });
      setTwilioDevice(device);
      setActiveCall(call);
      setCallingCandidateId(candidate.candidate_id);
      call.on('disconnect', () => {
        setActiveCall(null);
        setCallingCandidateId(null);
        setTwilioDevice(null);
      });
      toast.success(`Appel en cours vers ${candidate.full_name}…`);
    } catch (e) {
      toast.error('Erreur appel : ' + (e.message || String(e)));
      setActiveCall(null);
      setCallingCandidateId(null);
      setTwilioDevice(null);
    }
  };

  const handleHangupTwilio = () => {
    if (activeCall) activeCall.disconnect();
    if (twilioDevice) twilioDevice.destroy();
    setActiveCall(null);
    setCallingCandidateId(null);
    setTwilioDevice(null);
  };

  const handleStartPipeline = async () => {
    setPipelining(true);
    try {
      await api.post(`/ao/${id}/candidates/start`, {
        sources: { use_apec: true, use_turnover: true, use_linkedin: false },
        num_profiles_per_source: 10,
      });
      toast.success('Pipeline lancé');
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setPipelining(false);
    }
  };


  const handleSearch = async (source) => {
    setSearching(source);
    try {
      await aoAPI.searchCandidates(id, source, 10);
      setSearchProgressSource(source);
    } catch (e) {
      toast.error(extractError(e));
      setSearching(null);
    }
  };


  const toggle = async (candidateId) => {
    const isOpen = expanded[candidateId];
    setExpanded((p) => ({ ...p, [candidateId]: !isOpen }));
    if (!isOpen) {
      const cand = candidates.find((c) => String(c.candidate_id) === String(candidateId));
      if (cand && !cand._loaded) await loadCandidateDetails(candidateId);
    }
  };


  const [prequalModal, setPrequalModal] = useState(null);
  const [editReportModal,   setEditReportModal]   = useState(null); // { matchId, type: 'soft'|'hard', currentHtml }
  const [editAIReportModal,  setEditAIReportModal]  = useState(null); // { matchId, candidate }
  const [exportModalCR, setExportModalCR] = useState(false); // modal compte_rendu
  const [exportModalPU, setExportModalPU] = useState(false); // modal pro_unity
  const [gagneModal, setGagneModal] = useState(false);
  const [reponduModal, setReponduModal] = useState(false);
  const [cvOriginalModal, setCvOriginalModal] = useState(null); // candidate object
  const [unifiedCallModal, setUnifiedCallModal] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [entretienAOModal, setEntretienAOModal] = useState(null); // { candidateId, name, email, aoId }
  const [editCandidateModal, setEditCandidateModal] = useState(null); // candidate object
  const [scoreChangeModal, setScoreChangeModal] = useState(null); // { candidate, aoId }
  const [rtReportsByCandidate, setRtReportsByCandidate] = useState({}); // candidateId -> reports[]
  const [viewRTReportModal, setViewRTReportModal] = useState(null); // { report, candidate }
  const [taChatOpen, setTaChatOpen] = useState(false);

  const handleAdvance = async (matchId, candidateId, type, report) => {
    try {
      if (type === 'soft') {
        await matchAPI.prequalifySoft(matchId, report);
        toast.success('Préqualification soft enregistrée');
      } else if (type === 'hard') {
        await matchAPI.prequalifyHard(matchId, report);
        toast.success('Préqualification hard enregistrée');
      } else if (type === 'shortlist') {
        await kanbanAPI.shortlist(id, [candidateId]);
        toast.success('Candidat shortlisté');
      } else if (type === 'top') {
        await kanbanAPI.topProfil(id, [candidateId]);
        toast.success('Marqué top profil');
      }
      fetchData();
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleDisqualify = async (matchId, report) => {
    try {
      await matchAPI.disqualify(matchId, report);
      toast.success('Candidat disqualifié');
      fetchData();
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleMarkGagne = async (selectedCandidateIds, missionStartDate) => {
    const label = ao.job_title || 'Mission';
    const client = ao.client || '';

    try {
      await aoAPI.changeStatus(id, 'GAGNE');
    } catch (e) {
      toast.error('Erreur changement de statut : ' + extractError(e));
      return;
    }

    if (selectedCandidateIds.length === 0) {
      toast.success('AO marqué Gagné');
      setGagneModal(false);
      fetchData();
      return;
    }

    // Load details for candidates not yet expanded (email, phone, cv_minio_path…)
    const enriched = await Promise.all(
      selectedCandidateIds.map(async (cid) => {
        const cand = candidates.find((c) => String(c.candidate_id) === String(cid));
        if (!cand) return null;
        if (cand._loaded) return cand;
        try {
          const res = await candidateAPI.detail(cand.candidate_id);
          const d = res.data;
          return { ...cand, email: d.email, phone: d.phone, linkedin_link: d.linkedin_link, cv_minio_path: d.cv_minio_path, cv_link: d.cv_link, _loaded: true };
        } catch { return cand; }
      })
    );

    let successCount = 0;
    const errors = [];

    for (const cand of enriched) {
      if (!cand) continue;
      try {
        // 1. Deduplication: find existing resource by email or name
        let resourceId = null;

        if (cand.email) {
          try {
            const searchRes = await resourcesAPI.list({ search: cand.email });
            const existing = (searchRes.data || []).find((r) => r.email === cand.email);
            if (existing) resourceId = existing.id;
          } catch {}
        }
        if (!resourceId && cand.full_name) {
          try {
            const searchRes = await resourcesAPI.list({ search: cand.full_name });
            const existing = (searchRes.data || []).find((r) => r.full_name === cand.full_name);
            if (existing) resourceId = existing.id;
          } catch {}
        }

        if (resourceId) {
          await resourcesAPI.update(resourceId, {
            current_client: client,
            current_mission: label,
            mission_start_date: missionStartDate || null,
            status: 'ACTIF',
          });
        } else {
          const resourceRes = await resourcesAPI.create({
            full_name: cand.full_name || 'Inconnu',
            title: cand.title || '',
            email: cand.email || null,
            phone: cand.phone || null,
            linkedin_link: cand.linkedin_link || null,
            location: cand.location || null,
            availability: missionStartDate || null,
            current_client: client,
            current_mission: label,
            mission_start_date: missionStartDate || null,
            employee_type: 'INTERCO',
            status: 'ACTIF',
          });
          resourceId = resourceRes.data?.id;
        }

        // 2. Attach candidate's original CV to resource documents
        if (resourceId) {
          try {
            let cvUrl = null;
            if (cand.cv_minio_path) {
              const cvRes = await candidateAPI.getCvUrl(cand.candidate_id);
              cvUrl = cvRes.data?.cv_url;
            } else if (cand.cv_link?.startsWith('http')) {
              cvUrl = cand.cv_link;
            }
            if (cvUrl) {
              await resourcesAPI.createDocument(resourceId, {
                resource_id: resourceId,
                title: `CV Original — ${cand.full_name}`,
                document_type: 'CV',
                file_url: cvUrl,
              });
            }
          } catch (cvErr) {
            console.warn(`[GAGNE] Attach CV failed for ${cand.full_name}:`, cvErr);
          }
        }

        // 3. AO positioning
        await candidatePositioningAPI.create({
          candidate_id: cand.candidate_id,
          target_type: 'AO',
          target_id: id,
          target_label: `${label}${client ? ` – ${client}` : ''}`,
          status: 'POSITIONNE',
        });

        // 4. CRM client positioning (lookup company by name)
        if (client) {
          try {
            const companyRes = await companiesAPI.list({ search: client });
            const company = (companyRes.data || []).find((c) => c.name === client);
            if (company) {
              await candidatePositioningAPI.create({
                candidate_id: cand.candidate_id,
                target_type: 'CLIENT',
                target_id: company.id,
                target_label: client,
                status: 'POSITIONNE',
              });
            }
          } catch (crmErr) {
            console.warn(`[GAGNE] CRM client positioning failed for ${cand.full_name}:`, crmErr);
          }
        }

        successCount++;
      } catch (err) {
        errors.push(`${cand.full_name} : ${extractError(err)}`);
      }
    }

    if (errors.length > 0) errors.forEach((msg) => toast.error(msg, { duration: 6000 }));
    toast.success(
      successCount > 0
        ? `AO gagné — ${successCount} ressource${successCount > 1 ? 's' : ''} créée${successCount > 1 ? 's' : ''} et positionnée${successCount > 1 ? 's' : ''}`
        : 'AO marqué Gagné (aucune ressource créée)'
    );
    setGagneModal(false);
    fetchData();
  };

  const handleEditReport = async (matchId, type, report) => {
    try {
      if (type === 'soft') {
        await matchAPI.prequalifySoft(matchId, report);
        toast.success('Rapport soft skills mis à jour');
      } else if (type === 'hard') {
        await matchAPI.prequalifyHard(matchId, report);
        toast.success('Rapport hard skills mis à jour');
      }
      fetchData();
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleSaveCallReport = async (matchId, transcript, summary) => {
    try {
      await matchAPI.saveCallReport(matchId, { transcript, summary });
      toast.success('Compte-rendu enregistré');
      fetchData();
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleDeleteCallReport = async (matchId) => {
    if (!window.confirm('Supprimer ce résumé d\'appel ?')) return;
    try {
      await matchAPI.clearCallReport(matchId);
      toast.success('Résumé supprimé');
      setCandidates((prev) =>
        prev.map((c) =>
          String(c.match_id) === String(matchId)
            ? { ...c, call_summary_ai: null, call_transcript: null }
            : c
        )
      );
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleSendEmail = async (matchId, subject, body, ccEmails = []) => {
    try {
      await matchAPI.sendEmail(matchId, { subject, body, cc_emails: ccEmails });
      toast.success('Email envoyé' + (ccEmails.length ? ` (CC : ${ccEmails.length} personne${ccEmails.length > 1 ? 's' : ''})` : ''));
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleScoreChange = async (candidateId, aoId, newScore, reason) => {
    try {
      await rtAPI.changeScore({ candidate_id: candidateId, ao_id: aoId, new_score: newScore, reason });
      setCandidates(prev => prev.map(c =>
        String(c.candidate_id) === String(candidateId) ? { ...c, score_overall: newScore } : c
      ));
      toast.success('Score modifié');
      setScoreChangeModal(null);
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleScheduleEntretienFromAO = async (candidateId, data) => {
    try {
      const res = await pipelineAPI.scheduleInterview(candidateId, data);
      toast.success(res.data.email_sent
        ? 'Entretien planifié — email et lien Teams envoyés au candidat'
        : 'Entretien planifié — lien Teams généré (email non envoyé)');
      setEntretienAOModal(null);
      setEmailModal(null);
    } catch (e) {
      toast.error(extractError(e));
      throw e;
    }
  };

  const sorted = [...candidates].sort((a, b) => (b.score_overall || 0) - (a.score_overall || 0));
  const shortlist = sorted.filter((c) => c.shortlist_brouillon || c.shortlist);
  const topProfil = sorted.filter((c) => c.top_profil_brouillon || c.top_profil);
  const prequalSoftList = sorted.filter((c) => c.prequalified_soft);
  const prequalHardList = sorted.filter((c) => c.prequalified_hard);


  const role = (user?.role || '').toUpperCase();
  const isTA = role === 'TALENT_ACQUISITION';
  const isSuperAdmin = role === 'SUPER_ADMIN';
  const isAdmin = role === 'ADMIN' || isSuperAdmin;
  const isIA = role === 'BUSINESS_MANAGER';
  const isRT = role === 'REFERENT_TECHNIQUE';
  const isRH = role === 'RH';
  const canTopProfil = (isAdmin || isIA || isTA) && SHORTLIST_VALIDATED_STATUSES.includes((ao?.ao_avancement || '').toUpperCase());


  // ── Helpers texte ──────────────────────────────────────────
  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  };

  // ── Extraction données candidat ────────────────────────────
  const getCandSkills = (c) => {
    const report = c.report || {};
    if (Array.isArray(c.skills) && c.skills.length)       return c.skills;
    if (Array.isArray(report.skills) && report.skills.length) return report.skills;
    // Extraire depuis score_breakdown keys si disponible
    const breakdown = c.score_breakdown || {};
    const keys = Object.keys(breakdown);
    if (keys.length) return keys;
    return [];
  };

  const getCandStrengths = (c) => {
    const report = c.report || {};
    const arr = c.strengths?.length ? c.strengths : (report.strengths || []);
    return arr.join('\n');
  };

  const getCandGaps = (c) => {
    const report = c.report || {};
    const gaps    = c.gaps?.length     ? c.gaps     : (report.gaps || []);
    const missing = c.missing_skills?.length ? c.missing_skills : (report.missing_skills || []);
    return [...gaps, ...missing.map(m => `[Manquant] ${m}`)].join('\n');
  };

  // ── Chargement des détails candidats pour export ───────────
  const fetchDetailsForExport = async (candidateList) => {
    const results = await Promise.allSettled(
      candidateList.map(async (c) => {
        if (c._loaded) return c;
        try {
          const res = await candidateAPI.detail(c.candidate_id);
          const d = res.data;
          return {
            ...c,
            email:         d.email,
            phone:         d.phone,
            linkedin_link: d.linkedin_link,
            cv_minio_path: d.cv_minio_path,
            cv_link:       d.cv_link,
            experience:    d.experience || c.experience,
            availability:  d.availability || c.availability,
            salary:        d.salary || c.salary,
            mobility:      d.mobility || c.mobility,
            skills:        d.skills   || c.skills,
            languages:     d.languages,
            _loaded: true,
          };
        } catch { return c; }
      })
    );
    return results.map(r => r.status === 'fulfilled' ? r.value : r.reason);
  };

  // ── Export Compte Rendu ─────────────────────────────────────
  const exportCompteRendu = async (selSet) => {
    const base = selSet && selSet.size > 0
      ? sorted.filter(c => selSet.has(String(c.candidate_id)))
      : sorted;
    if (!base.length) return;
    const list = await fetchDetailsForExport(base);

    const headers = [
      'Rôle ciblé', 'Candidat', 'Expérience (ans)', 'Compétences techniques clés',
      'Adéquation mission (scoring)', 'Disponibilité', 'Localisation / Mobilité',
      'TJM / Salaire', 'Avis recruteur', 'Points de vigilance',
    ];

    const rows = list.map(c => ({
      'Rôle ciblé':                    ao?.job_title || '',
      'Candidat':                       c.full_name || '',
      'Expérience (ans)':               c.experience || '',
      'Compétences techniques clés':    getCandSkills(c).join(', '),
      'Adéquation mission (scoring)':   c.score_overall != null ? `${c.score_overall}%` : '',
      'Disponibilité':                   c.availability || '',
      'Localisation / Mobilité':         [c.location, c.mobility].filter(Boolean).join(' / '),
      'TJM / Salaire':                   c.salary || '',
      'Avis recruteur':                  getCandStrengths(c) || stripHtml(c.softskills_report),
      'Points de vigilance':             getCandGaps(c),
    }));

    const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
    ws['!cols'] = [
      { wch:28 }, { wch:22 }, { wch:14 }, { wch:40 }, { wch:22 },
      { wch:18 }, { wch:26 }, { wch:16 }, { wch:52 }, { wch:52 },
    ];
    // Style header
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c: col })];
      if (cell) {
        cell.s = { font: { bold: true }, fill: { fgColor: { rgb: '3B82F6' } }, alignment: { wrapText: true } };
      }
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Compte rendu');
    const filename = `compte_rendu_${(ao?.job_title || 'ao').replace(/[^a-z0-9]/gi, '_')}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // ── Export Pro Unity ────────────────────────────────────────
  const exportProUnity = async (selSet) => {
    const base = selSet && selSet.size > 0
      ? sorted.filter(c => selSet.has(String(c.candidate_id)))
      : sorted;
    if (!base.length) return;
    const list = await fetchDetailsForExport(base);

    const wb = XLSX.utils.book_new();
    const aoSkills = Array.isArray(ao?.tech_skills) ? ao.tech_skills : [];
    const aoSoft   = Array.isArray(ao?.soft_skills)  ? ao.soft_skills  : [];
    const aoCerts  = Array.isArray(ao?.certifications) ? ao.certifications : [];
    const allAoSkills = [...new Set([...aoSkills, ...aoSoft, ...aoCerts])];

    list.forEach((c, idx) => {
      const candSkills = getCandSkills(c);
      const allSkills  = [...new Set([...allAoSkills, ...candSkills])];

      const rows = allSkills.map(skill => {
        const inAO   = allAoSkills.some(s => s.toLowerCase() === skill.toLowerCase());
        const inCand = candSkills.some(s => s.toLowerCase() === skill.toLowerCase());
        // Score breakdown si disponible pour ce critère
        const breakdown = c.score_breakdown || {};
        const bdKey = Object.keys(breakdown).find(k => k.toLowerCase() === skill.toLowerCase());
        const bdScore = bdKey != null ? breakdown[bdKey] : null;
        return {
          'Compétence':         skill,
          'Niveau demandé':     inAO   ? 'Requis'         : 'Souhaité',
          'Niveau consultant':  inCand ? (bdScore != null ? `Maîtrisé (${bdScore}%)` : 'Maîtrisé') : 'Non mentionné',
          'Commentaire':        '',
        };
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch:32 }, { wch:18 }, { wch:22 }, { wch:42 }];
      const raw = (c.full_name || `Candidat${idx + 1}`);
      const sheetName = raw.slice(0, 31).replace(/[\\/?*[\]:]/g, '_');
      XLSX.utils.book_append_sheet(wb, ws, sheetName || `Candidat${idx + 1}`);
    });

    XLSX.writeFile(wb, 'ao_pro_unity.xlsx');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12, color: 'var(--text-muted)' }}>
        <div className="spinner" /> Chargement…
      </div>
    );
  }


  if (!ao) {
    return (
      <div className="empty-state">
        <h4>AO introuvable</h4>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          <ArrowLeft size={15} /> Retour
        </button>
      </div>
    );
  }


  const statusKey = (ao.ao_avancement || '').toUpperCase();
  const cfg = STATUS_LABELS[statusKey] || { label: ao.ao_avancement, color: 'var(--text-muted)' };
  const tjm = ao.tjm || ao.remuneration_avantages?.tjm || '';
  const shouldHideStats = HIDE_STATS_STATUSES.includes(statusKey);
  const showCandidatesPanel = !HIDE_STATS_STATUSES.includes(statusKey);
  const canValidateShortlist = shortlist.length >= 1;
  const pendingShortlistCount = shortlist.filter((c) => c.shortlist_brouillon && !c.shortlist).length;
  const pendingTopProfilCount = topProfil.filter((c) => c.top_profil_brouillon && !c.top_profil).length;


  return (
    <>
      <div
        className="ao-split-shell"
        style={{
          height: 'calc(100vh - var(--topbar-height, 64px) - 56px)',
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="ao-split-topbar">
          {!isRT && (
            <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
              <ArrowLeft size={15} /> Retour au pipeline
            </button>
          )}


          <div className="ao-topbar-right">
            {showCandidatesPanel && !isRT && (
              <>
                <div className="ao-search-group">
                  <span className="ao-search-label">Chercher des candidats :</span>
                  {[
                    { key: 'apec', label: 'APEC', color: '#60A5FA' },
                    { key: 'turnover', label: 'Turnover IT', color: '#0891b2' },
                    { key: 'linkedin', label: 'LinkedIn', color: '#0A66C2' },
                  ].map(({ key, label, color }) => (
                    <button
                      key={key}
                      className="btn btn-sm ao-search-btn"
                      disabled={!!searching}
                      onClick={() => handleSearch(key)}
                      style={{
                        borderColor: color,
                        color: searching === key ? 'white' : color,
                        background: searching === key ? color : `${color}0f`,
                        opacity: searching && searching !== key ? 0.45 : 1,
                        cursor: searching && searching !== key ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {searching === key ? (
                        <>
                          <div className="spinner" style={{ width: 11, height: 11, borderColor: `${color}33`, borderTopColor: 'white' }} /> {label}…
                        </>
                      ) : (
                        <>
                          <Search size={11} /> {label}
                        </>
                      )}
                    </button>
                  ))}
                  <button
                    className="btn btn-sm ao-search-btn-all"
                    disabled={!!searching}
                    onClick={() => handleSearch('all')}
                    style={{ opacity: searching && searching !== 'all' ? 0.45 : 1 }}
                  >
                    {searching === 'all' ? (
                      <>
                        <div className="spinner" style={{ width: 11, height: 11, borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white' }} /> Tous…
                      </>
                    ) : (
                      <>
                        <Play size={11} /> Tous les sites
                      </>
                    )}
                  </button>
                </div>

                <div className="ao-topbar-divider" />

                <button
                  className="btn btn-secondary btn-sm"
                  disabled={scoring}
                  onClick={() => setShowScoreCvModal(true)}
                  style={{ opacity: scoring ? 0.7 : 1 }}
                >
                  {scoring ? (
                    <>
                      <div className="spinner" style={{ width: 13, height: 13 }} /> Scoring…
                    </>
                  ) : (
                    <>
                      <Upload size={13} /> Scorer un CV
                    </>
                  )}
                </button>

                <div className="ao-topbar-divider" />
              </>
            )}

            <button className="btn btn-ghost btn-sm" onClick={fetchData} title="Actualiser">
              <RefreshCw size={13} />
            </button>

            {/* Boutons export */}
            {sorted.length > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={() => setExportModalCR(true)} title="Exporter compte rendu Excel">
                <Download size={13}/> Exporter Excel
              </button>
            )}
            {sorted.length > 0 && (
              <button className="btn btn-secondary btn-sm" onClick={() => setExportModalPU(true)} title="Exporter Pro Unity Excel">
                <Download size={13}/> Pro Unity
              </button>
            )}
          </div>
        </div>


        {statusKey === 'ABANDONNE' && ao.abandon_comment && (
          <div style={{
            margin:'0 0 0 0', padding:'12px 24px',
            background:'rgba(245,158,11,0.10)', borderBottom:'1px solid rgba(245,158,11,0.25)',
            display:'flex', alignItems:'flex-start', gap:10,
          }}>
            <XCircle size={16} color="#78350f" style={{ flexShrink:0, marginTop:2 }}/>
            <div>
              <span style={{ fontWeight:700, fontSize:'0.8125rem', color:'#78350f' }}>AO abandonné — </span>
              <span style={{ fontSize:'0.8125rem', color:'#92400e' }}>{ao.abandon_comment}</span>
            </div>
          </div>
        )}

        <div className={`ao-split-body${showCandidatesPanel ? '' : ' ao-split-body--no-left'}`}>
          {showCandidatesPanel && <div className="ao-split-left">
            <div className="ao-tabs">
              {[
                { key: 'tous',        label: 'Tous les profils',   count: sorted.length,         show: true },
                { key: 'prequalSoft', label: 'Préqual. Soft',      count: prequalSoftList.length, show: !SHORTLIST_VALIDATED_STATUSES.includes(statusKey) },
                { key: 'prequalHard', label: 'Préqual. Hard',      count: prequalHardList.length, show: !SHORTLIST_VALIDATED_STATUSES.includes(statusKey) },
                { key: 'shortlist',   label: 'Shortlist',          count: shortlist.length,       show: true },
                { key: 'top',         label: 'Top profils',        count: topProfil.length,       show: SHORTLIST_VALIDATED_STATUSES.includes(statusKey) },
              ].filter((t) => t.show).map((t) => (
                <button key={t.key} className={`ao-tab${tab === t.key ? ' ao-tab-active' : ''}`} onClick={() => setTab(t.key)}>
                  {t.label}
                  <span className={`ao-tab-badge${tab === t.key ? ' ao-tab-badge-active' : ''}`}>{t.count}</span>
                </button>
              ))}
            </div>


            <div className="ao-tab-content">
              {tab === 'tous' && (
                <CandidateList
                  candidates={sorted}
                  expanded={expanded}
                  onToggle={toggle}
                  onDownloadCV={handleDownloadCV}
                  onOpenCvModal={(c) => setCvOriginalModal(c)}
                  onDelete={!isRT ? handleDeleteMatch : null}
                  onAddToPipe={!isRT ? handleAddToPipeline : null}
                  addingToPipe={addingToPipe}
                  pipelineMap={pipelineMap}
                  showShortlist={false}
                  showTopProfil={false}
                  canTopProfil={false}
                  onEmail={!isRT ? (c) => setEmailModal({ matchId: c.match_id, name: c.full_name, email: c.email, candidateId: c.candidate_id }) : null}
                  onUnifiedCall={!isRT ? (c) => setUnifiedCallModal(c) : null}
                  onHangupTwilio={handleHangupTwilio}
                  callingCandidateId={callingCandidateId}
                  onDeleteCallReport={!isRT ? handleDeleteCallReport : null}
                  onEditCandidate={!isRT ? (c) => setEditCandidateModal(c) : null}
                  onEditReport={(c, type) => setEditReportModal({ matchId: c.match_id, type, currentHtml: type === 'soft' ? c.softskills_report : c.hardskills_report })}
                  onEditAIReport={(c) => setEditAIReportModal({ matchId: c.match_id, candidate: c })}
                  onConvertCV={!isRT ? (c) => setCvModal(c) : null}
                  advanceLabel={!isRT && !SHORTLIST_VALIDATED_STATUSES.includes(statusKey) ? "Préqual. Soft →" : null}
                  onAdvance={!isRT && !SHORTLIST_VALIDATED_STATUSES.includes(statusKey) ? (c) => setPrequalModal({ matchId: c.match_id, candidateId: c.candidate_id, type: 'soft' }) : null}
                  rtReportsByCandidate={rtReportsByCandidate}
                  canWriteRTReport={!SHORTLIST_VALIDATED_STATUSES.includes(statusKey) && (isRT || isIA || isAdmin)}
                  canViewRTReport={isRT || isIA || isAdmin || isTA}
                  onWriteRTReport={(c) => navigate(`/rt-report/${id}/${c.candidate_id}`)}
                  onViewRTReport={(c, reports) => setViewRTReportModal({ reports, candidate: c })}
                  onScoreChange={(c) => setScoreChangeModal({ candidate: c, aoId: id })}
                  emptyMessage="Aucun profil scoré — uploadez un CV ou lancez le pipeline"
                />
              )}


              {tab === 'shortlist' && (
                <>
                  {(isTA || isAdmin || isIA) && shortlist.length > 0 && SOURCING_STATUSES.includes(statusKey) && (
                    <div className="ao-validate-banner" style={{ background: 'rgba(245,158,11,0.08)', borderBottom: '1px solid rgba(245,158,11,0.20)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#FCD34D' }}>
                          {shortlist.length} candidat{shortlist.length > 1 ? 's' : ''} en shortlist
                        </span>
                        {pendingShortlistCount > 0 && (
                          <span style={{ fontSize: '0.75rem', color: '#b45309' }}>
                            {pendingShortlistCount} en attente de validation
                          </span>
                        )}
                      </div>
                      <button
                        className="btn btn-sm"
                        onClick={handleValidateShortlist}
                        style={{ background: '#16A34A', color: 'white', border: 'none', marginLeft: 'auto', whiteSpace: 'nowrap' }}
                      >
                        <CheckCircle size={13} /> Valider la shortlist
                      </button>
                    </div>
                  )}

                  <CandidateList
                    candidates={shortlist}
                    expanded={expanded}
                    onToggle={toggle}
                    onShortlist={handleShortlist}
                    onDownloadCV={handleDownloadCV}
                    onOpenCvModal={(c) => setCvOriginalModal(c)}
                    onDelete={!isRT ? handleDeleteMatch : null}
                    onAddToPipe={!isRT ? handleAddToPipeline : null}
                    addingToPipe={addingToPipe}
                    showShortlist
                    shortlistValidated={canTopProfil}
                    showTopProfil={canTopProfil}
                    canTopProfil={canTopProfil}
                    onTopProfil={(cId, cObj) => handleTopProfil(cId, cObj.top_profil || cObj.top_profil_brouillon)}
                    onEmail={(c) => setEmailModal({ matchId: c.match_id, name: c.full_name, email: c.email, candidateId: c.candidate_id })}
                    onUnifiedCall={(c) => setUnifiedCallModal(c)}
                    onHangupTwilio={handleHangupTwilio}
                    callingCandidateId={callingCandidateId}
                    onDeleteCallReport={handleDeleteCallReport}
                    onEditCandidate={(c) => setEditCandidateModal(c)}
                    onEditReport={(c, type) => setEditReportModal({ matchId: c.match_id, type, currentHtml: type === 'soft' ? c.softskills_report : c.hardskills_report })}
                  onEditAIReport={(c) => setEditAIReportModal({ matchId: c.match_id, candidate: c })}
                    onConvertCV={(c) => setCvModal(c)}
                    emptyMessage="Aucun candidat shortlisté"
                  />
                </>
              )}


              {tab === 'prequalSoft' && (
                <CandidateList
                  candidates={prequalSoftList}
                  expanded={expanded}
                  onToggle={toggle}
                  onShortlist={handleShortlist}
                  onDownloadCV={handleDownloadCV}
                  onOpenCvModal={(c) => setCvOriginalModal(c)}
                  onDelete={!isRT ? handleDeleteMatch : null}
                  onAddToPipe={!isRT ? handleAddToPipeline : null}
                  addingToPipe={addingToPipe}
                  pipelineMap={pipelineMap}
                  showShortlist={false}
                  showTopProfil={false}
                  canTopProfil={false}
                  onEmail={(c) => setEmailModal({ matchId: c.match_id, name: c.full_name, email: c.email, candidateId: c.candidate_id })}
                  onUnifiedCall={(c) => setUnifiedCallModal(c)}
                  onHangupTwilio={handleHangupTwilio}
                  callingCandidateId={callingCandidateId}
                  onDeleteCallReport={handleDeleteCallReport}
                  onEditCandidate={(c) => setEditCandidateModal(c)}
                  onEditReport={(c, type) => setEditReportModal({ matchId: c.match_id, type, currentHtml: type === 'soft' ? c.softskills_report : c.hardskills_report })}
                  onEditAIReport={(c) => setEditAIReportModal({ matchId: c.match_id, candidate: c })}
                  advanceLabel="Préqual. Hard →"
                  onAdvance={(c) => setPrequalModal({ matchId: c.match_id, candidateId: c.candidate_id, type: 'hard' })}
                  emptyMessage="Aucun profil préqualifié soft — utilisez le bouton dans l'onglet Tous les profils"
                />
              )}

              {tab === 'prequalHard' && (
                <CandidateList
                  candidates={prequalHardList}
                  expanded={expanded}
                  onToggle={toggle}
                  onShortlist={handleShortlist}
                  onDownloadCV={handleDownloadCV}
                  onOpenCvModal={(c) => setCvOriginalModal(c)}
                  onDelete={!isRT ? handleDeleteMatch : null}
                  onAddToPipe={!isRT ? handleAddToPipeline : null}
                  addingToPipe={addingToPipe}
                  pipelineMap={pipelineMap}
                  showShortlist={false}
                  showTopProfil={false}
                  canTopProfil={false}
                  onEmail={(c) => setEmailModal({ matchId: c.match_id, name: c.full_name, email: c.email, candidateId: c.candidate_id })}
                  onUnifiedCall={(c) => setUnifiedCallModal(c)}
                  onHangupTwilio={handleHangupTwilio}
                  callingCandidateId={callingCandidateId}
                  onDeleteCallReport={handleDeleteCallReport}
                  onEditCandidate={(c) => setEditCandidateModal(c)}
                  onEditReport={(c, type) => setEditReportModal({ matchId: c.match_id, type, currentHtml: type === 'soft' ? c.softskills_report : c.hardskills_report })}
                  onEditAIReport={(c) => setEditAIReportModal({ matchId: c.match_id, candidate: c })}
                  advanceLabel="→ Shortlist"
                  onAdvance={(c) => setPrequalModal({ matchId: c.match_id, candidateId: c.candidate_id, type: 'shortlist' })}
                  emptyMessage="Aucun profil préqualifié hard — utilisez le bouton dans Préqual. Soft"
                />
              )}

              {tab === 'top' && (
                <>
                  {canTopProfil && pendingTopProfilCount > 0 && ['PROPOSE', 'SHORT_LIST_ENVOYEE'].includes(statusKey) && (
                    <div className="ao-validate-banner" style={{ background: 'rgba(96,165,250,0.07)', borderBottom: '1px solid rgba(96,165,250,0.22)' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#38BDF8' }}>
                          {topProfil.length} top profil{topProfil.length > 1 ? 's' : ''}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#38BDF8' }}>
                          {pendingTopProfilCount} en attente de validation
                        </span>
                      </div>
                      <button
                        className="btn btn-sm"
                        onClick={handleValidateTopProfil}
                        style={{ background: '#16A34A', color: 'white', border: 'none', marginLeft: 'auto', whiteSpace: 'nowrap' }}
                      >
                        <CheckCircle size={13} /> Valider le top profil
                      </button>
                    </div>
                  )}


                  <TopProfilList
                    candidates={topProfil}
                    expanded={expanded}
                    onToggle={toggle}
                    onTopProfil={handleTopProfil}
                    onDownloadCV={handleDownloadCV}
                    onDelete={handleDeleteMatch}
                    onConvertCV={(c) => setCvModal(c)}
                    ao={ao}

                  />
                </>
              )}
            </div>
          </div>}


          <div className="ao-split-right">
            {showCandidatesPanel ? (<>
            <div className="ao-right-header">
              <div className="ao-right-header-top">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span className="ao-status-badge" style={{ background: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}33` }}>
                    {cfg.label}
                  </span>
                  {ao.source_type && <span className="ao-source-badge">{ao.source_type}</span>}
                  {ao.ao_code && (
                    ao.fiche_poste_url
                      ? <a href={ao.fiche_poste_url} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize:'0.75rem', fontWeight:700, color:'#60A5FA', textDecoration:'none', padding:'3px 8px', borderRadius:6, background:'rgba(96,165,250,0.08)', border:'1px solid rgba(96,165,250,0.2)' }}>
                          #{ao.ao_code} ↗
                        </a>
                      : <span style={{ whiteSpace:'nowrap', fontSize:'0.75rem', fontWeight:700, color:'var(--text-muted)', padding:'3px 8px', borderRadius:6, background:'rgba(255,255,255,0.06)' }}>
                          #{ao.ao_code}
                        </span>
                  )}
                </div>


                <div style={{ display: 'flex', gap: 6 }}>
                  {(isAdmin || isIA) && (
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowEditAO(true)}>
                      <Edit3 size={13} /> Modifier
                    </button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowFiche(true)}>
                    <FileDown size={13} /> Fiche PDF
                  </button>
                </div>
              </div>


              <h2 className="ao-right-title">{ao.job_title || 'Sans titre'}</h2>


              <div className="ao-right-meta">
                {ao.client && <span className="ao-meta-pill"><Building2 size={11} />{ao.client}</span>}
                {ao.location && <span className="ao-meta-pill"><MapPin size={11} />{ao.location}</span>}
                {ao.contract_type && <span className="ao-meta-pill"><Briefcase size={11} />{ao.contract_type}</span>}
                {tjm && <span className="ao-meta-pill">💰 {tjm}</span>}
                {ao.work_mode && <span className="ao-meta-pill">🏠 {ao.work_mode}</span>}
                {ao.experience_years && <span className="ao-meta-pill">⏱ {ao.experience_years}</span>}
                {ao.response_deadline && <span className="ao-meta-pill"><Calendar size={11} />Limite : {fmtDate(ao.response_deadline)}</span>}
              </div>


              {/* Traçabilité */}
              <div style={{ margin: '10px 0' }}>
                <TracabilityInfo
                  createdAt={ao.created_at || ao.received_at || ao.date_creation}
                  createdById={ao.created_by_user_id}
                  updatedAt={ao.updated_at}
                  updatedById={ao.updated_by_user_id}
                  dense
                />
              </div>

              {!shouldHideStats && (
                <div className="ao-right-stats">
                  <div className="ao-stat-pill orange"><Award size={12} /> {shortlist.length} Shortlist</div>
                  <div className="ao-stat-pill gold"><Star size={12} /> {topProfil.length} Top profil</div>
                  <div className="ao-stat-pill blue"><Users size={12} /> {candidates.length} Scorés</div>
                </div>
              )}


              {['REPONDU', 'ENTRETIEN', 'TOP_PROFIL_ENVOYE_AU_CLIENT'].includes(statusKey) && (isAdmin || isIA) && (
                <div className="ao-workflow-actions">
                  {statusKey === 'TOP_PROFIL_ENVOYE_AU_CLIENT' && (
                    <button
                      className="btn btn-sm"
                      onClick={() => setReponduModal(true)}
                      style={{ background: '#16a34a', color: 'white', border: 'none' }}
                    >
                      <CheckCircle size={13} /> Marquer Répondu
                    </button>
                  )}
                  <button
                    className="btn btn-sm"
                    onClick={() => setGagneModal(true)}
                    style={{ background: '#4ADE80', color: 'white', border: 'none' }}
                  >
                    <CheckCircle size={13} /> Marquer Gagné
                  </button>
                </div>
              )}

              {(statusKey === 'NOUVEAU' ||
  TRAITEMENT_STATUSES.includes(statusKey) ||
  statusKey === 'ENVOYE_A_EQUIPE_TALENT_ACQUISITION' ||
  SOURCING_STATUSES.includes(statusKey)) && (
  <div className="ao-workflow-actions">
    {statusKey === 'NOUVEAU' && isAdmin && (
      <button className="btn btn-primary btn-sm" onClick={() => setAssignIAModal(true)}>
        <UserCheck size={13} /> Prise en charge
      </button>
    )}

    {statusKey === 'NOUVEAU' && isIA && (
      <button className="btn btn-primary btn-sm" onClick={() => handleAction('take-charge')}>
        <UserCheck size={13} /> Prendre en charge
      </button>
    )}

                  {TRAITEMENT_STATUSES.includes(statusKey) && (isAdmin || isIA) && (
                    <>
                      <button className="btn btn-secondary btn-sm" onClick={() => setSendTAModal(true)}>
                        <Users size={13} /> Envoyer au TA
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleAction('take-sourcing')}>
                        <Play size={13} /> Faire le sourcing
                      </button>
                    </>
                  )}

                  {statusKey === 'ENVOYE_A_EQUIPE_TALENT_ACQUISITION' && (isTA || isAdmin || isIA) && (
                    <button className="btn btn-secondary btn-sm" onClick={() => handleAction('take-sourcing')}>
                      <Play size={13} /> {isTA ? 'Je prends le sourcing' : 'Prendre le sourcing'}
                    </button>
                  )}

                  {SOURCING_STATUSES.includes(statusKey) && (isTA || isAdmin || isIA) && canValidateShortlist && (
                    <button
                      className="btn btn-sm"
                      onClick={handleValidateShortlist}
                      style={{ background: '#16A34A', color: 'white', border: 'none' }}
                    >
                      <CheckCircle size={13} /> Valider la shortlist
                    </button>
                  )}
                </div>
              )}
            </div>


            <div className="ao-right-scroll">
              {ao.executive_summary && (
                <AoSection title="Résumé du poste">
                  <p className="ao-txt">{ao.executive_summary}</p>
                </AoSection>
              )}


              {ao.objectifs_principaux?.length > 0 && (
                <AoSection title="Objectifs principaux">
                  <ul className="ao-ul">{ao.objectifs_principaux.map((o, i) => <li key={i}>{o}</li>)}</ul>
                </AoSection>
              )}

              {ao.responsibilities?.length > 0 && (
                <AoSection title="Responsabilités">
                  <ul className="ao-ul">{ao.responsibilities.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </AoSection>
              )}


              {ao.tech_skills?.length > 0 && (
                <AoSection title="Compétences techniques">
                  <div className="ao-chips">{ao.tech_skills.map((s) => <span key={s} className="ao-chip ao-chip-blue">{s}</span>)}</div>
                </AoSection>
              )}


              {ao.soft_skills?.length > 0 && (
                <AoSection title="Compétences comportementales">
                  <div className="ao-chips">{ao.soft_skills.map((s) => <span key={s} className="ao-chip ao-chip-green">{s}</span>)}</div>
                </AoSection>
              )}


              {ao.certifications?.length > 0 && (
                <AoSection title="Certifications souhaitées">
                  <div className="ao-chips">{ao.certifications.map((s) => <span key={s} className="ao-chip ao-chip-orange">{s}</span>)}</div>
                </AoSection>
              )}


              {ao.selection_criteria?.length > 0 && (
                <AoSection title="Critères de sélection">
                  <ul className="ao-ul">{ao.selection_criteria.map((c, i) => <li key={i}>{c}</li>)}</ul>
                </AoSection>
              )}


              {ao.detailed_description && (
                <AoSection title="Description complète">
                  <p className="ao-txt" style={{ whiteSpace: 'pre-wrap' }}>{ao.detailed_description}</p>
                </AoSection>
              )}


              {ao.keywords?.length > 0 && (
                <AoSection title="Mots-clés">
                  <div className="ao-chips">{ao.keywords.map((k) => <span key={k} className="ao-chip ao-chip-gray">{k}</span>)}</div>
                </AoSection>
              )}

              <ProUnityCompetencesSection ao={ao} />

              {ao.fiche_poste_url && (
                <AoSection title="Source de l'AO">
                  <a
                    href={ao.fiche_poste_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ao-link"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      color: '#60A5FA',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      textDecoration: 'none',
                      padding: '8px 12px',
                      background: 'rgba(96,165,250,0.06)',
                      borderRadius: 8,
                      border: '1px solid rgba(96,165,250,0.15)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = 'rgba(96,165,250,0.12)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'rgba(96,165,250,0.06)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <ExternalLink size={16} />
                    Lien vers la fiche de poste source
                  </a>
                </AoSection>
              )}

              {(isTA || isIA || isAdmin || isRT) && (
                <AoSection title="Suivi métier">
                  <div style={{ display: 'grid', gap: 12 }}>
                    {isTA && (
                      <>
                        <div
                          style={{
                            padding: '12px 14px',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--surface)',
                          }}
                        >
                          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                            Délai de réponse avec shortlist
                          </div>
                          <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                            {ao.response_deadline ? fmtDate(ao.response_deadline) : 'Non défini'}
                          </div>
                        </div>


                        <div
                          style={{
                            padding: '12px 14px',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--radius-md)',
                            background: 'var(--surface)',
                          }}
                        >
                          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                            Ingénieur d'affaire associé
                          </div>
                          <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                            {ao.handler_user_name || ao.business_manager_name || ao.commercial_contact || 'Non assigné'}
                          </div>
                        </div>
                      </>
                    )}


                    {isIA && (
                      <div
                        style={{
                          padding: '14px',
                          border: '1px solid rgba(96,165,250,0.22)',
                          borderRadius: 'var(--radius-md)',
                          background: 'rgba(96,165,250,0.07)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 12,
                          flexWrap: 'wrap',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#38BDF8', textTransform: 'uppercase', marginBottom: 6 }}>
                            Date limite définie pour le TA
                          </div>
                          <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#1e3a8a' }}>
                            {ao.response_deadline ? fmtDate(ao.response_deadline) : 'Non définie'}
                          </div>
                        </div>


                        <button className="btn btn-secondary btn-sm" onClick={() => setSendTAModal(true)}>
                          <Edit3 size={13} /> Modifier la date limite
                        </button>
                      </div>
                    )}

                    {/* ── Référent Technique ─────────────────────────── */}
                    {(isAdmin || isIA || isTA || isRT) && (
                      <div style={{ padding:'14px', border:'1px solid rgba(8,145,178,0.30)', borderRadius:'var(--radius-md)', background:'rgba(8,145,178,0.08)' }}>
                        <div style={{ fontSize:'0.75rem', fontWeight:700, color:'#0891B2', textTransform:'uppercase', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                          <ClipboardCheck size={13}/> Référent Technique
                        </div>
                        {aoReferent ? (
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                            <div>
                              <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#0E7490' }}>{aoReferent.prenom} {aoReferent.nom}</div>
                              <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{aoReferent.email}</div>
                            </div>
                            {(isAdmin || isIA || isTA) && (
                              <button className="btn btn-ghost btn-sm" style={{ color:'#0891B2', fontSize:'0.75rem' }}
                                disabled={assigningRT}
                                onClick={async () => {
                                  setAssigningRT(true);
                                  try { await rtAPI.assignReferent(id, null); setAoReferent(null); toast.success('RT retiré'); }
                                  catch { toast.error('Erreur'); }
                                  finally { setAssigningRT(false); }
                                }}>
                                <X size={12}/> Retirer
                              </button>
                            )}
                          </div>
                        ) : (
                          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                            <select style={{ flex:1, padding:'6px 10px', border:'1.5px solid rgba(8,145,178,0.30)', borderRadius:7, fontSize:'0.8125rem', background:'var(--bg-card)', color:'var(--text-primary)' }}
                              defaultValue="" disabled={!(isAdmin || isIA || isTA) || assigningRT}
                              onChange={async (e) => {
                                const rtId = e.target.value;
                                if (!rtId) return;
                                setAssigningRT(true);
                                try {
                                  await rtAPI.assignReferent(id, rtId);
                                  const chosen = rtList.find(r => r.id === rtId);
                                  setAoReferent(chosen || { id: rtId });
                                  toast.success('Référent Technique assigné');
                                } catch { toast.error('Erreur assignation RT'); }
                                finally { setAssigningRT(false); }
                              }}>
                              <option value="">— Choisir un référent —</option>
                              {rtList.map(rt => (
                                <option key={rt.id} value={rt.id}>
                                  {rt.prenom} {rt.nom}{rt.role_label ? ` (${rt.role_label})` : ''}
                                </option>
                              ))}
                            </select>
                            {assigningRT && <div className="spinner" style={{ width:14, height:14 }}/>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </AoSection>
              )}
            </div>
            </>) : (
              <div className="ao-overview">

                {/* ── Hero ─────────────────────────────────────────── */}
                <div className="ao-overview-hero" style={{ '--ao-accent': cfg.color }}>
                  <div className="ao-overview-hero-left">
                    <div className="ao-overview-badges">
                      <span className="ao-status-badge" style={{ background: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}33` }}>
                        {cfg.label}
                      </span>
                      {ao.source_type && <span className="ao-source-badge">{ao.source_type}</span>}
                      {ao.ao_code && (
                        ao.fiche_poste_url
                          ? <a href={ao.fiche_poste_url} target="_blank" rel="noopener noreferrer"
                              style={{ fontSize:'0.75rem', fontWeight:700, color:'#60A5FA', textDecoration:'none', padding:'3px 8px', borderRadius:6, background:'rgba(96,165,250,0.08)', border:'1px solid rgba(96,165,250,0.2)' }}>
                              #{ao.ao_code} ↗
                            </a>
                          : <span style={{ whiteSpace:'nowrap', fontSize:'0.75rem', fontWeight:700, color:'var(--text-muted)', padding:'3px 8px', borderRadius:6, background:'rgba(255,255,255,0.06)' }}>
                              #{ao.ao_code}
                            </span>
                      )}
                    </div>
                    <h1 className="ao-overview-title">{ao.job_title || 'Sans titre'}</h1>
                    <div className="ao-overview-meta">
                      {ao.client       && <span className="ao-overview-meta-item"><Building2 size={14}/>{ao.client}</span>}
                      {ao.location     && <span className="ao-overview-meta-item"><MapPin    size={14}/>{ao.location}</span>}
                      {ao.contract_type && <span className="ao-overview-meta-item"><Briefcase size={14}/>{ao.contract_type}</span>}
                      {ao.work_mode    && <span className="ao-overview-meta-item">🏠 {ao.work_mode}</span>}
                      {ao.experience_years && <span className="ao-overview-meta-item">⏱ {ao.experience_years}</span>}
                      {tjm             && <span className="ao-overview-meta-item">💰 {tjm}</span>}
                    </div>
                  </div>
                  <div className="ao-overview-hero-right">
                    <div style={{ display:'flex', gap:8 }}>
                      {(isAdmin || isIA) && (
                        <button className="btn btn-secondary btn-sm" onClick={() => setShowEditAO(true)}>
                          <Edit3 size={13}/> Modifier
                        </button>
                      )}
                      <button className="btn btn-secondary btn-sm" onClick={() => setShowFiche(true)}>
                        <FileDown size={13}/> Fiche PDF
                      </button>
                    </div>
                    {ao.response_deadline && (
                      <div className="ao-overview-deadline">
                        <Calendar size={13}/>
                        <span>Date limite :</span>
                        <strong>{fmtDate(ao.response_deadline)}</strong>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── CTA workflow ─────────────────────────────────── */}
                {statusKey === 'NOUVEAU' && (isAdmin || isIA) && (
                  <div className="ao-overview-cta" style={{ '--cta-accent': cfg.color }}>
                    <div className="ao-overview-cta-icon" style={{ background: cfg.color }}>
                      <Zap size={20}/>
                    </div>
                    <div className="ao-overview-cta-body">
                      <div className="ao-overview-cta-title">AO en attente de prise en charge</div>
                      <div className="ao-overview-cta-desc">
                        {isAdmin
                          ? "Affectez cet appel d'offres à un Ingénieur d'Affaires ou prenez-le directement en charge pour démarrer le traitement."
                          : "Prenez en charge cet appel d'offres pour commencer le traitement et lancer le sourcing."}
                      </div>
                    </div>
                   <div className="ao-overview-cta-actions">
  {isAdmin && (
    <button className="btn btn-primary" onClick={() => setAssignIAModal(true)}>
      <Users size={14}/> Prise en charge
    </button>
  )}
  {!isAdmin && (
    <button className="btn btn-primary" onClick={() => handleAction('take-charge')}>
      <UserCheck size={14}/> Prendre en charge
    </button>
  )}
</div>
                  </div>
                )}

                {TRAITEMENT_STATUSES.includes(statusKey) && (isAdmin || isIA) && (
                  <div className="ao-overview-cta ao-overview-cta--orange">
                    <div className="ao-overview-cta-icon" style={{ background: '#EA580C' }}>
                      <Play size={20}/>
                    </div>
                    <div className="ao-overview-cta-body">
                      <div className="ao-overview-cta-title">AO en cours de traitement</div>
                      <div className="ao-overview-cta-desc">
                        Lancez le sourcing vous-même ou affectez cet AO à un Talent Acquisition pour le traitement des candidatures.
                      </div>
                    </div>
                    <div className="ao-overview-cta-actions">
                      <button className="btn btn-primary" onClick={() => handleAction('take-sourcing')}>
                        <Play size={14}/> Lancer le sourcing
                      </button>
                      <button className="btn btn-secondary" onClick={() => setSendTAModal(true)} style={{ border:'1px solid rgba(255,255,255,0.22)', color:'var(--text-primary)', background:'rgba(255,255,255,0.10)' }}>
                        <Users size={14}/> Affecter à un TA
                      </button>
                    </div>
                  </div>
                )}

                {statusKey === 'ENVOYE_A_EQUIPE_TALENT_ACQUISITION' && (isTA || isAdmin || isIA) && (
                  <div className="ao-overview-cta ao-overview-cta--cyan">
                    <div className="ao-overview-cta-icon" style={{ background: '#0891B2' }}>
                      <UserCheck size={20}/>
                    </div>
                    <div className="ao-overview-cta-body">
                      <div className="ao-overview-cta-title">En attente d'un Talent Acquisition</div>
                      <div className="ao-overview-cta-desc">
                        {isTA
                          ? "Cet AO vous a été envoyé. Prenez-le en charge pour démarrer le sourcing de candidats."
                          : "Cet AO a été envoyé à l'équipe Talent Acquisition, en attente de prise en charge."}
                      </div>
                    </div>
                    <div className="ao-overview-cta-actions">
                      <button className="btn btn-primary" onClick={() => handleAction('take-sourcing')}>
                        <Play size={14}/> {isTA ? 'Je prends le sourcing' : 'Lancer le sourcing'}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Référent Technique (overview) ────────────────── */}
                {(isAdmin || isIA || isTA || isRT) && (
                  <div style={{ marginBottom: 16 }}>
                    <AoSection title="Référent Technique">
                      <div style={{ padding:'14px', border:'1px solid rgba(8,145,178,0.30)', borderRadius:'var(--radius-md)', background:'rgba(8,145,178,0.08)' }}>
                        <div style={{ fontSize:'0.75rem', fontWeight:700, color:'#0891B2', textTransform:'uppercase', marginBottom:8, display:'flex', alignItems:'center', gap:6 }}>
                          <ClipboardCheck size={13}/> Référent Technique assigné
                        </div>
                        {aoReferent ? (
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
                            <div>
                              <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#0E7490' }}>{aoReferent.prenom} {aoReferent.nom}</div>
                              <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{aoReferent.email}</div>
                            </div>
                            {(isAdmin || isIA || isTA) && (
                              <button className="btn btn-ghost btn-sm" style={{ color:'#0891B2', fontSize:'0.75rem' }}
                                disabled={assigningRT}
                                onClick={async () => {
                                  setAssigningRT(true);
                                  try { await rtAPI.assignReferent(id, null); setAoReferent(null); toast.success('RT retiré'); }
                                  catch { toast.error('Erreur'); }
                                  finally { setAssigningRT(false); }
                                }}>
                                <X size={12}/> Retirer
                              </button>
                            )}
                          </div>
                        ) : (isAdmin || isIA || isTA) ? (
                          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                            <select style={{ flex:1, padding:'6px 10px', border:'1.5px solid rgba(8,145,178,0.30)', borderRadius:7, fontSize:'0.8125rem', background:'var(--bg-card)', color:'var(--text-primary)' }}
                              defaultValue="" disabled={assigningRT}
                              onChange={async (e) => {
                                const rtId = e.target.value;
                                if (!rtId) return;
                                setAssigningRT(true);
                                try {
                                  await rtAPI.assignReferent(id, rtId);
                                  const chosen = rtList.find(r => r.id === rtId);
                                  setAoReferent(chosen || { id: rtId });
                                  toast.success('Référent Technique assigné');
                                } catch { toast.error('Erreur assignation RT'); }
                                finally { setAssigningRT(false); }
                              }}>
                              <option value="">— Choisir un référent —</option>
                              {rtList.map(rt => (
                                <option key={rt.id} value={rt.id}>
                                  {rt.prenom} {rt.nom}{rt.role_label ? ` (${rt.role_label})` : ''}
                                </option>
                              ))}
                            </select>
                            {assigningRT && <div className="spinner" style={{ width:14, height:14 }}/>}
                          </div>
                        ) : (
                          <div style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>Aucun référent technique assigné</div>
                        )}
                      </div>
                    </AoSection>
                  </div>
                )}

                {/* ── Content 2-col grid ────────────────────────────── */}
                <div className="ao-overview-grid">
                  <div className="ao-overview-col">
                    {ao.executive_summary && (
                      <AoSection title="Résumé du poste">
                        <p className="ao-txt">{ao.executive_summary}</p>
                      </AoSection>
                    )}
                    {ao.objectifs_principaux?.length > 0 && (
                      <AoSection title="Objectifs principaux">
                        <ul className="ao-ul">{ao.objectifs_principaux.map((o, i) => <li key={i}>{o}</li>)}</ul>
                      </AoSection>
                    )}
                    {ao.responsibilities?.length > 0 && (
                      <AoSection title="Responsabilités">
                        <ul className="ao-ul">{ao.responsibilities.map((r, i) => <li key={i}>{r}</li>)}</ul>
                      </AoSection>
                    )}
                    {ao.detailed_description && (
                      <AoSection title="Description complète">
                        <p className="ao-txt" style={{ whiteSpace:'pre-wrap' }}>{ao.detailed_description}</p>
                      </AoSection>
                    )}
                  </div>
                  <div className="ao-overview-col">
                    {ao.tech_skills?.length > 0 && (
                      <AoSection title="Compétences techniques">
                        <div className="ao-chips">{ao.tech_skills.map((s) => <span key={s} className="ao-chip ao-chip-blue">{s}</span>)}</div>
                      </AoSection>
                    )}
                    {ao.soft_skills?.length > 0 && (
                      <AoSection title="Compétences comportementales">
                        <div className="ao-chips">{ao.soft_skills.map((s) => <span key={s} className="ao-chip ao-chip-green">{s}</span>)}</div>
                      </AoSection>
                    )}
                    {ao.certifications?.length > 0 && (
                      <AoSection title="Certifications souhaitées">
                        <div className="ao-chips">{ao.certifications.map((s) => <span key={s} className="ao-chip ao-chip-orange">{s}</span>)}</div>
                      </AoSection>
                    )}
                    {ao.selection_criteria?.length > 0 && (
                      <AoSection title="Critères de sélection">
                        <ul className="ao-ul">{ao.selection_criteria.map((c, i) => <li key={i}>{c}</li>)}</ul>
                      </AoSection>
                    )}
                    {ao.keywords?.length > 0 && (
                      <AoSection title="Mots-clés">
                        <div className="ao-chips">{ao.keywords.map((k) => <span key={k} className="ao-chip ao-chip-gray">{k}</span>)}</div>
                      </AoSection>
                    )}

                    <ProUnityCompetencesSection ao={ao} />

                    {ao.fiche_poste_url && (
                      <AoSection title="Source de l'AO">
                        <a
                          href={ao.fiche_poste_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ao-link"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 8,
                            color: '#60A5FA',
                            fontWeight: 600,
                            fontSize: '0.9rem',
                            textDecoration: 'none',
                            padding: '8px 12px',
                            background: 'rgba(96,165,250,0.06)',
                            borderRadius: 8,
                            border: '1px solid rgba(96,165,250,0.15)',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.background = 'rgba(96,165,250,0.12)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.background = 'rgba(96,165,250,0.06)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          <ExternalLink size={16} />
                          Lien vers la fiche de poste source
                        </a>
                      </AoSection>
                    )}
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      </div>


      {showFiche && <FichePoste ao={ao} user={user} onClose={() => setShowFiche(false)} />}
      {showScoreCvModal && (
        <ScoreCvModal
          aoId={id}
          onClose={() => setShowScoreCvModal(false)}
          onScored={fetchData}
          onUploadFile={handleScoreManualFile}
          uploading={scoring}
        />
      )}
      {cvModal && (
        <CvConvertModal
          candidate={cvModal}
          entityId={cvModal.candidate_id}
          sourceType="candidate"
          targetTitle={ao?.job_title}
          onClose={() => setCvModal(null)}
          onRescore={ao?.id ? async () => { await aoAPI.scoreExisting(ao.id, cvModal.candidate_id); await fetchData(); } : undefined}
        />
      )}
      {cvOriginalModal && (
        <CvOriginalModal
          candidate={cvOriginalModal}
          onClose={() => setCvOriginalModal(null)}
          onDownload={() => handleDownloadCV(cvOriginalModal)}
          onReplace={(file) => handleReplaceCV(cvOriginalModal.candidate_id, file)}
          onDelete={() => handleDeleteCV(cvOriginalModal.candidate_id)}
          onDirectRescore={async () => {
            await aoAPI.scoreExisting(id, cvOriginalModal.candidate_id);
            setCvOriginalModal(null);
            await fetchData();
            toast.success('Rescore effectué');
          }}
        />
      )}
      {showEditAO && (
        <AOEditModal
          ao={ao}
          onClose={() => setShowEditAO(false)}
          onSaved={() => {
            setShowEditAO(false);
            fetchData();
          }}
        />
      )}
    {assignIAModal && (
  <AssignResponsableModal
    ao={ao}
    currentUser={user}
    onClose={() => setAssignIAModal(false)}
    onSaved={() => {
      setAssignIAModal(false);
      fetchData();
    }}
  />
)}
      {sendTAModal && (
        <SendToTAModal
          ao={ao}
          onClose={() => setSendTAModal(false)}
          onSaved={() => {
            setSendTAModal(false);
            fetchData();
          }}
        />
      )}

      {exportModalCR && (
        <ExportSelectModal
          title="Exporter Compte Rendu"
          subtitle={`compte_rendu_${(ao?.job_title || 'ao').replace(/[^a-z0-9]/gi, '_')}.xlsx`}
          candidates={sorted}
          onClose={() => setExportModalCR(false)}
          onExport={(sel) => { exportCompteRendu(sel); setExportModalCR(false); }}
        />
      )}

      {exportModalPU && (
        <ExportSelectModal
          title="Exporter Pro Unity"
          subtitle="ao_pro_unity.xlsx — un onglet par candidat"
          candidates={sorted}
          onClose={() => setExportModalPU(false)}
          onExport={(sel) => { exportProUnity(sel); setExportModalPU(false); }}
        />
      )}

      {searchProgressSource && (
        <SearchProgressModal
          aoId={id}
          onDone={() => fetchData()}
          onClose={() => {
            setSearchProgressSource(null);
            setSearching(null);
          }}
        />
      )}

      {prequalModal && (
        <PrequalModal
          type={prequalModal.type}
          defaultReport={prequalModal.defaultReport}
          onClose={() => setPrequalModal(null)}
          onSave={(report) => {
            handleAdvance(prequalModal.matchId, prequalModal.candidateId, prequalModal.type, report);
            setPrequalModal(null);
          }}
          onDisqualify={(report) => {
            handleDisqualify(prequalModal.matchId, report);
            setPrequalModal(null);
          }}
        />
      )}

      {editReportModal && (
        <EditReportModal
          type={editReportModal.type}
          currentHtml={editReportModal.currentHtml}
          onClose={() => setEditReportModal(null)}
          onSave={(report) => {
            handleEditReport(editReportModal.matchId, editReportModal.type, report);
            setEditReportModal(null);
          }}
        />
      )}

      {editAIReportModal && (
        <EditAIReportModal
          candidate={editAIReportModal.candidate}
          onClose={() => setEditAIReportModal(null)}
          onSave={async (data) => {
            try {
              await matchAPI.updateAIReport(editAIReportModal.matchId, data);
              // Mettre à jour le candidat localement
              setCandidates(prev => prev.map(c =>
                String(c.match_id) === String(editAIReportModal.matchId)
                  ? {
                      ...c,
                      strengths: data.strengths ?? c.strengths,
                      gaps: data.gaps ?? c.gaps,
                      missing_skills: data.missing_skills ?? c.missing_skills,
                      interview_questions: data.interview_questions ?? c.interview_questions,
                      final_recommendation: data.final_recommendation ?? c.final_recommendation,
                      score_breakdown: data.score_breakdown ?? c.score_breakdown,
                      score_overall: data.score_overall ?? c.score_overall,
                    }
                  : c
              ));
              setEditAIReportModal(null);
              toast.success('Rapport IA mis à jour');
            } catch (e) {
              toast.error(extractError(e));
            }
          }}
        />
      )}

      {reponduModal && (
        <ReponduModal
          aoLabel={ao?.job_title || 'Mission'}
          topProfils={topProfil}
          onClose={() => setReponduModal(false)}
          onConfirm={handleMarkRepondu}
        />
      )}

      {gagneModal && (
        <GagneModal
          aoLabel={ao?.job_title || 'Mission'}
          topProfils={topProfil}
          onClose={() => setGagneModal(false)}
          onConfirm={handleMarkGagne}
        />
      )}

      {unifiedCallModal && (
        <UnifiedCallModal
          candidate={unifiedCallModal}
          isCalling={callingCandidateId === unifiedCallModal.candidate_id}
          onCall={() => handleTwilioCall(unifiedCallModal)}
          onHangup={handleHangupTwilio}
          onSave={(transcript, summary) => {
            handleSaveCallReport(unifiedCallModal.match_id, transcript, summary);
            setUnifiedCallModal(null);
          }}
          onClose={() => setUnifiedCallModal(null)}
        />
      )}

      {emailModal && (
        <EmailModal
          matchId={emailModal.matchId}
          candidateId={emailModal.candidateId}
          aoId={id}
          candidateName={emailModal.name}
          candidateEmail={emailModal.email}
          onClose={() => setEmailModal(null)}
          onSend={(subject, body, ccEmails) => {
            handleSendEmail(emailModal.matchId, subject, body, ccEmails);
            setEmailModal(null);
          }}
          onScheduleInterview={async (data) => {
            await handleScheduleEntretienFromAO(emailModal.candidateId, data);
          }}
        />
      )}

      {entretienAOModal && (
        <EntretienAOModal
          candidateId={entretienAOModal.candidateId}
          candidateName={entretienAOModal.name}
          candidateEmail={entretienAOModal.email}
          aoId={entretienAOModal.aoId}
          onClose={() => setEntretienAOModal(null)}
          onSchedule={(data) => handleScheduleEntretienFromAO(entretienAOModal.candidateId, data)}
        />
      )}

      {editCandidateModal && (
        <CandidateEditModal
          candidate={editCandidateModal}
          onClose={() => setEditCandidateModal(null)}
          onSaved={(updated) => {
            setCandidates((prev) =>
              prev.map((c) =>
                String(c.candidate_id) === String(updated.id)
                  ? { ...c, ...updated }
                  : c
              )
            );
            setEditCandidateModal(null);
            toast.success('Candidat mis à jour');
          }}
        />
      )}

      {scoreChangeModal && (
        <ScoreChangeModal
          candidate={scoreChangeModal.candidate}
          aoId={scoreChangeModal.aoId}
          onClose={() => setScoreChangeModal(null)}
          onSave={handleScoreChange}
        />
      )}

      {viewRTReportModal && (
        <RTReportViewModal
          reports={viewRTReportModal.reports}
          candidate={viewRTReportModal.candidate}
          onClose={() => setViewRTReportModal(null)}
        />
      )}

      {/* ── Bouton chat IA flottant pour TA ── */}
      {(isTA || isAdmin || isIA) && ao && (
        <>
          <button
            onClick={() => setTaChatOpen(v => !v)}
            title="Évaluation technique IA"
            style={{
              position:'fixed', bottom:28, right:28, zIndex:1100,
              width:56, height:56, borderRadius:'50%',
              background:'linear-gradient(135deg,#60A5FA 0%,#93C5FD 100%)',
              border:'none', cursor:'pointer', boxShadow:'0 4px 20px rgba(96,165,250,0.35)',
              display:'flex', alignItems:'center', justifyContent:'center', color:'#fff',
              transition:'transform 0.2s',
            }}
          >
            {taChatOpen ? <X size={22}/> : <BrainCircuit size={24}/>}
          </button>
          {taChatOpen && (
            <TAChatBot
              ao={ao}
              aoId={id}
              candidates={candidates}
              onClose={() => setTaChatOpen(false)}
              onHardQualValidated={(matchId, candidateId, report) => {
                setTaChatOpen(false);
                setPrequalModal({ matchId, candidateId, type: 'hard', defaultReport: report });
              }}
            />
          )}
        </>
      )}
    </>
  );
}


function AoSection({ title, children }) {
  return (
    <div className="ao-section">
      <div className="ao-section-head">
        <span className="ao-section-label">{title.toUpperCase()}</span>
        <div className="ao-section-line" />
      </div>
      {children}
    </div>
  );
}


function _puLevelConfig(level) {
  const lv = (level || '').toLowerCase();
  if (/advanced|expert|senior|haut|fluent|native/.test(lv))
    return { color: '#FF4100', bg: 'rgba(255,65,0,0.08)', border: 'rgba(255,65,0,0.22)' };
  if (/nice\s+to\s+have|basic|junior|beginner|low/.test(lv))
    return { color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.22)' };
  // intermediate / confirmed / level intermediate / etc.
  return { color: '#60A5FA', bg: 'rgba(96,165,250,0.08)', border: 'rgba(96,165,250,0.22)' };
}

function ProUnityLevelBadge({ level }) {
  const { color, bg, border } = _puLevelConfig(level);
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px', borderRadius: 10,
      fontSize: '0.71rem', fontWeight: 700, whiteSpace: 'nowrap',
      background: bg, color, border: `1px solid ${border}`,
    }}>
      {level || '—'}
    </span>
  );
}

function _PUSkillCard({ name, level }) {
  const { color } = _puLevelConfig(level);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 8, padding: '7px 10px 7px 12px', borderRadius: 6,
      borderLeft: `3px solid ${color}`,
      background: 'rgba(128,128,128,0.04)',
      minWidth: 0,
    }}>
      <span style={{ fontSize: '0.83rem', color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name}
      </span>
      <ProUnityLevelBadge level={level} />
    </div>
  );
}

function ProUnityCompetencesSection({ ao }) {
  if (ao?.source_type !== 'prounity') return null;
  const comp = ao?.worked_payload?.prounity_competences
    || ao?.raw_payload?.job?.competences;
  if (!comp) return null;

  const roles = comp.roles || [];
  const skills = comp.skills || [];
  const languages = comp.languages || [];
  if (!roles.length && !skills.length && !languages.length) return null;

  const subLabel = { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 8 };

  return (
    <AoSection title="Compétences requises (ProUnity)">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {roles.length > 0 && (
          <div>
            <div style={subLabel}>Rôles</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {roles.map((r, i) => <_PUSkillCard key={i} name={r.role} level={r.level} />)}
            </div>
          </div>
        )}
        {skills.length > 0 && (
          <div>
            <div style={subLabel}>Compétences techniques</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 5 }}>
              {skills.map((s, i) => <_PUSkillCard key={i} name={s.competence} level={s.level} />)}
            </div>
          </div>
        )}
        {languages.length > 0 && (
          <div>
            <div style={subLabel}>Langues</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {languages.map((l, i) => {
                const { color, bg, border } = _puLevelConfig(l.level);
                return (
                  <span key={i} style={{ padding: '4px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600, background: bg, color, border: `1px solid ${border}` }}>
                    {l.language}{l.level ? ` · ${l.level}` : ''}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AoSection>
  );
}


const LINKEDIN_SOURCES = ['linkedin', 'linkedin_it', 'LINKEDIN_IT', 'LinkedIn'];


function isLinkedIn(c) {
  return LINKEDIN_SOURCES.some(
    (s) =>
      (c.source || '').toLowerCase() === s.toLowerCase() ||
      (c.match_status || '').toLowerCase().includes('linkedin')
  );
}


function CandidateList({
  candidates,
  expanded,
  onToggle,
  onShortlist,
  onDownloadCV,
  onOpenCvModal,
  onDelete,
  onAddToPipe,
  addingToPipe = {},
  pipelineMap = {},
  showShortlist,
  shortlistValidated,
  showTopProfil,
  canTopProfil,
  onTopProfil,
  onAdvance,
  advanceLabel,
  rtReportsByCandidate,
  canWriteRTReport,
  canViewRTReport,
  onWriteRTReport,
  onViewRTReport,
  onScoreChange,
  onEmail,
  onUnifiedCall,
  onHangupTwilio,
  callingCandidateId,
  onDeleteCallReport,
  onEditCandidate,
  onEditReport,
  onEditAIReport,
  onConvertCV,
  emptyMessage,
}) {
  const normal = candidates.filter((c) => !isLinkedIn(c));
  const linkedin = candidates.filter((c) => isLinkedIn(c));


  if (candidates.length === 0) {
    return (
      <div className="ao-empty">
        <Users size={32} style={{ opacity: 0.3 }} />
        <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>Aucun profil</div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', textAlign: 'center' }}>{emptyMessage}</div>
      </div>
    );
  }


  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {normal.map((c, i) => (
        <CandidateRow
          key={c.candidate_id || i}
          candidate={c}
          rank={i + 1}
          expanded={!!expanded[c.candidate_id]}
          onToggle={() => onToggle(c.candidate_id)}
          onShortlist={() => onShortlist(c.candidate_id, c)}
          onTopProfil={onTopProfil ? () => onTopProfil(c.candidate_id, c) : null}
          onUnifiedCall={onUnifiedCall ? (opts) => onUnifiedCall(opts ? { ...c, ...opts } : c) : null}
          onHangupTwilio={onHangupTwilio}
          isCalling={callingCandidateId === c.candidate_id}
          onEditCandidate={onEditCandidate ? () => onEditCandidate(c) : null}
          onDownloadCV={() => onDownloadCV(c)}
          onOpenCvModal={onOpenCvModal ? () => onOpenCvModal(c) : null}
          onDelete={() => onDelete(c.match_id)}
          onAddToPipe={onAddToPipe ? () => onAddToPipe(c) : null}
          addingToPipe={!!addingToPipe[c.candidate_id]}
          pipelineEntryId={pipelineMap[String(c.candidate_id)] || null}
          showShortlist={showShortlist}
          shortlistValidated={shortlistValidated}
          showTopProfil={showTopProfil}
          canTopProfil={canTopProfil}
          onAdvance={onAdvance ? () => onAdvance(c) : null}
          advanceLabel={advanceLabel}
          onWriteRTReport={(() => {
            const submitted = rtReportsByCandidate?.[c.candidate_id]?.filter(r => r.status === 'SOUMIS') || [];
            return (submitted.length === 0 && canWriteRTReport && onWriteRTReport) ? () => onWriteRTReport(c) : null;
          })()}
          onViewRTReport={(() => {
            const submitted = rtReportsByCandidate?.[c.candidate_id]?.filter(r => r.status === 'SOUMIS') || [];
            return (submitted.length > 0 && canViewRTReport && onViewRTReport) ? () => onViewRTReport(c, submitted) : null;
          })()}
          onScoreChange={onScoreChange ? () => onScoreChange(c) : null}
          onEmail={onEmail ? () => onEmail(c) : null}
          onDeleteCallReport={onDeleteCallReport ? () => onDeleteCallReport(c.match_id) : null}
          onEditReport={onEditReport ? (type) => onEditReport(c, type) : null}
          onEditAIReport={onEditAIReport ? () => onEditAIReport(c) : null}
          onConvertCV={onConvertCV ? () => onConvertCV(c) : null}
          isLast={i === normal.length - 1 && linkedin.length === 0}

        />
      ))}


      {linkedin.length > 0 && (
        <LinkedInSection
          candidates={linkedin}
          expanded={expanded}
          onToggle={onToggle}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}


function CandidateRow({
  candidate: c,
  rank,
  expanded,
  onToggle,
  onShortlist,
  onTopProfil,
  onDownloadCV,
  onOpenCvModal,
  onDelete,
  onAddToPipe,
  addingToPipe,
  pipelineEntryId = null,
  showShortlist,
  shortlistValidated,
  showTopProfil,
  canTopProfil,
  onAdvance,
  advanceLabel,
  onWriteRTReport,
  onViewRTReport,
  onScoreChange,
  onEmail,
  onUnifiedCall,
  onHangupTwilio,
  isCalling,
  onEditCandidate,
  onDeleteCallReport,
  onEditReport,
  onEditAIReport,
  onConvertCV,
  isLast,
}) {
  const report = c.report || {};
  const strengths = c.strengths?.length ? c.strengths : report.strengths || [];
  const gaps = c.gaps?.length ? c.gaps : report.gaps || [];
  const missing = c.missing_skills?.length ? c.missing_skills : report.missing_skills || [];
  const questions = c.interview_questions?.length ? c.interview_questions : report.interview_questions || [];
  const reco = c.final_recommendation || report.final_recommendation || '';
  const score = c.score_overall;
  const hasCV = !!(c.cv_minio_path || c.cv_link || c.profile_url);
  const isShortlisted = c.shortlist || c.shortlist_brouillon;
  const isTopP = c.top_profil || c.top_profil_brouillon;


  return (
    <div style={{ borderBottom: isLast ? 'none' : '1px solid var(--border)', background: c.disqualifie ? 'rgba(220,38,38,0.05)' : undefined }}>
      <div className={`ao-cand-row${expanded ? ' ao-cand-expanded' : ''}`} onClick={onToggle} style={c.disqualifie ? { borderLeft:'3px solid #DC2626' } : {}}>
        <div className="ao-cand-rank">#{rank}</div>
        <div className="avatar avatar-md" style={{ flexShrink: 0 }}>{initials(c.full_name || '??')}</div>


        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <ScrollText style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)', flexShrink: 1, minWidth: 0 }}>
              {c.full_name || 'Candidat'}
            </ScrollText>
            {isShortlisted && (
              <span style={{ fontSize: '0.625rem', padding: '1px 6px', borderRadius: 8, background: c.shortlist ? 'rgba(96,165,250,0.14)' : 'rgba(245,158,11,0.14)', color: c.shortlist ? '#60A5FA' : '#FCD34D', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>
                {c.shortlist ? '✓ SL' : '✎ SL'}
              </span>
            )}
            {isTopP && (
              <span style={{ whiteSpace: 'nowrap', fontSize: '0.625rem', padding: '1px 5px', borderRadius: 8, background: c.top_profil ? 'rgba(34,197,94,0.14)' : 'rgba(245,158,11,0.14)', color: c.top_profil ? '#4ADE80' : '#FCD34D', fontWeight: 700, flexShrink: 0 }}>
                ⭐
              </span>
            )}
            {c.disqualifie && (
              <span style={{ fontSize: '0.625rem', padding: '1px 7px', borderRadius: 8, background: 'rgba(220,38,38,0.14)', color: '#DC2626', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>
                ✗ Disqualifié
              </span>
            )}
          </div>
          <ScrollText style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 1 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
              {c.title && <span>{c.title}</span>}
              {c.title && (c.location || c.source) && <span style={{ opacity: 0.35 }}>·</span>}
              {c.location && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><MapPin size={9} />{c.location}</span>}
              {c.location && c.source && <span style={{ opacity: 0.35 }}>·</span>}
              {c.source && <span style={{ whiteSpace: 'nowrap', fontSize: '0.6rem', padding: '1px 4px', borderRadius: 6, background: 'var(--gray-100)', fontWeight: 600 }}>{c.source}</span>}
            </span>
          </ScrollText>
        </div>


        <div style={{ flexShrink: 0, textAlign: 'center', width: 52 }} onClick={(e) => e.stopPropagation()}>
          {score != null ? (
            <>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  margin: '0 auto',
                  background: scoreBg(score),
                  color: scoreColor(score),
                  border: `2px solid ${scoreBorder(score)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: '0.9375rem',
                  cursor: onScoreChange ? 'pointer' : 'default',
                  transition: 'box-shadow 0.15s',
                }}
                title={onScoreChange ? 'Modifier le score' : undefined}
                onClick={() => onScoreChange && onScoreChange()}
                onMouseEnter={e => { if (onScoreChange) e.currentTarget.style.boxShadow = `0 0 0 3px ${scoreBorder(score)}`; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
              >
                {score}
              </div>
              {reco && (
                <div style={{ fontSize: '0.5625rem', marginTop: 2, color: scoreColor(score), fontWeight: 600, lineHeight: 1.2 }}>
                  {reco}
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>—</div>
          )}
        </div>


        <div style={{ width: 72, flexShrink: 0 }}>
          <div style={{ height: 4, background: 'var(--gray-200)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${score || 0}%`, background: scoreColor(score || 0), borderRadius: 2, transition: 'width 0.3s' }} />
          </div>
        </div>


        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          {showShortlist && !(shortlistValidated && isShortlisted) && (
            <button
              className="btn btn-sm"
              onClick={onShortlist}
              style={{
                whiteSpace: 'nowrap',
                background: c.shortlist ? 'rgba(96,165,250,0.08)' : c.shortlist_brouillon ? 'rgba(245,158,11,0.08)' : undefined,
                color: c.shortlist ? '#60A5FA' : c.shortlist_brouillon ? '#d97706' : undefined,
                borderColor: c.shortlist ? 'rgba(96,165,250,0.35)' : c.shortlist_brouillon ? '#FCD34D' : undefined,
              }}
            >
              <Award size={12} />
              {isShortlisted ? 'Retirer shortlist' : 'Shortlister'}
            </button>
          )}


          {showTopProfil && canTopProfil && onTopProfil && (
            <button
              className="btn btn-sm"
              onClick={onTopProfil}
              style={{
                whiteSpace: 'nowrap',
                background: c.top_profil ? 'rgba(34,197,94,0.08)' : c.top_profil_brouillon ? 'rgba(245,158,11,0.08)' : undefined,
                color: c.top_profil ? '#4ADE80' : c.top_profil_brouillon ? '#d97706' : undefined,
                borderColor: c.top_profil ? 'rgba(34,197,94,0.35)' : c.top_profil_brouillon ? '#FCD34D' : undefined,
              }}
            >
              <Star size={12} />
              {isTopP ? 'Retirer top profil' : 'Top profil'}
            </button>
          )}


          {onAdvance && (
            <button
              className="btn btn-sm"
              onClick={onAdvance}
              style={{ whiteSpace: 'nowrap', background: 'rgba(96,165,250,0.08)', color: '#60A5FA', borderColor: 'rgba(96,165,250,0.35)', fontWeight: 700 }}
            >
              {advanceLabel}
            </button>
          )}

          {isCalling && (
            <button
              className="btn btn-sm"
              onClick={onHangupTwilio}
              title="Raccrocher"
              style={{ background: '#dc2626', color: 'white', border: 'none', whiteSpace: 'nowrap' }}
            >
              <Phone size={12} /> Raccrocher
            </button>
          )}

          {onViewRTReport && (
            <button
              className="btn btn-sm btn-icon"
              onClick={onViewRTReport}
              title="Voir rapport RT"
              style={{ background: 'rgba(8,145,178,0.10)', color: '#38BDF8', borderColor: 'rgba(8,145,178,0.30)' }}
            >
              <ClipboardCheck size={13} />
            </button>
          )}

          {onWriteRTReport && (
            <button
              className="btn btn-sm btn-icon"
              onClick={onWriteRTReport}
              title="Rédiger rapport RT"
              style={{ background: 'rgba(8,145,178,0.10)', color: '#38BDF8', borderColor: 'rgba(8,145,178,0.30)' }}
            >
              <ClipboardCheck size={13} />
            </button>
          )}

          {pipelineEntryId ? (
            <a
              href={`/pipeline/${pipelineEntryId}`}
              className="btn btn-sm"
              title="Voir ce candidat dans le pipe"
              style={{
                whiteSpace: 'nowrap',
                background: 'rgba(34,197,94,0.10)',
                color: '#4ADE80',
                borderColor: 'rgba(34,197,94,0.30)',
                textDecoration: 'none',
              }}
            >
              <UserCheck size={12}/> Dans le pipe
            </a>
          ) : onAddToPipe ? (
            <button
              className="btn btn-sm"
              onClick={onAddToPipe}
              disabled={addingToPipe}
              title="Envoyer dans le Pipe candidats"
              style={{
                whiteSpace: 'nowrap',
                background: 'rgba(139,92,246,0.10)',
                color: '#A78BFA',
                borderColor: 'rgba(139,92,246,0.28)',
                opacity: addingToPipe ? 0.6 : 1,
              }}
            >
              {addingToPipe ? <div className="spinner" style={{ width:11, height:11 }}/> : '→'}
              {!addingToPipe && ' Pipe'}
            </button>
          ) : null}
          {onDelete && (
            <button className="btn btn-ghost btn-icon btn-sm" onClick={onDelete} style={{ color: 'var(--danger-600)' }}>
              <Trash2 size={12} />
            </button>
          )}
        </div>


        <div style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
          {expanded ? <ChevronUp size={16} /> : <ChevronRight size={16} />}
        </div>
      </div>


      {expanded && (
        <div className="ao-cand-detail">
          <div className="ao-cand-detail-grid">
            <div>
              <div className="ao-detail-label" style={{ color: 'var(--blue-600)' }}>Coordonnées &amp; CV</div>
              {!c._loaded ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
                  <div className="spinner" style={{ width: 14, height: 14 }} /> Chargement…
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {c.email && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onEmail && onEmail(); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--blue-600)', fontSize: '0.8125rem', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                    >
                      <Mail size={12} />{c.email}
                    </button>
                  )}
                  {c.phone && (
                    <PhoneLink phone={c.phone} style={{ display:'flex', alignItems:'center', gap:6, fontSize:'0.8125rem', color:'#60A5FA' }}>
                      <Phone size={12} />{c.phone}
                    </PhoneLink>
                  )}
                  {c.linkedin_link && <a href={c.linkedin_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0A66C2', fontSize: '0.8125rem', textDecoration: 'none' }}><ExternalLink size={12} />LinkedIn</a>}
                  {!c.email && !c.phone && !c.linkedin_link && <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Aucune coordonnée</span>}
                  <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary btn-sm" style={{ opacity: hasCV ? 1 : 0.45 }} onClick={(e) => { e.stopPropagation(); onOpenCvModal ? onOpenCvModal() : onDownloadCV(); }}>
                      <Download size={12} /> {c.cv_minio_path ? 'CV original' : c.cv_link ? 'Voir CV' : 'CV non dispo'}
                    </button>
                    {onConvertCV && (
                      <button className="btn btn-sm" onClick={(e) => { e.stopPropagation(); onConvertCV(); }} style={{ background: '#60A5FA', color: 'white', border: 'none', whiteSpace: 'nowrap' }}>
                        <Zap size={12} /> Convertir CV
                      </button>
                    )}
                    {onEditCandidate && (
                      <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); onEditCandidate(); }} title="Corriger les informations">
                        <Edit3 size={12} /> Modifier
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* ── Profil enrichi ── */}
              {c._loaded && (c.availability || c.salary || c.experience || c.skills?.length || c.languages?.length || c.summary_ai) && (
                <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px dashed var(--border)' }}>
                  <div className="ao-detail-label" style={{ color: '#059669', marginBottom: 8 }}>Profil</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {c.availability && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Calendar size={11} style={{ flexShrink: 0 }} />
                        <span><strong style={{ color: 'var(--text-primary)' }}>Dispo :</strong> {c.availability}</span>
                      </div>
                    )}
                    {c.salary && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ flexShrink: 0, fontSize: 11 }}>💰</span>
                        <span><strong style={{ color: 'var(--text-primary)' }}>Prétentions :</strong> {c.salary}</span>
                      </div>
                    )}
                    {c.experience && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Briefcase size={11} style={{ flexShrink: 0 }} />
                        <span><strong style={{ color: 'var(--text-primary)' }}>Expérience :</strong> {c.experience}</span>
                      </div>
                    )}
                    {c.languages?.length > 0 && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                        <Globe size={11} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span><strong style={{ color: 'var(--text-primary)' }}>Langues :</strong> {Array.isArray(c.languages) ? c.languages.join(', ') : c.languages}</span>
                      </div>
                    )}
                    {c.skills?.length > 0 && (
                      <div style={{ marginTop: 2 }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Compétences</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                          {(Array.isArray(c.skills) ? c.skills : Object.keys(c.skills || {})).slice(0, 12).map((s, i) => (
                            <span key={i} style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.6875rem', background: 'rgba(96,165,250,0.08)', color: '#60A5FA', fontWeight: 600 }}>{s}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {c.summary_ai && (
                      <div style={{ marginTop: 4, padding: '8px 10px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 6 }}>
                        <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: '#059669', textTransform: 'uppercase', marginBottom: 4 }}>Résumé IA</div>
                        <p style={{ margin: 0, fontSize: '0.75rem', lineHeight: 1.6, color: 'var(--text-primary)', maxHeight: 80, overflowY: 'auto' }}>{c.summary_ai}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>


            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                <div className="ao-detail-label" style={{ marginBottom:0 }}>Rapport de matching</div>
                {onEditAIReport && (
                  <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onEditAIReport(c); }}
                    style={{ fontSize:'0.7rem', padding:'2px 8px', height:'auto' }}>
                    <Edit3 size={11}/> Modifier
                  </button>
                )}
              </div>


              {c.score_breakdown && (
                <div style={{ marginBottom: 10 }}>
                  {Object.entries(c.score_breakdown).map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                      <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', width: 80, flexShrink: 0, textTransform: 'capitalize' }}>{k}</span>
                      <div style={{ flex: 1, height: 5, background: 'var(--gray-200)', borderRadius: 3 }}>
                        <div style={{ height: '100%', width: `${v}%`, background: scoreColor(v), borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: scoreColor(v), width: 28, textAlign: 'right' }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}


              {strengths.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--success-600)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}><CheckCircle size={12} /> Points forts</div>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>{strengths.slice(0, 3).map((s, i) => <li key={i} style={{ fontSize: '0.8125rem', lineHeight: 1.6 }}>{s}</li>)}</ul>
                </div>
              )}


              {gaps.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--warning-600)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}><AlertCircle size={12} /> Écarts</div>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>{gaps.slice(0, 2).map((g, i) => <li key={i} style={{ fontSize: '0.8125rem', lineHeight: 1.6 }}>{g}</li>)}</ul>
                </div>
              )}


              {missing.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--danger-600)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}><XCircle size={12} /> Manquant</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {missing.slice(0, 5).map((s, i) => (
                      <span key={i} style={{ padding: '1px 7px', borderRadius: 4, fontSize: '0.75rem', background: 'var(--danger-100)', color: 'var(--danger-600)', fontWeight: 600 }}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}


              {questions.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--blue-600)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}><MessageSquare size={12} /> Questions entretien</div>
                  <ol style={{ margin: 0, paddingLeft: 16 }}>{questions.slice(0, 3).map((q, i) => <li key={i} style={{ fontSize: '0.8125rem', lineHeight: 1.6 }}>{q}</li>)}</ol>
                </div>
              )}
            </div>
          </div>

          {(c.softskills_report || c.hardskills_report || c.call_summary_ai) && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="ao-detail-label" style={{ color: '#A78BFA', marginBottom: 0 }}>Rapports de préqualification</div>
              {c.softskills_report && (
                <CandidateReport
                  label="Rapport Soft Skills"
                  tag="Préqual. Soft"
                  nextStage="Préqual. Hard Skills"
                  color="#38BDF8"
                  bg="rgba(8,145,178,0.08)"
                  border="rgba(8,145,178,0.22)"
                  html={c.softskills_report}
                  onEdit={onEditReport ? () => onEditReport('soft') : null}
                />
              )}
              {c.hardskills_report && (
                <CandidateReport
                  label="Rapport Hard Skills"
                  tag="Préqual. Hard"
                  nextStage="Shortlist"
                  color="#A78BFA"
                  bg="rgba(139,92,246,0.08)"
                  border="rgba(139,92,246,0.22)"
                  html={c.hardskills_report}
                  onEdit={onEditReport ? () => onEditReport('hard') : null}
                />
              )}
              {c.call_summary_ai && (
                <CandidateReport
                  label="Résumé d'appel"
                  tag="Appel"
                  color="#4ADE80"
                  bg="rgba(34,197,94,0.08)"
                  border="rgba(34,197,94,0.22)"
                  html={c.call_summary_ai}
                  onEdit={onUnifiedCall ? () => onUnifiedCall({ _defaultTab: 'summary' }) : null}
                  onDelete={onDeleteCallReport || null}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function CandidateReport({ label, tag, nextStage, color, bg, border, html, onEdit, onDelete }) {
  const isHtml = html && (html.includes('<p>') || html.includes('<ul>') || html.includes('<strong>') || html.includes('<br'));
  return (
    <div style={{ border: `1px solid ${border}`, borderRadius: 10, background: bg, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.6875rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
        <span style={{ whiteSpace: 'nowrap', fontSize: '0.6875rem', padding: '1px 8px', borderRadius: 999, background: color, color: '#fff', fontWeight: 700 }}>{tag}</span>
        {nextStage && (
          <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
            Etape suivante : <span style={{ fontWeight: 700, color }}>{nextStage}</span>
          </span>
        )}
        {(onEdit || onDelete) && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
            {onEdit && (
              <button onClick={(e) => { e.stopPropagation(); onEdit(); }} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 5, fontSize: '0.6875rem', background: 'var(--bg-card)', border: `1px solid ${border}`, color, cursor: 'pointer', fontWeight: 600 }}>
                <Edit3 size={11} /> Modifier
              </button>
            )}
            {onDelete && (
              <button onClick={(e) => { e.stopPropagation(); onDelete(); }} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 5, fontSize: '0.6875rem', background: 'var(--bg-card)', border: '1px solid #FCA5A5', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>
                <Trash2 size={11} /> Supprimer
              </button>
            )}
          </div>
        )}
      </div>
      {isHtml ? (
        <div
          className="rte-content"
          style={{ fontSize: '0.8125rem', lineHeight: 1.7, color: 'var(--text-primary)', background: 'transparent', padding: 0, maxHeight: 220, overflowY: 'auto' }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <p style={{ margin: 0, fontSize: '0.8125rem', lineHeight: 1.7, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', maxHeight: 220, overflowY: 'auto' }}>{html}</p>
      )}
    </div>
  );
}


function LinkedInSection({ candidates, expanded, onToggle, onDelete }) {
  const [collapsed, setCollapsed] = useState(false);


  return (
    <div className="ao-linkedin-section">
      <div className="ao-linkedin-header" onClick={() => setCollapsed((c) => !c)}>
        <div className="ao-linkedin-icon">in</div>
        <div className="ao-linkedin-title">
          <span style={{ fontWeight: 700, color: '#0A66C2' }}>
            {candidates.length} profil{candidates.length > 1 ? 's' : ''} LinkedIn intéressant{candidates.length > 1 ? 's' : ''}
          </span>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 8 }}>
            · Pas de CV disponible — coordonnées directes
          </span>
        </div>
        <div style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>


      {!collapsed && (
        <div className="ao-linkedin-note">
          💡 Ces profils ont été trouvés sur LinkedIn. Ils ne disposent pas de CV dans notre base —
          le scoring IA n'a pas été effectué. Contactez-les directement via leur profil LinkedIn
          pour évaluer leur adéquation avec la mission.
        </div>
      )}


      {!collapsed && candidates.map((c, i) => {
        const isExp = !!expanded[c.candidate_id];
        return (
          <div key={c.candidate_id || i} className="ao-linkedin-card">
            <div className="ao-linkedin-card-main" onClick={() => onToggle(c.candidate_id)}>
              <div className="ao-linkedin-badge">in</div>


              <div className="avatar avatar-md" style={{ flexShrink: 0, border: '2px solid #0A66C2' }}>
                {initials(c.full_name || '??')}
              </div>


              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.full_name || 'Profil LinkedIn'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
                  {c.title && <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 240 }}>{c.title}</span>}
                  {c.location && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 2 }}><MapPin size={10} />{c.location}</span>}
                  <span className="ao-linkedin-tag">LinkedIn</span>
                  <span className="ao-linkedin-no-cv">📄 Pas de CV</span>
                </div>
              </div>


              <div style={{ flexShrink: 0, textAlign: 'center', width: 72 }}>
                <div className="ao-linkedin-no-score">
                  <span style={{ fontSize: '0.6875rem', color: '#64748b', fontWeight: 600 }}>Non scoré</span>
                </div>
              </div>


              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                {c.linkedin_link && (
                  <a
                    href={c.linkedin_link}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm"
                    style={{ background: '#0A66C2', color: 'white', border: 'none', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={11} /> Voir profil
                  </a>
                )}
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onDelete(c.match_id)} style={{ color: 'var(--danger-600)' }}>
                  <Trash2 size={12} />
                </button>
              </div>


              <div style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
                {isExp ? <ChevronUp size={15} /> : <ChevronRight size={15} />}
              </div>
            </div>


            {isExp && (
              <div className="ao-linkedin-detail">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <div className="ao-detail-label" style={{ color: '#0A66C2' }}>Coordonnées LinkedIn</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {c.email && (
                        <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--blue-600)', fontSize: '0.8125rem', textDecoration: 'none' }}>
                          <Mail size={12} /> {c.email}
                        </a>
                      )}
                      {c.phone && (
                        <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontSize: '0.8125rem', textDecoration: 'none' }}>
                          <Phone size={12} /> {c.phone}
                        </a>
                      )}
                      {c.linkedin_link && (
                        <a href={c.linkedin_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0A66C2', fontSize: '0.8125rem', textDecoration: 'none', fontWeight: 600 }}>
                          <ExternalLink size={12} /> Profil LinkedIn complet
                        </a>
                      )}
                      {!c.email && !c.phone && !c.linkedin_link && (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Aucune coordonnée disponible</span>
                      )}
                    </div>
                  </div>


                  <div>
                    <div className="ao-detail-label" style={{ color: '#0A66C2' }}>Pourquoi ce profil ?</div>
                    <div className="ao-linkedin-why">
                      <div style={{ fontSize: '0.8125rem', color: '#475569', lineHeight: 1.7 }}>
                        Ce profil a été identifié sur LinkedIn sur la base des mots-clés de l'AO.
                        Pour obtenir un scoring complet, importez son CV depuis son profil LinkedIn
                        via le bouton <strong>"Scorer un CV"</strong>.
                      </div>
                      {c.title && (
                        <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(96,165,250,0.07)', borderRadius: 6, fontSize: '0.8125rem', color: '#60A5FA' }}>
                          📌 Titre actuel : <strong>{c.title}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function TopProfilList({ candidates, expanded, onToggle, onTopProfil, onDownloadCV, onDelete, onConvertCV }) {
  if (candidates.length === 0) {
    return (
      <div className="ao-empty">
        <Star size={32} style={{ opacity: 0.3 }} />
        <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>Aucun top profil</div>
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Définissez des top profils depuis l'onglet Shortlist</div>
      </div>
    );
  }


  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {candidates.map((c, i) => {
        const report = c.report || {};
        const strengths = c.strengths?.length ? c.strengths : report.strengths || [];
        const score = c.score_overall;
        const hasCV = !!(c.cv_minio_path || c.cv_link || c.profile_url);
        const isExp = !!expanded[c.candidate_id];


        return (
          <div key={c.candidate_id || i} style={{ borderBottom: i === candidates.length - 1 ? 'none' : '1px solid var(--border)' }}>
            <div className={`ao-cand-row${isExp ? ' ao-cand-expanded' : ''}`} style={{ background: isExp ? 'rgba(96,165,250,0.06)' : 'transparent' }} onClick={() => onToggle(c.candidate_id)}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.875rem', background: i === 0 ? '#FCD34D' : i === 1 ? 'var(--border)' : i === 2 ? 'rgba(234,88,12,0.25)' : 'var(--gray-100)', color: i < 3 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                #{i + 1}
              </div>
              <div className="avatar avatar-md" style={{ flexShrink: 0, border: '2px solid #FCD34D' }}>{initials(c.full_name || '??')}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ScrollText style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--text-primary)' }}>
                  {c.full_name || 'Candidat'}
                </ScrollText>
                <ScrollText style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {[
                    c.source && <span key="src" style={{ fontSize: '0.7rem', fontWeight: 600, color: '#60A5FA', background: 'rgba(96,165,250,0.1)', borderRadius: 3, padding: '0 4px' }}>{c.source}</span>,
                    c.title && <span key="ttl">{c.title}</span>,
                    c.location && <span key="loc" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><MapPin size={9} />{c.location}</span>,
                  ].filter(Boolean).reduce((acc, el, idx) => idx === 0 ? [el] : [...acc, <span key={`sep${idx}`} style={{ opacity: 0.4 }}> · </span>, el], [])}
                </ScrollText>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: score >= 75 ? 'rgba(34,197,94,0.14)' : 'rgba(245,158,11,0.14)',
                  color: score >= 75 ? '#4ADE80' : '#ca8a04',
                  border: `2px solid ${score >= 75 ? 'rgba(34,197,94,0.35)' : '#FCD34D'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: '1rem',
                }}
              >
                {score ?? '—'}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                <button className="btn btn-sm" onClick={() => onConvertCV(c)} style={{ whiteSpace: 'nowrap', background: '#60A5FA', color: 'white', border: 'none' }}>
                  <Zap size={13} /> Convertir CV
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => onDownloadCV(c)} style={{ opacity: hasCV ? 1 : 0.45 }}>
                  <Download size={12} /> CV
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => onTopProfil(c.candidate_id, true)} style={{ color: 'var(--warning-600)' }}>
                  <Star size={12} /> Retirer top
                </button>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onDelete(c.match_id)} style={{ color: 'var(--danger-600)' }}>
                  <Trash2 size={12} />
                </button>
              </div>
              <div style={{ color: 'var(--text-muted)', flexShrink: 0 }}>{isExp ? <ChevronUp size={16} /> : <ChevronRight size={16} />}</div>
            </div>


            {isExp && (
              <div style={{ padding: '12px 16px 16px 60px', background: 'rgba(96,165,250,0.07)', borderTop: '1px solid rgba(96,165,250,0.18)', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--blue-600)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Coordonnées</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {c.email && (
                        <a href={`mailto:${c.email}`} style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--blue-600)', fontSize: '0.8125rem', textDecoration: 'none' }}>
                          <Mail size={12} /> {c.email}
                        </a>
                      )}
                      {c.phone && (
                        <PhoneLink phone={c.phone} style={{ display:'flex', alignItems:'center', gap:6, color:'#60A5FA', fontSize:'0.8125rem' }}>
                          <Phone size={12} /> {c.phone}
                        </PhoneLink>
                      )}
                      {c.linkedin_link && (
                        <a href={c.linkedin_link} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#0A66C2', fontSize: '0.8125rem', textDecoration: 'none' }}>
                          <ExternalLink size={12} /> LinkedIn
                        </a>
                      )}
                      {!c.email && !c.phone && !c.linkedin_link && (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Aucune coordonnée</span>
                      )}
                      <div style={{ marginTop: 4 }}>
                        <button className="btn btn-secondary btn-sm" style={{ opacity: hasCV ? 1 : 0.45 }} onClick={() => onDownloadCV(c)}>
                          <Download size={12} /> CV original
                        </button>
                      </div>
                    </div>
                  </div>

                  {strengths.length > 0 && (
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#D97706', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Points forts</div>
                      <ul style={{ margin: 0, paddingLeft: 16 }}>{strengths.map((s, j) => <li key={j} style={{ fontSize: '0.875rem', lineHeight: 1.7 }}>{s}</li>)}</ul>
                    </div>
                  )}
                </div>


                {(c.softskills_report || c.hardskills_report || c.call_summary_ai) && (
                  <div style={{ borderTop: '1px solid #FDE68A', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#38BDF8' }}>Rapports de préqualification</div>
                    {c.softskills_report && (
                      <CandidateReport label="Rapport Soft Skills" tag="Préqual. Soft" nextStage="Préqual. Hard Skills"
                        color="#38BDF8" bg="rgba(8,145,178,0.08)" border="rgba(8,145,178,0.22)" html={c.softskills_report} />
                    )}
                    {c.hardskills_report && (
                      <CandidateReport label="Rapport Hard Skills" tag="Préqual. Hard" nextStage="Shortlist"
                        color="#A78BFA" bg="rgba(139,92,246,0.08)" border="rgba(139,92,246,0.22)" html={c.hardskills_report} />
                    )}
                    {c.call_summary_ai && (
                      <CandidateReport label="Résumé d'appel" tag="Appel"
                        color="#059669" bg="#ECFDF5" border="#A7F3D0" html={c.call_summary_ai} />
                    )}
                  </div>
                )}

                <div style={{ padding: '10px 14px', background: 'rgba(96,165,250,0.06)', borderRadius: 'var(--radius-md)', border: '1px solid rgba(96,165,250,0.15)', fontSize: '0.8125rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Zap size={14} color="#60A5FA" /> Cliquez sur <strong>Convertir CV</strong> pour générer la fiche au format Wikeys.
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function RTReportViewModal({ reports, candidate, onClose }) {
  const sorted = [...reports].sort(a => a.report_type === 'SOFT_SKILLS' ? -1 : 1);
  const [activeTab, setActiveTab] = React.useState(sorted[0]?.report_type || 'SOFT_SKILLS');
  const report = sorted.find(r => r.report_type === activeTab) || sorted[0];

  if (!report) return null;

  const isSoft = report.report_type === 'SOFT_SKILLS';
  const typeColor  = isSoft ? '#0891B2' : '#7C3AED';
  const typeBg     = isSoft ? 'rgba(8,145,178,0.10)' : 'rgba(139,92,246,0.10)';
  const typeBorder = isSoft ? 'rgba(8,145,178,0.28)' : 'rgba(139,92,246,0.28)';
  const note = report.note_sur_10;
  const noteColor = note >= 7 ? '#16A34A' : note >= 5 ? '#F59E0B' : '#DC2626';
  const noteBg    = note >= 7 ? 'rgba(34,197,94,0.08)' : note >= 5 ? '#FFFBEB' : 'rgba(239,68,68,0.12)';

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:720, width:'100%', maxHeight:'90vh', display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header" style={{ borderBottom:'3px solid #0891B2', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:40, height:40, borderRadius:'50%', background:'rgba(8,145,178,0.10)', border:'2px solid #A5F3FC', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <ClipboardCheck size={18} color="#0891B2"/>
            </div>
            <div>
              <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700 }}>Évaluation RT — {candidate?.full_name}</h3>
              <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:2, display:'flex', gap:10 }}>
                {report.rt_user_name && <span>Évalué par <strong>{report.rt_user_name}</strong></span>}
                {report.submitted_at && <span>· {new Date(report.submitted_at).toLocaleDateString('fr-FR')}</span>}
              </div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>

        {/* Tabs Soft / Hard */}
        {sorted.length > 1 && (
          <div style={{ display:'flex', borderBottom:'1px solid var(--border)', padding:'0 24px', flexShrink:0 }}>
            {sorted.map(r => {
              const label = r.report_type === 'SOFT_SKILLS' ? 'Soft Skills' : 'Hard Skills';
              const color = r.report_type === 'SOFT_SKILLS' ? '#0891B2' : '#7C3AED';
              const active = activeTab === r.report_type;
              return (
                <button key={r.report_type} onClick={() => setActiveTab(r.report_type)}
                  style={{ padding:'10px 18px', border:'none', background:'none', cursor:'pointer', fontWeight:active ? 700 : 500,
                    color: active ? color : 'var(--text-muted)', borderBottom: active ? `2px solid ${color}` : '2px solid transparent',
                    marginBottom:-1, fontSize:'0.875rem', transition:'all 0.15s' }}>
                  {label}
                  {r.note_sur_10 != null && (
                    <span style={{ whiteSpace: 'nowrap', marginLeft:6, padding:'1px 7px', borderRadius:20, fontSize:'0.7rem', fontWeight:700,
                      background: active ? `${color}18` : 'var(--gray-100)', color: active ? color : 'var(--text-muted)' }}>
                      {r.note_sur_10}/10
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {/* Body — scrollable */}
        <div style={{ overflowY:'auto', flex:1, padding:'20px 24px', display:'flex', flexDirection:'column', gap:20 }}>

          {/* Note globale */}
          {note != null && (
            <div style={{ display:'flex', alignItems:'center', gap:16, padding:'14px 18px', background:noteBg, border:`1px solid ${noteColor}30`, borderRadius:10 }}>
              <div style={{ textAlign:'center' }}>
                <div style={{ fontSize:'2.5rem', fontWeight:900, color:noteColor, lineHeight:1 }}>{note}</div>
                <div style={{ fontSize:'0.7rem', color:noteColor, fontWeight:600 }}>/10</div>
              </div>
              <div style={{ width:1, height:40, background:'var(--border)' }}/>
              <div>
                <div style={{ fontSize:'0.75rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', marginBottom:2 }}>Note globale</div>
                <div style={{ display:'flex', gap:4 }}>
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <div key={n} style={{ width:18, height:18, borderRadius:3, background: n <= note ? noteColor : 'var(--gray-200)' }}/>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Commentaire */}
          {report.commentaire && (
            <div>
              <div style={{ fontSize:'0.75rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:8 }}>Commentaire général</div>
              <div style={{ padding:'12px 14px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, fontSize:'0.875rem', lineHeight:1.7, whiteSpace:'pre-wrap' }}>
                {report.commentaire}
              </div>
            </div>
          )}

          {/* Table Soft Skills */}
          {isSoft && report.table_data?.length > 0 && (
            <div>
              <div style={{ fontSize:'0.75rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:10 }}>Évaluation des critères</div>
              <div style={{ border:'1px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8125rem' }}>
                  <thead>
                    <tr style={{ background:'var(--gray-50)' }}>
                      {['Critère / Soft skill', 'Note', 'Commentaire'].map(h => (
                        <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'var(--text-muted)', fontSize:'0.75rem', textTransform:'uppercase', borderBottom:'1px solid var(--border)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.table_data.map((row, i) => (
                      <tr key={i} style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none', background: i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-elevated)' }}>
                        <td style={{ padding:'8px 12px', fontWeight:600 }}>{row.critere || '—'}</td>
                        <td style={{ padding:'8px 12px', width:70 }}>
                          {row.note_sur_10 != null ? <span style={{ whiteSpace: 'nowrap', padding:'2px 8px', borderRadius:20, fontWeight:700, fontSize:'0.75rem', background: row.note_sur_10 >= 7 ? 'rgba(34,197,94,0.08)' : row.note_sur_10 >= 5 ? '#FFFBEB' : 'rgba(239,68,68,0.12)', color: row.note_sur_10 >= 7 ? '#16A34A' : row.note_sur_10 >= 5 ? '#F59E0B' : '#DC2626' }}>{row.note_sur_10}/10</span> : '—'}
                        </td>
                        <td style={{ padding:'8px 12px', color:'var(--text-muted)' }}>{row.commentaire || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Table Hard Skills */}
          {!isSoft && report.table_data?.length > 0 && (
            <div>
              <div style={{ fontSize:'0.75rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:10 }}>Évaluation des compétences</div>
              <div style={{ border:'1px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8125rem' }}>
                  <thead>
                    <tr style={{ background:'var(--gray-50)' }}>
                      {['Compétence', 'AO', 'Évaluée', 'Note', 'Commentaire'].map(h => (
                        <th key={h} style={{ padding:'8px 12px', textAlign:'left', fontWeight:700, color:'var(--text-muted)', fontSize:'0.75rem', textTransform:'uppercase', borderBottom:'1px solid var(--border)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {report.table_data.map((row, i) => (
                      <tr key={i} style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none', background: i % 2 === 0 ? 'var(--bg-card)' : 'var(--bg-elevated)' }}>
                        <td style={{ padding:'8px 12px', fontWeight:600 }}>{row.competence || '—'}</td>
                        <td style={{ padding:'8px 12px' }}>{row.demandee ? <CheckCircle size={14} color="#16A34A"/> : <XCircle size={14} color="#DC2626"/>}</td>
                        <td style={{ padding:'8px 12px' }}>{row.evaluee ? <CheckCircle size={14} color="#16A34A"/> : <span style={{ color:'var(--text-muted)' }}>—</span>}</td>
                        <td style={{ padding:'8px 12px', width:70 }}>
                          {row.note_sur_10 != null ? <span style={{ whiteSpace: 'nowrap', padding:'2px 8px', borderRadius:20, fontWeight:700, fontSize:'0.75rem', background: row.note_sur_10 >= 7 ? 'rgba(34,197,94,0.08)' : row.note_sur_10 >= 5 ? '#FFFBEB' : 'rgba(239,68,68,0.12)', color: row.note_sur_10 >= 7 ? '#16A34A' : row.note_sur_10 >= 5 ? '#F59E0B' : '#DC2626' }}>{row.note_sur_10}/10</span> : '—'}
                        </td>
                        <td style={{ padding:'8px 12px', color:'var(--text-muted)' }}>{row.commentaire || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Questions IA */}
          {report.questions_ia?.length > 0 && (
            <div>
              <div style={{ fontSize:'0.75rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:10 }}>
                Questions d'entretien ({report.questions_ia.length})
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {report.questions_ia.map((q, i) => (
                  <div key={i} style={{ padding:'12px 14px', border:'1px solid var(--border)', borderRadius:8, background:'var(--surface)' }}>
                    <div style={{ fontWeight:600, fontSize:'0.875rem', display:'flex', gap:8, marginBottom: (q.reponse_candidat || q.reponse_attendue) ? 8 : 0 }}>
                      <span style={{ whiteSpace:'nowrap', padding:'1px 7px', borderRadius:4, background:typeBg, color:typeColor, fontWeight:700, fontSize:'0.75rem', flexShrink:0 }}>Q{i+1}</span>
                      {q.question}
                    </div>
                    {q.reponse_candidat && (
                      <div style={{ marginTop:8, padding:'8px 10px', background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:6, fontSize:'0.8125rem', color:'#166534' }}>
                        <span style={{ fontWeight:700 }}>Réponse : </span>{q.reponse_candidat}
                      </div>
                    )}
                    {q.reponse_attendue && (
                      <details style={{ marginTop:6 }}>
                        <summary style={{ fontSize:'0.75rem', color:'var(--text-muted)', cursor:'pointer', userSelect:'none' }}>Réponse attendue</summary>
                        <div style={{ marginTop:6, padding:'8px 10px', background:'var(--gray-50)', borderRadius:6, fontSize:'0.8125rem', color:'var(--text-muted)', lineHeight:1.6 }}>
                          {q.reponse_attendue}
                        </div>
                      </details>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!report.commentaire && !report.table_data?.length && !report.questions_ia?.length && (
            <div style={{ textAlign:'center', color:'var(--text-muted)', padding:'20px 0', fontSize:'0.875rem' }}>
              Rapport soumis sans contenu détaillé
            </div>
          )}
        </div>

        <div style={{ padding:'12px 24px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', flexShrink:0 }}>
          <button className="btn btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}


function ScoreChangeModal({ candidate, aoId, onClose, onSave }) {
  const [newScore, setNewScore] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [loadingHist, setLoadingHist] = React.useState(true);

  React.useEffect(() => {
    rtAPI.getScoreHistory(candidate.candidate_id)
      .then(r => setHistory(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingHist(false));
  }, [candidate.candidate_id]);

  const handleSave = async () => {
    const score = parseFloat(newScore);
    if (isNaN(score) || score < 0 || score > 100) { toast.error('Score doit être entre 0 et 100'); return; }
    setSaving(true);
    try { await onSave(candidate.candidate_id, aoId, score, reason.trim() || null); }
    finally { setSaving(false); }
  };

  const currentScore = candidate.score_overall;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:520, width:'100%' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom:'3px solid #7C3AED' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Star size={20} color="#7C3AED"/>
            <div>
              <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700 }}>Modifier le score</h3>
              <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:2 }}>{candidate.full_name}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>

        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div style={{ padding:'10px 14px', background:'rgba(139,92,246,0.10)', border:'1px solid #DDD6FE', borderRadius:8, display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:'0.8125rem', color:'var(--text-muted)' }}>Score actuel (IA) :</span>
            {currentScore != null ? (
              <span style={{ fontWeight:800, fontSize:'1.25rem', color: scoreColor(currentScore) }}>{currentScore}</span>
            ) : <span style={{ color:'var(--text-muted)' }}>—</span>}
          </div>

          <div>
            <label style={{ display:'block', fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:6, textTransform:'uppercase' }}>
              Nouveau score (0 – 100)
            </label>
            <input
              type="number" min="0" max="100" step="1"
              style={{ width:'100%', padding:'8px 10px', border:'1.5px solid var(--border)', borderRadius:7, fontSize:'0.9375rem', fontWeight:700 }}
              value={newScore}
              onChange={e => setNewScore(e.target.value)}
              placeholder="ex: 78"
            />
          </div>

          <div>
            <label style={{ display:'block', fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:6, textTransform:'uppercase' }}>
              Raison (optionnel)
            </label>
            <textarea
              rows={3}
              style={{ width:'100%', padding:'8px', border:'1.5px solid var(--border)', borderRadius:7, fontSize:'0.875rem', resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Expliquez pourquoi vous modifiez ce score…"
            />
          </div>

          <div>
            <div style={{ fontSize:'0.75rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:8 }}>
              Historique des modifications
            </div>
            {loadingHist ? (
              <div style={{ display:'flex', justifyContent:'center', padding:16 }}><div className="spinner"/></div>
            ) : history.length === 0 ? (
              <div style={{ fontSize:'0.8125rem', color:'var(--text-muted)', padding:'10px 0' }}>Aucune modification enregistrée</div>
            ) : (
              <div style={{ maxHeight:180, overflowY:'auto', display:'flex', flexDirection:'column', gap:6 }}>
                {history.map(h => (
                  <div key={h.id} style={{ padding:'8px 12px', border:'1px solid var(--border)', borderRadius:6, fontSize:'0.8125rem' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2 }}>
                      <span style={{ fontWeight:700 }}>
                        {h.old_score != null ? `${h.old_score}` : 'N/A'} → <span style={{ color: scoreColor(h.new_score) }}>{h.new_score}</span>
                      </span>
                      <span style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>
                        {new Date(h.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    {h.changed_by_name && <div style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>Par {h.changed_by_name}</div>}
                    {h.reason && <div style={{ marginTop:4, color:'var(--text-primary)' }}>{h.reason}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !newScore}
            style={{ background:'#7C3AED', borderColor:'#7C3AED' }}>
            {saving ? <><div className="spinner" style={{ width:13, height:13 }}/> Enregistrement…</> : <><Star size={13}/> Modifier le score</>}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ── AOFormField défini hors du composant parent ──────────────────
   Si défini DANS AOEditModal, React voit un nouveau type de composant
   à chaque rendu → unmount/remount → perte de focus sur chaque frappe. */
function AOFormField({ label, value, onChange, textarea, rows = 3, placeholder, hint }) {
  return (
    <div className="aoe-field">
      <label className="aoe-label">{label}</label>
      {hint && <span className="aoe-hint">{hint}</span>}
      {textarea ? (
        <textarea className="aoe-textarea" rows={rows} value={value} onChange={onChange} placeholder={placeholder} />
      ) : (
        <input className="aoe-input" value={value} onChange={onChange} placeholder={placeholder} />
      )}
    </div>
  );
}

function AOEditModal({ ao, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    job_title:            ao.job_title || '',
    client:               ao.client || '',
    location:             ao.location || '',
    contract_type:        ao.contract_type || '',
    work_mode:            ao.work_mode || '',
    experience_years:     ao.experience_years || '',
    tjm:                  ao.tjm || '',
    commercial_contact:   ao.commercial_contact || '',
    response_deadline:    ao.response_deadline || '',
    start_date:           ao.start_date || '',
    executive_summary:    ao.executive_summary || '',
    detailed_description: ao.detailed_description || '',
    tech_skills:          (ao.tech_skills || []).join(', '),
    soft_skills:          (ao.soft_skills || []).join(', '),
    objectifs_principaux: (ao.objectifs_principaux || []).join('\n'),
    responsibilities:     (ao.responsibilities || []).join('\n'),
    selection_criteria:   (ao.selection_criteria || []).join('\n'),
    keywords:             (ao.keywords || []).join(', '),
  });

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        tech_skills:          form.tech_skills.split(',').map((s) => s.trim()).filter(Boolean),
        soft_skills:          form.soft_skills.split(',').map((s) => s.trim()).filter(Boolean),
        keywords:             form.keywords.split(',').map((s) => s.trim()).filter(Boolean),
        objectifs_principaux: form.objectifs_principaux.split('\n').map((s) => s.trim()).filter(Boolean),
        responsibilities:     form.responsibilities.split('\n').map((s) => s.trim()).filter(Boolean),
        selection_criteria:   form.selection_criteria.split('\n').map((s) => s.trim()).filter(Boolean),
      };
      await aoAPI.updateAO(ao.ao_id || ao.id, payload);
      toast.success('AO mis à jour');
      onSaved();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ao-edit-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ao-edit-modal">

        {/* ── Header ── */}
        <div className="ao-edit-header">
          <div>
            <div className="ao-edit-header-title">Modifier l'appel d'offres</div>
            <div className="ao-edit-header-sub">{ao.job_title || 'Sans titre'}</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        {/* ── Body ── */}
        <div className="ao-edit-body">

          <div className="aoe-section">
            <div className="aoe-section-title">Informations générales</div>
            <div className="aoe-grid-2">
              <AOFormField label="Titre du poste" value={form.job_title} onChange={set('job_title')} placeholder="ex. Développeur Java Senior" />
              <AOFormField label="Client / Entreprise" value={form.client} onChange={set('client')} placeholder="ex. Société Générale" />
              <AOFormField label="Localisation" value={form.location} onChange={set('location')} placeholder="ex. Paris 8e" />
              <AOFormField label="Type de contrat" value={form.contract_type} onChange={set('contract_type')} placeholder="ex. CDI, Mission" />
              <AOFormField label="Mode de travail" value={form.work_mode} onChange={set('work_mode')} placeholder="ex. Hybride, Full remote" />
              <AOFormField label="Expérience requise" value={form.experience_years} onChange={set('experience_years')} placeholder="ex. 5+ ans" />
              <AOFormField label="TJM / Rémunération" value={form.tjm} onChange={set('tjm')} placeholder="ex. 600-700 €/j" />
              <AOFormField label="Contact commercial" value={form.commercial_contact} onChange={set('commercial_contact')} placeholder="Nom du contact" />
              <AOFormField label="Date limite de réponse" value={form.response_deadline} onChange={set('response_deadline')} placeholder="ex. 2026-05-30" />
              <AOFormField label="Date de démarrage" value={form.start_date} onChange={set('start_date')} placeholder="ex. 2026-06-01" />
            </div>
          </div>

          <div className="aoe-section">
            <div className="aoe-section-title">Description du poste</div>
            <AOFormField label="Résumé exécutif" value={form.executive_summary} onChange={set('executive_summary')} textarea rows={3} placeholder="Présentation courte du poste et du contexte client…" />
            <AOFormField label="Description complète" value={form.detailed_description} onChange={set('detailed_description')} textarea rows={5} placeholder="Description détaillée, environnement technique, contexte de mission…" />
          </div>

          <div className="aoe-section">
            <div className="aoe-section-title">Profil recherché</div>
            <div className="aoe-grid-2">
              <AOFormField label="Compétences techniques" value={form.tech_skills} onChange={set('tech_skills')} textarea rows={3} hint="Séparées par des virgules" placeholder="Java, Spring Boot, Docker, Kubernetes…" />
              <AOFormField label="Compétences comportementales" value={form.soft_skills} onChange={set('soft_skills')} textarea rows={3} hint="Séparées par des virgules" placeholder="Leadership, communication, autonomie…" />
            </div>
            <AOFormField label="Mots-clés" value={form.keywords} onChange={set('keywords')} hint="Séparés par des virgules" placeholder="microservices, agile, fintech…" />
          </div>

          <div className="aoe-section aoe-section--last">
            <div className="aoe-section-title">Processus de sélection</div>
            <AOFormField label="Objectifs principaux" value={form.objectifs_principaux} onChange={set('objectifs_principaux')} textarea rows={3} hint="Un objectif par ligne" placeholder="Développer le module de paiement&#10;Migrer l'infrastructure vers le cloud…" />
            <AOFormField label="Responsabilités" value={form.responsibilities} onChange={set('responsibilities')} textarea rows={3} hint="Une responsabilité par ligne" placeholder="Concevoir l'architecture technique&#10;Animer les daily stand-ups…" />
            <AOFormField label="Critères de sélection" value={form.selection_criteria} onChange={set('selection_criteria')} textarea rows={3} hint="Un critère par ligne" placeholder="Maîtrise de Java 17+&#10;Expérience en environnement bancaire…" />
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="ao-edit-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><div className="spinner" style={{ width:14, height:14 }}/> Enregistrement…</> : <><Save size={14}/> Enregistrer</>}
          </button>
        </div>

      </div>
    </div>
  );
}


// ── Modal Marquer AO Répondu ──────────────────────────────────
function ReponduModal({ aoLabel, topProfils, onClose, onConfirm }) {
  const [selected, setSelected] = React.useState(() => new Set(topProfils.map(c => c.candidate_id)));
  const [saving, setSaving] = React.useState(false);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleConfirm = async () => {
    setSaving(true);
    try { await onConfirm([...selected]); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: '3px solid #16a34a' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle size={22} color="#16a34a" />
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Marquer l'AO comme Répondu</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{aoLabel}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ padding: '10px 14px', background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)', borderRadius: 8 }}>
            <div style={{ fontSize: '0.8125rem', color: '#16a34a', fontWeight: 600, marginBottom: 3 }}>
              Sélectionnez les top profils à positionner
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Les candidats sélectionnés seront créés comme positionnements (état : Positionné).
            </div>
          </div>

          {topProfils.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              Aucun top profil défini — l'AO sera quand même marqué Répondu.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {topProfils.map(c => (
                <label key={c.candidate_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, border: `1px solid ${selected.has(c.candidate_id) ? 'rgba(22,163,74,0.35)' : 'var(--border)'}`, background: selected.has(c.candidate_id) ? 'rgba(22,163,74,0.08)' : 'var(--bg-elevated)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selected.has(c.candidate_id)} onChange={() => toggle(c.candidate_id)} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#16a34a' }} />
                  <div className="avatar avatar-sm" style={{ border: '2px solid #FCD34D', flexShrink: 0 }}>{initials(c.full_name || '??')}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{c.full_name}</div>
                    {c.title && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>}
                  </div>
                  {c.score_overall != null && (
                    <span style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', fontWeight: 700, color: '#16a34a', background: 'rgba(22,163,74,0.14)', padding: '2px 8px', borderRadius: 20 }}>
                      {c.score_overall}/100
                    </span>
                  )}
                  {!c.top_profil && c.top_profil_brouillon && (
                    <span style={{ fontSize: '0.7rem', color: '#d97706', fontStyle: 'italic' }}>brouillon</span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={saving}
            style={{ background: '#16a34a', borderColor: '#16a34a', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <CheckCircle size={14} />}
            {saving ? 'Enregistrement…' : `Confirmer${selected.size > 0 ? ` — ${selected.size} positionné${selected.size > 1 ? 's' : ''}` : ' — AO Répondu'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal Marquer AO Gagné ────────────────────────────────────
function GagneModal({ aoLabel, topProfils, onClose, onConfirm }) {
  const [selected, setSelected] = React.useState(() => new Set(topProfils.map((c) => c.candidate_id)));
  const [missionStartDate, setMissionStartDate] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfirm([...selected], missionStartDate || null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 580, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: '3px solid #15803d' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle size={22} color="#15803d" />
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Marquer l'AO comme Gagné</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{aoLabel}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ padding: '10px 14px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 8 }}>
            <div style={{ fontSize: '0.8125rem', color: '#4ADE80', fontWeight: 600, marginBottom: 4 }}>
              Sélectionnez les top profils à convertir en ressources
            </div>
            <div style={{ fontSize: '0.75rem', color: '#4d7c0f' }}>
              Les candidats sélectionnés seront créés comme ressources (intercontrat) et leur positionnement sera enregistré.
            </div>
          </div>

          {topProfils.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              Aucun top profil défini — vous pouvez quand même marquer l'AO comme gagné.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {topProfils.map((c) => (
                <label key={c.candidate_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, border: `1px solid ${selected.has(c.candidate_id) ? 'rgba(34,197,94,0.35)' : 'var(--border)'}`, background: selected.has(c.candidate_id) ? 'rgba(34,197,94,0.08)' : 'var(--bg-elevated)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selected.has(c.candidate_id)} onChange={() => toggle(c.candidate_id)} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#4ADE80' }} />
                  <div className="avatar avatar-sm" style={{ border: '2px solid #FCD34D', flexShrink: 0 }}>{initials(c.full_name || '??')}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{c.full_name}</div>
                    {c.title && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.title}</div>}
                  </div>
                  {c.score_overall != null && (
                    <span style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', fontWeight: 700, color: '#4ADE80', background: 'rgba(34,197,94,0.14)', padding: '2px 8px', borderRadius: 20 }}>
                      {c.score_overall}/100
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}

          <div>
            <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, marginBottom: 5 }}>
              Date de démarrage de la mission (optionnel)
            </label>
            <input
              type="date"
              className="aoe-input"
              value={missionStartDate}
              onChange={(e) => setMissionStartDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={saving}
            style={{ background: '#4ADE80', borderColor: '#4ADE80', opacity: saving ? 0.7 : 1 }}
          >
            {saving ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <CheckCircle size={14} />}
            {saving ? 'Enregistrement…' : `Confirmer — AO Gagné${selected.size > 0 ? ` (${selected.size} ressource${selected.size > 1 ? 's' : ''})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal édition rapport (soft / hard) ──────────────────────
const EDIT_REPORT_CFG = {
  soft: { title: 'Modifier le rapport Soft Skills', icon: '💬', color: '#0891b2', border: 'rgba(8,145,178,0.28)', bg: 'rgba(8,145,178,0.10)' },
  hard: { title: 'Modifier le rapport Hard Skills', icon: '🔧', color: '#7c3aed', border: 'rgba(139,92,246,0.28)', bg: 'rgba(139,92,246,0.10)' },
};

function EditReportModal({ type, currentHtml, onClose, onSave }) {
  const cfg = EDIT_REPORT_CFG[type] || EDIT_REPORT_CFG.soft;
  const initialText = (currentHtml || '').replace(/<[^>]+>/g, '').trim();
  const [reportData, setReportData] = React.useState({ html: currentHtml || '', text: initialText });
  const MIN_CHARS = 30;
  const isValid = reportData.text.trim().length >= MIN_CHARS;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 740, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: `3px solid ${cfg.color}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.5rem' }}>{cfg.icon}</span>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{cfg.title}</h3>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <RichTextEditor
            initialContent={currentHtml}
            onUpdate={setReportData}
            minHeight={220}
            autoFocus
          />
          {!isValid && reportData.text.length > 0 && (
            <p style={{ fontSize: '0.75rem', color: 'var(--danger-600)', margin: '6px 0 0' }}>
              Rapport trop court ({MIN_CHARS - reportData.text.length} caractères manquants).
            </p>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button
            className="btn btn-primary"
            disabled={!isValid}
            onClick={() => isValid && onSave(reportData.html)}
            style={{ background: cfg.color, borderColor: cfg.color, opacity: isValid ? 1 : 0.5 }}
          >
            <CheckCircle size={14} /> Enregistrer les modifications
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal avancement (rapport obligatoire) ────────────────────
const ADVANCE_CFG = {
  soft: {
    title: 'Préqualification Soft Skills',
    icon: '💬',
    color: '#7c3aed',
    border: '#ddd6fe',
    bg: '#f5f3ff',
    hints: ['Communication et clarté d\'expression', 'Motivation et projet professionnel', 'Disponibilité et mobilité', 'Posture et savoir-être', 'Adéquation culturelle'],
    placeholder: 'Ex : Très bonne communication, motivé par les nouvelles technologies, disponible sous 1 mois, mobile IDF, profil dynamique et structuré…',
  },
  hard: {
    title: 'Préqualification Hard Skills',
    icon: '🔧',
    color: '#0891b2',
    border: 'rgba(56,189,248,0.25)',
    bg: 'rgba(56,189,248,0.08)',
    hints: ['Stack technique maîtrisée', 'Expériences clés vérifiées', 'Certifications', 'Niveau d\'expertise', 'Adéquation avec les exigences du poste'],
    placeholder: 'Ex : Maîtrise confirmée de React/Node, 8 ans d\'expérience en gestion de projet, certifié PMP, niveau expert sur les outils demandés…',
  },
  shortlist: {
    title: 'Passage en Shortlist',
    icon: '🏅',
    color: '#60A5FA',
    border: '#fed7aa',
    bg: '#fff7ed',
    hints: ['Points forts qui justifient la sélection', 'Adéquation globale au poste', 'Recommandation pour le client'],
    placeholder: 'Ex : Profil très aligné avec les exigences client, expérience solide sur des missions similaires, excellent score IA (67/100), sélectionné en priorité…',
  },
  top: {
    title: 'Passage en Top Profil',
    icon: '⭐',
    color: '#ca8a04',
    border: '#fcd34d',
    bg: '#fffbeb',
    hints: ['Pourquoi c\'est le meilleur candidat', 'Éléments différenciants', 'Recommandation forte pour le client'],
    placeholder: 'Ex : Candidat exceptionnel, profil rare sur le marché, expérience parfaitement adaptée, fortement recommandé pour présentation client…',
  },
};

function PrequalModal({ type, onClose, onSave, onDisqualify, defaultReport }) {
  const [reportData, setReportData] = React.useState({ html: defaultReport || '', text: defaultReport || '' });
  const editorRef = React.useRef(null);
  const cfg = ADVANCE_CFG[type] || ADVANCE_CFG.soft;
  const MIN_CHARS = 30;
  const isValid = reportData.text.trim().length >= MIN_CHARS;
  const showDisqualify = onDisqualify && (type === 'soft' || type === 'hard');

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 740, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: `3px solid ${cfg.color}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.5rem' }}>{cfg.icon}</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>{cfg.title}</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>Rapport obligatoire — min. {MIN_CHARS} caractères</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ padding: '10px 14px', background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: 8 }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: cfg.color, marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Suggestions — cliquez pour insérer
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {cfg.hints.map((h, i) => (
                <span key={i}
                  style={{ padding: '3px 10px', background: 'var(--bg-card)', border: `1px solid ${cfg.border}`, borderRadius: 20, fontSize: '0.75rem', color: cfg.color, fontWeight: 500, cursor: 'pointer', userSelect: 'none' }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    editorRef.current?.insertContent(`<p>• ${h} : </p>`);
                  }}>
                  + {h}
                </span>
              ))}
            </div>
          </div>

          <div>            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Rapport de qualification <span style={{ color: 'var(--danger-600)' }}>*</span>
              </label>
              <span style={{ fontSize: '0.7rem', fontWeight: 700, color: isValid ? '#16a34a' : reportData.text.length > 0 ? '#dc2626' : '#94a3b8' }}>
                {reportData.text.length} / {MIN_CHARS} car. min {isValid ? '✓' : ''}
              </span>
            </div>
            <RichTextEditor
              ref={editorRef}
              onUpdate={setReportData}
              placeholder={cfg.placeholder}
              initialContent={defaultReport || ''}
              minHeight={200}
              autoFocus
            />
            {!isValid && reportData.text.length > 0 && (
              <p style={{ fontSize: '0.75rem', color: 'var(--danger-600)', margin: '6px 0 0' }}>
                Veuillez rédiger un rapport plus détaillé ({MIN_CHARS - reportData.text.length} caractères manquants).
              </p>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          {showDisqualify && (
            <button
              className="btn btn-danger"
              onClick={() => onDisqualify(reportData.html)}
              style={{ background: '#dc2626', borderColor: '#dc2626', color: '#fff' }}
            >
              <X size={14} /> Disqualifier
            </button>
          )}
          <button
            className="btn btn-primary"
            disabled={!isValid}
            onClick={() => isValid && onSave(reportData.html)}
            style={{ background: cfg.color, borderColor: cfg.color, opacity: isValid ? 1 : 0.5 }}
          >
            <CheckCircle size={14} /> Valider et avancer
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal unifié appel + compte-rendu (3 onglets) ─────────────
function UnifiedCallModal({ candidate, isCalling, onCall, onHangup, onSave, onClose }) {
  const defaultTab = candidate._defaultTab || 'call';
  const [activeTab, setActiveTab] = React.useState(defaultTab);
  const [transcript, setTranscript] = React.useState(candidate.call_transcript || '');
  const existingHtml = candidate.call_summary_ai || '';
  const existingText = existingHtml.replace(/<[^>]+>/g, '').trim();
  const [summaryData, setSummaryData] = React.useState({ html: existingHtml, text: existingText });
  const [recording, setRecording] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const prevIsCallingRef = useRef(isCalling);
  const isValid = summaryData.text.trim().length >= 20;

  const HINTS = ['Disponibilité', 'Compétences vérifiées', 'Motivation', 'Prétentions salariales', 'Points de vigilance', 'Prochaine action'];

  React.useEffect(() => {
    if (prevIsCallingRef.current && !isCalling) {
      setActiveTab('summary');
      toast('Appel terminé — renseignez la synthèse', { icon: '📋' });
    }
    prevIsCallingRef.current = isCalling;
  }, [isCalling]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Impossible d'accéder au microphone");
    }
  };

  const stopRecordingAndTranscribe = () => {
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    mr.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
      mr.stream.getTracks().forEach((t) => t.stop());
      setRecording(false);
      setProcessing(true);
      try {
        const res = await matchAPI.callAI(candidate.match_id, blob);
        if (res.data.transcript) setTranscript(res.data.transcript);
        if (res.data.summary) {
          const html = `<p>${res.data.summary.replace(/\n/g, '</p><p>')}</p>`;
          setSummaryData({ html, text: res.data.summary });
          setActiveTab('summary');
          toast.success('Transcription IA terminée');
        }
      } catch (e) {
        toast.error(extractError(e));
      } finally {
        setProcessing(false);
      }
    };
    mr.stop();
  };

  const TABS = [
    { key: 'call', label: '📞 Appeler' },
    { key: 'summary', label: '📋 Synthèse' },
    { key: 'transcript', label: '📝 Transcription' },
  ];

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 760, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: '3px solid #0891b2' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.5rem' }}>📞</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Appel & compte-rendu</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{candidate.full_name || 'Candidat'}</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {processing && (
              <span style={{ fontSize: '0.75rem', color: '#0891b2', display: 'flex', alignItems: 'center', gap: 5 }}>
                <div className="spinner" style={{ width: 12, height: 12, borderTopColor: '#0891b2' }} /> Transcription IA…
              </span>
            )}
            <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 20px', background: 'var(--surface)' }}>
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              style={{ padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: activeTab === t.key ? 700 : 400, color: activeTab === t.key ? '#0891b2' : 'var(--text-muted)', borderBottom: activeTab === t.key ? '2px solid #0891b2' : '2px solid transparent', marginBottom: -1, whiteSpace: 'nowrap' }}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* ── Onglet 1 : Appeler ── */}
          {activeTab === 'call' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {isCalling ? (
                <div style={{ padding: '16px', background: 'rgba(239,68,68,0.12)', border: '1px solid #FCA5A5', borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9375rem', fontWeight: 700, color: '#dc2626' }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#dc2626', display: 'inline-block', animation: 'pulse 1s infinite' }} />
                    Appel en cours avec {candidate.full_name}…
                  </div>
                  <button className="btn" onClick={onHangup} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '8px 20px' }}>
                    <Phone size={14} /> Raccrocher
                  </button>
                </div>
              ) : (
                <div style={{ padding: '16px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                  {candidate.phone ? (
                    <>
                      <div style={{ fontSize: '0.9375rem', color: 'var(--text-primary)', fontWeight: 600 }}>
                        Appeler <span style={{ color: '#059669' }}>{candidate.phone}</span>
                      </div>
                      <button className="btn" onClick={onCall} style={{ background: '#059669', color: 'white', border: 'none', padding: '8px 20px' }}>
                        <Phone size={14} /> Lancer l'appel Twilio
                      </button>
                    </>
                  ) : (
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                      Aucun numéro de téléphone disponible pour ce candidat.
                    </div>
                  )}
                </div>
              )}

              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>
                  Enregistrement & transcription automatique
                </div>
                <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, fontSize: '0.75rem', color: '#FCD34D', marginBottom: 10 }}>
                  ⚠️ L'enregistrement capture uniquement votre microphone, pas l'audio Twilio. Parlez pendant/après l'appel pour dicter votre compte-rendu et le transcrire via IA.
                </div>
                {recording ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8125rem', color: '#dc2626', fontWeight: 600 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#dc2626', display: 'inline-block', animation: 'pulse 1s infinite' }} />
                      Enregistrement en cours…
                    </div>
                    <button className="btn btn-sm" onClick={stopRecordingAndTranscribe} style={{ background: '#dc2626', color: 'white', border: 'none', alignSelf: 'flex-start' }}>
                      ⏹ Arrêter et transcrire via IA
                    </button>
                  </div>
                ) : (
                  <button className="btn btn-sm btn-secondary" onClick={startRecording} disabled={processing} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    🎙 Démarrer l'enregistrement
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Onglet 2 : Synthèse ── */}
          {activeTab === 'summary' && (
            <>
              <div style={{ padding: '10px 12px', background: 'rgba(56,189,248,0.08)', border: '1px solid #bae6fd', borderRadius: 8 }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0891b2', marginBottom: 7, textTransform: 'uppercase' }}>Éléments clés — cliquez pour insérer</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {HINTS.map((h, i) => (
                    <span key={i} onMouseDown={(e) => e.preventDefault()}
                      style={{ padding: '3px 10px', background: 'var(--bg-card)', border: '1px solid #bae6fd', borderRadius: 20, fontSize: '0.75rem', color: '#0891b2', cursor: 'pointer' }}
                      onClick={() => setSummaryData((d) => ({ html: d.html + `<p>• ${h} : </p>`, text: d.text + (d.text ? '\n' : '') + '• ' + h + ' : ' }))}>
                      + {h}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontSize: '0.8125rem', fontWeight: 600 }}>Synthèse de l'appel <span style={{ color: 'var(--danger-600)' }}>*</span></label>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: isValid ? '#16a34a' : summaryData.text.length > 0 ? '#dc2626' : '#94a3b8' }}>
                    {summaryData.text.length} car. {isValid ? '✓' : '(min. 20)'}
                  </span>
                </div>
                <RichTextEditor
                  onUpdate={setSummaryData}
                  value={summaryData.html}
                  placeholder="• Disponibilité : immédiate&#10;• Compétences : maîtrise React…&#10;• Motivation : très intéressé…&#10;• Prétentions : 55k€ brut&#10;• Points de vigilance : …&#10;• Prochaine action : entretien à planifier"
                  minHeight={200}
                  autoFocus
                />
              </div>
            </>
          )}
          {activeTab === 'transcript' && (
            <div>
              <label style={{ fontSize: '0.8125rem', fontWeight: 600, display: 'block', marginBottom: 8 }}>Transcription brute de l'appel <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(facultatif)</span></label>
              <textarea rows={13}
                placeholder="Collez ici la transcription complète ou dictez librement ce qui a été dit…"
                value={transcript} onChange={(e) => setTranscript(e.target.value)}
                style={{ width: '100%', padding: '12px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', lineHeight: 1.7, resize: 'vertical', fontFamily: 'inherit', outline: 'none', minHeight: 240 }} />
              <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                💡 Utilisez le bouton <strong>"Enregistrer &amp; transcrire"</strong> en haut pour transcrire automatiquement votre appel via IA.
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" disabled={!isValid || processing}
            onClick={() => isValid && onSave(transcript, summaryData.html)}
            style={{ background: '#0891b2', borderColor: '#0891b2', opacity: isValid && !processing ? 1 : 0.5 }}>
            <Save size={14} /> Enregistrer le compte-rendu
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal correction infos candidat ──────────────────────────
function CandEditField({ label, field, placeholder, type = 'text', form, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
      <input
        type={type}
        className="aoe-input"
        value={form[field]}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function CandidateEditModal({ candidate, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    full_name:    candidate.full_name || '',
    title:        candidate.title || '',
    email:        candidate.email || '',
    phone:        candidate.phone || '',
    location:     candidate.location || '',
    linkedin_link: candidate.linkedin_link || '',
    experience:   candidate.experience || '',
    availability: candidate.availability || '',
    salary:       candidate.salary || '',
  });

  const handleChange = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const handleSave = async () => {
    if (!form.full_name.trim()) { toast.error('Le nom est obligatoire'); return; }
    setSaving(true);
    try {
      const res = await candidateAPI.patch(candidate.candidate_id, form);
      onSaved({ id: candidate.candidate_id, ...form, ...res.data });
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: '3px solid #60A5FA' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Edit3 size={18} color="#60A5FA" />
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Corriger les informations</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
                {candidate.full_name} · Source : {candidate.source || '—'}
              </div>
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1/-1' }}>
            <CandEditField label="Nom complet *" field="full_name" placeholder="Prénom Nom" form={form} onChange={handleChange} />
          </div>
          <CandEditField label="Titre / Poste" field="title" placeholder="ex. Développeur Java Senior" form={form} onChange={handleChange} />
          <CandEditField label="Localisation" field="location" placeholder="ex. Paris 8e" form={form} onChange={handleChange} />
          <CandEditField label="Email" field="email" placeholder="candidat@email.com" type="email" form={form} onChange={handleChange} />
          <CandEditField label="Téléphone" field="phone" placeholder="+33 6 xx xx xx xx" form={form} onChange={handleChange} />
          <CandEditField label="LinkedIn" field="linkedin_link" placeholder="https://linkedin.com/in/..." form={form} onChange={handleChange} />
          <CandEditField label="Expérience" field="experience" placeholder="ex. 8 ans" form={form} onChange={handleChange} />
          <CandEditField label="Disponibilité" field="availability" placeholder="ex. Immédiate, 1 mois" form={form} onChange={handleChange} />
          <CandEditField label="Prétentions salariales" field="salary" placeholder="ex. 55k€ - 60k€" form={form} onChange={handleChange} />
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ background: '#60A5FA', borderColor: '#60A5FA' }}>
            {saving ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Enregistrement…</> : <><Save size={14} /> Enregistrer</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal envoi email avec templates ─────────────────────────
const EMAIL_TEMPLATES = [
  {
    id: 'premier_echange',
    label: 'Premier échange',
    subject: (name) => `Opportunité professionnelle — Votre profil nous intéresse`,
    body: (name) => `Bonjour ${name || ''},

Je me permets de vous contacter suite à l'étude de votre profil sur APEC. Votre parcours a retenu notre attention dans le cadre d'un projet pour l'un de nos clients.

Seriez-vous disponible pour un bref échange téléphonique de 15 minutes afin de vous présenter cette opportunité et d'évaluer votre intérêt ?

Dans l'attente de votre retour, je reste disponible pour tout renseignement complémentaire.

Cordialement,`,
  },
  {
    id: 'entretien',
    label: 'Convocation entretien',
    subject: (name) => `Invitation à un entretien — Suite de votre candidature`,
    body: (name) => `Bonjour ${name || ''},

Suite à notre échange, nous avons le plaisir de vous inviter à un entretien avec notre équipe.

Pourriez-vous nous indiquer vos disponibilités pour la semaine prochaine ? Nous nous adapterons à votre agenda.

L'entretien se déroulera en visioconférence (ou en présentiel selon votre préférence) et durera environ 45 minutes.

Merci de nous confirmer votre intérêt pour la suite du processus.

Cordialement,`,
  },
  {
    id: 'disponibilite',
    label: 'Vérification disponibilité',
    subject: (name) => `Vérification de votre disponibilité — Opportunité en cours`,
    body: (name) => `Bonjour ${name || ''},

Nous avons une nouvelle opportunité professionnelle qui pourrait correspondre à votre profil et à vos recherches actuelles.

Pourriez-vous nous confirmer :
• Votre disponibilité actuelle (immédiate / sous x semaines / mois)
• Votre type de recherche (CDI / freelance / les deux)
• Votre localisation souhaitée ou mobilité

Ces informations nous permettront de vous proposer des missions adaptées à votre situation.

Dans l'attente de votre retour,

Cordialement,`,
  },
  {
    id: 'relance',
    label: 'Relance candidat',
    subject: (name) => `Relance — Opportunité professionnelle toujours d'actualité`,
    body: (name) => `Bonjour ${name || ''},

Je me permets de revenir vers vous concernant l'opportunité professionnelle que nous vous avons présentée.

Avez-vous eu le temps d'y réfléchir ? Cette mission est toujours disponible et correspond à votre profil.

N'hésitez pas à me contacter pour toute question.

Cordialement,`,
  },
  {
    id: 'refus',
    label: 'Refus courtois',
    subject: (name) => `Suite à l'étude de votre candidature`,
    body: (name) => `Bonjour ${name || ''},

Nous vous remercions de l'intérêt que vous portez à nos opportunités et du temps que vous nous avez accordé.

Après étude attentive de votre profil, nous n'avons pas de mission correspondant à votre recherche en ce moment. Cependant, nous conservons votre candidature dans notre base et reviendrons vers vous dès qu'une opportunité correspondant à vos compétences se présentera.

Nous vous souhaitons une bonne continuation dans votre recherche.

Cordialement,`,
  },
];

function EmailModal({ matchId, candidateId, aoId, candidateName, candidateEmail, onClose, onSend, onScheduleInterview }) {
  const { user: currentUser } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');
  const [sending, setSending] = React.useState(false);

  // CC state
  const [allUsers, setAllUsers] = React.useState([]);
  const [ccUsers, setCcUsers] = React.useState([]);

  // Teams scheduling state
  const [entDate, setEntDate] = React.useState('');
  const [entHour, setEntHour] = React.useState('09');
  const [entMin,  setEntMin]  = React.useState('00');
  const dueDate = entDate ? `${entDate}T${entHour}:${entMin}` : '';
  const [duration, setDuration] = React.useState(60);
  const [invitedUsers, setInvitedUsers] = React.useState([]);

  // Personal templates state
  const [userTemplates, setUserTemplates] = React.useState([]);
  const [savingTpl, setSavingTpl] = React.useState(false);
  const [saveForm, setSaveForm] = React.useState(null); // null | { name: '' }
  const [editingTpl, setEditingTpl] = React.useState(null); // { id, name, subject, body }

  const isEntretien = selectedTemplate === 'entretien';
  const isValid = candidateEmail && (isEntretien ? dueDate : (subject.trim() && body.trim()));

  React.useEffect(() => {
    api.get('/auth/users?is_active=true').then(r => setAllUsers(r.data || [])).catch(() => {});
    emailTemplatesAPI.list().then(r => setUserTemplates(r.data || [])).catch(() => {});
  }, []);

  const applyTemplate = (tpl) => {
    setSelectedTemplate(tpl.id);
    if (tpl.subject && typeof tpl.subject === 'function') {
      setSubject(tpl.subject(candidateName));
      setBody(tpl.body(candidateName));
    } else {
      setSubject(tpl.subject);
      setBody(tpl.body);
    }
  };

  const addCc = (email) => {
    if (email && !ccUsers.includes(email) && email !== candidateEmail) setCcUsers(prev => [...prev, email]);
  };
  const removeCc = (email) => setCcUsers(prev => prev.filter(e => e !== email));

  const addInvited = (email) => {
    if (email && !invitedUsers.includes(email)) setInvitedUsers(prev => [...prev, email]);
  };
  const removeInvited = (email) => setInvitedUsers(prev => prev.filter(e => e !== email));

  const handleSaveTemplate = async () => {
    if (!saveForm?.name?.trim() || !subject.trim() || !body.trim()) return;
    setSavingTpl(true);
    try {
      const res = await emailTemplatesAPI.create({ name: saveForm.name.trim(), subject: subject.trim(), body: body.trim() });
      setUserTemplates(prev => [res.data, ...prev]);
      setSaveForm(null);
      toast.success('Modèle enregistré');
    } catch (e) { toast.error(extractError(e)); }
    finally { setSavingTpl(false); }
  };

  const handleDeleteTemplate = async (tpl) => {
    try {
      await emailTemplatesAPI.remove(tpl.id);
      setUserTemplates(prev => prev.filter(t => t.id !== tpl.id));
      toast.success('Modèle supprimé');
    } catch (e) { toast.error(extractError(e)); }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTpl?.name?.trim()) return;
    setSavingTpl(true);
    try {
      const res = await emailTemplatesAPI.update(editingTpl.id, {
        name: editingTpl.name.trim(),
        subject: editingTpl.subject.trim(),
        body: editingTpl.body.trim(),
      });
      setUserTemplates(prev => prev.map(t => t.id === editingTpl.id ? res.data : t));
      setEditingTpl(null);
      toast.success('Modèle mis à jour');
    } catch (e) { toast.error(extractError(e)); }
    finally { setSavingTpl(false); }
  };

  const handleSend = async () => {
    if (!isValid) return;
    setSending(true);
    try {
      if (isEntretien && onScheduleInterview) {
        await onScheduleInterview({
          ao_id: aoId,
          title: subject.trim() || `Entretien — ${candidateName}`,
          due_date: new Date(dueDate).toISOString(),
          duration_minutes: duration,
          description: body.trim() || null,
          invited_users: invitedUsers,
        });
      } else {
        await onSend(subject, body, ccUsers);
      }
    } catch {
      // errors shown via toast in parent handler
    } finally {
      setSending(false);
    }
  };

  const availableForCc = allUsers.filter(u => u.email && !ccUsers.includes(u.email) && u.email !== currentUser?.email && u.email !== candidateEmail);
  const availableForInvite = allUsers.filter(u => u.email && !invitedUsers.includes(u.email) && u.email !== currentUser?.email);

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 900, width: '100%', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: `3px solid ${isEntretien ? '#4ADE80' : '#60A5FA'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: isEntretien ? 'rgba(34,197,94,0.08)' : 'rgba(96,165,250,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isEntretien ? <Video size={18} color="#15803d" /> : <Mail size={18} color="#60A5FA" />}
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>
                {isEntretien ? 'Planifier un entretien Teams' : 'Envoyer un email'}
              </h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{candidateName || 'Candidat'}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body" style={{ display: 'flex', gap: 20 }}>
          {/* Left column — Templates */}
          <div style={{ width: 210, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', maxHeight: 520 }}>

            {/* Prédéfinis */}
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 2 }}>Modèles prédéfinis</div>
            {EMAIL_TEMPLATES.map((tpl) => (
              <button key={tpl.id} onClick={() => applyTemplate(tpl)}
                style={{
                  padding: '8px 12px',
                  border: `1.5px solid ${selectedTemplate === tpl.id ? (tpl.id === 'entretien' ? '#4ADE80' : '#60A5FA') : 'var(--border)'}`,
                  borderRadius: 8,
                  background: selectedTemplate === tpl.id ? (tpl.id === 'entretien' ? 'rgba(34,197,94,0.08)' : 'rgba(96,165,250,0.08)') : 'var(--surface)',
                  cursor: 'pointer', fontSize: '0.8125rem',
                  fontWeight: selectedTemplate === tpl.id ? 700 : 400,
                  color: selectedTemplate === tpl.id ? (tpl.id === 'entretien' ? '#4ADE80' : '#60A5FA') : 'var(--text-primary)',
                  textAlign: 'left', transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                {tpl.id === 'entretien' && <Video size={12} />}
                {tpl.label}
              </button>
            ))}

            {/* Mes modèles */}
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginTop: 10, marginBottom: 2 }}>Mes modèles</div>
            {userTemplates.length === 0 && (
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '6px 0', lineHeight: 1.4 }}>
                Aucun modèle perso. Rédigez un email et cliquez "Enregistrer comme modèle".
              </div>
            )}
            {userTemplates.map((tpl) => (
              <div key={tpl.id} style={{ position: 'relative' }}>
                <button onClick={() => applyTemplate(tpl)}
                  style={{
                    width: '100%', padding: '7px 32px 7px 12px',
                    border: `1.5px solid ${selectedTemplate === tpl.id ? '#60A5FA' : 'var(--border)'}`,
                    borderRadius: 8, background: selectedTemplate === tpl.id ? 'rgba(96,165,250,0.08)' : 'var(--surface)',
                    cursor: 'pointer', fontSize: '0.8125rem',
                    fontWeight: selectedTemplate === tpl.id ? 700 : 400,
                    color: selectedTemplate === tpl.id ? '#60A5FA' : 'var(--text-primary)',
                    textAlign: 'left', transition: 'all 0.15s',
                  }}>
                  {tpl.name}
                </button>
                <div style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 2 }}>
                  <button onClick={() => setEditingTpl({ id: tpl.id, name: tpl.name, subject: tpl.subject, body: tpl.body })}
                    title="Modifier" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                    <Edit3 size={11} />
                  </button>
                  <button onClick={() => handleDeleteTemplate(tpl)}
                    title="Supprimer" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            ))}

            {/* Enregistrer modèle courant */}
            {!isEntretien && (
              saveForm === null ? (
                <button onClick={() => setSaveForm({ name: '' })}
                  style={{ marginTop: 4, padding: '7px 12px', border: '1.5px dashed var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Plus size={12} /> Enregistrer comme modèle
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 4, padding: '8px', background: 'var(--gray-50)', border: '1px solid var(--border)', borderRadius: 8 }}>
                  <input autoFocus value={saveForm.name} onChange={e => setSaveForm({ name: e.target.value })}
                    placeholder="Nom du modèle…"
                    style={{ padding: '6px 8px', border: '1.5px solid var(--border)', borderRadius: 6, fontSize: '0.8rem', fontFamily: 'inherit', outline: 'none', background: 'var(--bg-card)' }} />
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={handleSaveTemplate} disabled={savingTpl || !saveForm.name.trim()}
                      style={{ flex: 1, padding: '5px', border: 'none', borderRadius: 6, background: '#60A5FA', color: '#fff', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, opacity: saveForm.name.trim() ? 1 : 0.5 }}>
                      {savingTpl ? '…' : 'Sauvegarder'}
                    </button>
                    <button onClick={() => setSaveForm(null)}
                      style={{ padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', cursor: 'pointer', fontSize: '0.75rem' }}>
                      Annuler
                    </button>
                  </div>
                </div>
              )
            )}
          </div>

          {/* Right column — Form */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {candidateEmail ? (
              <div style={{ padding: '8px 12px', background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 8, fontSize: '0.8125rem', color: '#4ADE80', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Mail size={14} /> Destinataire : <strong>{candidateEmail}</strong>
              </div>
            ) : (
              <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, fontSize: '0.8125rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={14} /> Ce candidat n'a pas d'adresse email enregistrée.
              </div>
            )}

            {/* CC section — email uniquement */}
            {!isEntretien && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Copie (CC) — membres WIBusiness</label>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: ccUsers.length ? 6 : 0 }}>
                  {ccUsers.map(email => {
                    const u = allUsers.find(x => x.email === email);
                    return (
                      <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.25)', borderRadius: 20, fontSize: '0.75rem', color: '#60A5FA', fontWeight: 600 }}>
                        {u ? `${u.prenom} ${u.nom}` : email}
                        <button onClick={() => removeCc(email)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'rgba(96,165,250,0.5)', display: 'flex', alignItems: 'center' }}>
                          <X size={11} />
                        </button>
                      </span>
                    );
                  })}
                </div>
                <select value="" onChange={(e) => addCc(e.target.value)}
                  style={{ width: '100%', padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.8125rem', fontFamily: 'inherit', outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)' }}>
                  <option value="">Ajouter un membre en copie…</option>
                  {availableForCc.map(u => (
                    <option key={u.id} value={u.email}>{u.prenom} {u.nom} — {u.email}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Teams scheduling section */}
            {isEntretien && (
              <div style={{ padding: '14px 16px', background: 'rgba(34,197,94,0.08)', border: '1px solid #86EFAC', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: '#4ADE80', fontWeight: 700 }}>
                  <Video size={14} /> Entretien Teams — planification automatique
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#4ADE80', marginBottom: 4 }}>
                      Date et heure <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', gap: 5 }}>
                      <input type="date" value={entDate} onChange={e => setEntDate(e.target.value)}
                        style={{ flex: 2, padding: '8px 6px', border: `1.5px solid ${entDate ? 'rgba(34,197,94,0.35)' : 'rgba(34,197,94,0.25)'}`, borderRadius: 7, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', background: 'var(--bg-card)', color: 'var(--text-primary)', colorScheme: 'dark' }} />
                      <select value={entHour} onChange={e => setEntHour(e.target.value)}
                        style={{ flex: 1, padding: '8px 4px', border: '1.5px solid #BBF7D0', borderRadius: 7, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'var(--bg-card)', boxSizing: 'border-box' }}>
                        {Array.from({length: 24}, (_, i) => String(i).padStart(2, '0')).map(h => (
                          <option key={h} value={h}>{h}h</option>
                        ))}
                      </select>
                      <select value={entMin} onChange={e => setEntMin(e.target.value)}
                        style={{ flex: 1, padding: '8px 4px', border: '1.5px solid #BBF7D0', borderRadius: 7, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'var(--bg-card)', boxSizing: 'border-box' }}>
                        {['00', '15', '30', '45'].map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#4ADE80', marginBottom: 4 }}>Durée</label>
                    <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}
                      style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #BBF7D0', borderRadius: 7, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'var(--bg-card)', boxSizing: 'border-box' }}>
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={45}>45 min</option>
                      <option value={60}>1h</option>
                      <option value={90}>1h30</option>
                      <option value={120}>2h</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#4ADE80', marginBottom: 4 }}>Inviter des membres WIBusiness</label>
                  <select value="" onChange={(e) => addInvited(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #BBF7D0', borderRadius: 7, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'var(--bg-card)', boxSizing: 'border-box' }}>
                    <option value="">Sélectionner un membre…</option>
                    {availableForInvite.map(u => (
                      <option key={u.id} value={u.email}>{u.prenom} {u.nom} — {u.role}</option>
                    ))}
                  </select>
                  {invitedUsers.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                      {invitedUsers.map(email => {
                        const u = allUsers.find(x => x.email === email);
                        return (
                          <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', background: 'var(--bg-card)', border: '1px solid #86EFAC', borderRadius: 20, fontSize: '0.75rem', color: '#4ADE80', fontWeight: 600 }}>
                            {u ? `${u.prenom} ${u.nom}` : email}
                            <button onClick={() => removeInvited(email)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'rgba(34,197,94,0.35)', display: 'flex', alignItems: 'center' }}>
                              <X size={11} />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="input-group" style={{ margin: 0 }}>
              <label className="input-label" style={{ marginBottom: 6 }}>
                {isEntretien ? "Titre de l'entretien" : 'Objet'} <span style={{ color: 'var(--danger-600)' }}>*</span>
              </label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                placeholder={isEntretien ? "Titre de l'entretien…" : "Objet de l'email…"}
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none' }} />
            </div>

            <div className="input-group" style={{ margin: 0, flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <label className="input-label" style={{ margin: 0 }}>
                  {isEntretien ? 'Message personnalisé (optionnel)' : <>Message <span style={{ color: 'var(--danger-600)' }}>*</span></>}
                </label>
                {!isEntretien && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{body.length} car.</span>}
              </div>
              <textarea rows={isEntretien ? 4 : 10} value={body} onChange={(e) => setBody(e.target.value)}
                placeholder={isEntretien ? "Informations complémentaires pour le candidat…" : "Sélectionnez un modèle ou rédigez votre message…"}
                style={{ width: '100%', padding: '12px 14px', border: `1.5px solid ${(!isEntretien && body.trim()) ? '#bfdbfe' : 'var(--border)'}`, borderRadius: 8, fontSize: '0.875rem', lineHeight: 1.75, resize: 'vertical', fontFamily: 'inherit', outline: 'none', minHeight: isEntretien ? 80 : 220 }} />
            </div>

            {isEntretien && (
              <div style={{ padding: '8px 12px', background: 'rgba(96,165,250,0.07)', border: '1px solid rgba(96,165,250,0.22)', borderRadius: 8, fontSize: '0.8125rem', color: '#60A5FA', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Video size={13} /> Le lien Teams sera généré automatiquement et envoyé au candidat par email.
              </div>
            )}
          </div>
        </div>

        {/* Edit template modal */}
        {editingTpl && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}>
            <div style={{ background: 'var(--bg-card)', borderRadius: 10, padding: 20, width: 400, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Modifier le modèle</div>
              <input value={editingTpl.name} onChange={e => setEditingTpl(p => ({ ...p, name: e.target.value }))}
                placeholder="Nom…" style={{ padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 7, fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none', background: 'var(--bg-card)' }} />
              <input value={editingTpl.subject} onChange={e => setEditingTpl(p => ({ ...p, subject: e.target.value }))}
                placeholder="Objet…" style={{ padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 7, fontSize: '0.85rem', fontFamily: 'inherit', outline: 'none', background: 'var(--bg-card)' }} />
              <textarea rows={6} value={editingTpl.body} onChange={e => setEditingTpl(p => ({ ...p, body: e.target.value }))}
                placeholder="Contenu…" style={{ padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 7, fontSize: '0.85rem', lineHeight: 1.7, resize: 'vertical', fontFamily: 'inherit', outline: 'none', background: 'var(--bg-card)' }} />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setEditingTpl(null)} className="btn btn-secondary btn-sm">Annuler</button>
                <button onClick={handleUpdateTemplate} disabled={savingTpl} className="btn btn-primary btn-sm" style={{ background: '#60A5FA', borderColor: '#60A5FA' }}>
                  {savingTpl ? '…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button
            className="btn btn-primary"
            disabled={!isValid || sending}
            onClick={handleSend}
            style={{ background: isEntretien ? '#4ADE80' : '#60A5FA', borderColor: isEntretien ? '#4ADE80' : '#60A5FA', opacity: isValid && !sending ? 1 : 0.6 }}
          >
            {sending
              ? <><div className="spinner" style={{ width: 14, height: 14 }} /> {isEntretien ? 'Planification…' : 'Envoi…'}</>
              : isEntretien
                ? <><Video size={14} /> Planifier l'entretien</>
                : <><Send size={14} /> Envoyer l'email{ccUsers.length > 0 ? ` + ${ccUsers.length} CC` : ''}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}


function EntretienAOModal({ candidateId, candidateName, candidateEmail, aoId, onClose, onSchedule }) {
  const [title, setTitle] = React.useState(`Entretien — ${candidateName || 'Candidat'}`);
  const [entDate, setEntDate] = React.useState('');
  const [entHour, setEntHour] = React.useState('09');
  const [entMin,  setEntMin]  = React.useState('00');
  const dueDate = entDate ? `${entDate}T${entHour}:${entMin}` : '';
  const [duration, setDuration] = React.useState(60);
  const [description, setDescription] = React.useState('');
  const [allUsers, setAllUsers] = React.useState([]);
  const [invitedUsers, setInvitedUsers] = React.useState([]);
  const [selectedUser, setSelectedUser] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    api.get('/auth/users?is_active=true').then(r => setAllUsers(r.data || [])).catch(() => {});
  }, []);

  const addUser = (email) => {
    if (email && !invitedUsers.includes(email)) {
      setInvitedUsers(prev => [...prev, email]);
    }
    setSelectedUser('');
  };

  const removeUser = (email) => setInvitedUsers(prev => prev.filter(e => e !== email));

  const handleSubmit = async () => {
    if (!dueDate) return;
    setSubmitting(true);
    try {
      await onSchedule({
        ao_id: aoId,
        title: title.trim() || `Entretien — ${candidateName}`,
        due_date: new Date(dueDate).toISOString(),
        duration_minutes: duration,
        description: description.trim() || null,
        invited_users: invitedUsers,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const availableUsers = allUsers.filter(u => u.email && !invitedUsers.includes(u.email));

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: '3px solid #15803d' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(34,197,94,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Video size={18} color="#15803d" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Planifier un entretien Teams</h3>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{candidateName}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {candidateEmail ? (
            <div style={{ padding: '8px 12px', background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 8, fontSize: '0.8125rem', color: '#4ADE80', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Mail size={14} /> Destinataire : <strong>{candidateEmail}</strong>
            </div>
          ) : (
            <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, fontSize: '0.8125rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={14} /> Ce candidat n'a pas d'adresse email — le lien Teams sera créé mais l'email ne sera pas envoyé.
            </div>
          )}

          <div style={{ padding: '8px 12px', background: 'rgba(96,165,250,0.07)', border: '1px solid rgba(96,165,250,0.22)', borderRadius: 8, fontSize: '0.8125rem', color: '#60A5FA', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Video size={13} /> Lien Teams généré automatiquement — le candidat recevra une invitation par email
          </div>

          <div className="input-group" style={{ margin: 0 }}>
            <label className="input-label" style={{ marginBottom: 6 }}>Titre de l'entretien</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="input-group" style={{ margin: 0 }}>
              <label className="input-label" style={{ marginBottom: 6 }}>
                Date et heure <span style={{ color: 'var(--danger-600)' }}>*</span>
                <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>(format 24h)</span>
              </label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="date" value={entDate} onChange={e => setEntDate(e.target.value)}
                  style={{ flex: 2, padding: '10px 8px', border: `1.5px solid ${entDate ? 'rgba(34,197,94,0.35)' : 'var(--border)'}`, borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none' }} />
                <select value={entHour} onChange={e => setEntHour(e.target.value)}
                  style={{ flex: 1, padding: '10px 4px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'var(--surface)' }}>
                  {Array.from({length: 24}, (_, i) => String(i).padStart(2, '0')).map(h => (
                    <option key={h} value={h}>{h}h</option>
                  ))}
                </select>
                <select value={entMin} onChange={e => setEntMin(e.target.value)}
                  style={{ flex: 1, padding: '10px 4px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'var(--surface)' }}>
                  {['00', '15', '30', '45'].map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="input-group" style={{ margin: 0 }}>
              <label className="input-label" style={{ marginBottom: 6 }}>Durée</label>
              <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}
                style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'var(--surface)' }}>
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
                <option value={45}>45 min</option>
                <option value={60}>1h</option>
                <option value={90}>1h30</option>
                <option value={120}>2h</option>
              </select>
            </div>
          </div>

          <div className="input-group" style={{ margin: 0 }}>
            <label className="input-label" style={{ marginBottom: 6 }}>Inviter des membres WIBusiness</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={selectedUser} onChange={(e) => addUser(e.target.value)}
                style={{ flex: 1, padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', fontFamily: 'inherit', outline: 'none', background: 'var(--surface)' }}>
                <option value="">Sélectionner un membre…</option>
                {availableUsers.map(u => (
                  <option key={u.id} value={u.email}>
                    {u.prenom} {u.nom} ({u.role})
                  </option>
                ))}
              </select>
            </div>
            {invitedUsers.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                {invitedUsers.map(email => {
                  const u = allUsers.find(x => x.email === email);
                  return (
                    <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.22)', fontSize: '0.8125rem', color: '#60A5FA', fontWeight: 600 }}>
                      {u ? `${u.prenom} ${u.nom}` : email}
                      <button onClick={() => removeUser(email)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#38BDF8', padding: 0, display: 'flex', alignItems: 'center' }}>
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            {invitedUsers.length > 0 && (
              <div style={{ marginTop: 6, padding: '6px 10px', background: 'rgba(34,197,94,0.08)', border: '1px solid #86EFAC', borderRadius: 6, fontSize: '0.75rem', color: '#4ADE80', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Calendar size={11} /> Ces membres seront ajoutés à la réunion Teams et notifiés dans WIBusiness.
              </div>
            )}
          </div>

          <div className="input-group" style={{ margin: 0 }}>
            <label className="input-label" style={{ marginBottom: 6 }}>Notes / message personnalisé (optionnel)</label>
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Informations complémentaires pour le candidat…"
              style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', lineHeight: 1.6, resize: 'vertical', fontFamily: 'inherit', outline: 'none' }} />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button
            className="btn btn-primary"
            disabled={!dueDate || submitting}
            onClick={handleSubmit}
            style={{ background: '#4ADE80', borderColor: '#4ADE80', opacity: dueDate && !submitting ? 1 : 0.6 }}
          >
            {submitting
              ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Création…</>
              : <><Video size={14} /> Planifier l'entretien</>}
          </button>
        </div>
      </div>
    </div>
  );
}



// ── Modal édition rapport IA ─────────────────────────────────

function EditAIReportModal({ candidate, onClose, onSave }) {
  const c = candidate;
  const [saving, setSaving] = React.useState(false);
  const SEP = String.fromCharCode(10);
  const [strengths, setStrengths] = React.useState((c.strengths || []).join(SEP));
  const [gaps, setGaps] = React.useState((c.gaps || []).join(SEP));
  const [missing, setMissing] = React.useState((c.missing_skills || []).join(SEP));
  const [questions, setQuestions] = React.useState((c.interview_questions || []).join(SEP));
  const [reco, setReco] = React.useState(c.final_recommendation || '');
  const [breakdown, setBreakdown] = React.useState(c.score_breakdown ? { ...c.score_breakdown } : {});
  const [scoreOverall, setScoreOverall] = React.useState(c.score_overall !== undefined ? String(c.score_overall) : '');
  const parseLines = (t) => t.split(SEP).map(s => s.trim()).filter(Boolean);
  const handleSave = async () => {
    setSaving(true);
    await onSave({
      strengths: parseLines(strengths),
      gaps: parseLines(gaps),
      missing_skills: parseLines(missing),
      interview_questions: parseLines(questions),
      final_recommendation: reco.trim() || null,
      score_breakdown: Object.keys(breakdown).length ? breakdown : null,
      score_overall: scoreOverall !== '' ? parseInt(scoreOverall, 10) : null,
    });
    setSaving(false);
  };
  const ta = { width:'100%', padding:'8px 10px', border:'1.5px solid var(--border)', borderRadius:8, fontSize:'0.8125rem', background:'var(--bg-elevated)', color:'var(--text-primary)', fontFamily:'inherit', resize:'vertical', outline:'none', lineHeight:1.6 };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg" style={{ maxHeight:'90vh', display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom:'3px solid #60A5FA', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <Zap size={18} color="#60A5FA"/>
            <div>
              <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700 }}>Modifier le rapport IA de matching</h3>
              <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:2 }}>{c.full_name} - Score actuel : {c.score_overall ?? '-'}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="modal-body" style={{ overflowY:'auto', flex:1, display:'flex', flexDirection:'column', gap:16 }}>
          <div>
            <label style={{ display:'block', fontSize:'0.78rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Score global (0-100)</label>
            <input type="number" min="0" max="100" value={scoreOverall} onChange={e => setScoreOverall(e.target.value)} style={{ ...ta, height:40, resize:'none' }} placeholder="ex: 78"/>
          </div>
          {Object.keys(breakdown).length > 0 && (
            <div>
              <label style={{ display:'block', fontSize:'0.78rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Scores par critere (0-100)</label>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:8 }}>
                {Object.entries(breakdown).map(([key, val]) => (
                  <div key={key} style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:'0.8rem', color:'var(--text-secondary)', width:90, flexShrink:0, textTransform:'capitalize' }}>{key}</span>
                    <input type="number" min="0" max="100" value={val} onChange={e => setBreakdown(prev => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))} style={{ ...ta, height:36, resize:'none', flex:1 }}/>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <label style={{ display:'block', fontSize:'0.78rem', fontWeight:700, color:'var(--success-600)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Points forts - un par ligne</label>
            <textarea rows={4} style={ta} value={strengths} onChange={e => setStrengths(e.target.value)}/>
          </div>
          <div>
            <label style={{ display:'block', fontSize:'0.78rem', fontWeight:700, color:'var(--warning-600)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Ecarts - un par ligne</label>
            <textarea rows={3} style={ta} value={gaps} onChange={e => setGaps(e.target.value)}/>
          </div>
          <div>
            <label style={{ display:'block', fontSize:'0.78rem', fontWeight:700, color:'var(--danger-600)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Competences manquantes - une par ligne</label>
            <textarea rows={2} style={ta} value={missing} onChange={e => setMissing(e.target.value)}/>
          </div>
          <div>
            <label style={{ display:'block', fontSize:'0.78rem', fontWeight:700, color:'var(--info-600)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Questions entretien - une par ligne</label>
            <textarea rows={4} style={ta} value={questions} onChange={e => setQuestions(e.target.value)}/>
          </div>
          <div>
            <label style={{ display:'block', fontSize:'0.78rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6 }}>Recommandation finale</label>
            <textarea rows={2} style={ta} value={reco} onChange={e => setReco(e.target.value)}/>
          </div>
        </div>
        <div className="modal-footer" style={{ flexShrink:0 }}>
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><div className="spinner" style={{ width:14, height:14 }}/> Enregistrement...</> : <><Save size={14}/> Enregistrer</>}
          </button>
        </div>
      </div>
    </div>
  );
}


function ExportSelectModal({ title, subtitle, candidates, onClose, onExport }) {
  const [sel, setSel] = React.useState(new Set(candidates.map(c => String(c.candidate_id))));
  const [loading, setLoading] = React.useState(false);
  const toggle = (id) => setSel(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const allSel = sel.size === candidates.length;

  const handleExport = async () => {
    setLoading(true);
    try { await onExport(sel); } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:520, maxHeight:'80vh', display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom:'2px solid #60A5FA', flexShrink:0 }}>
          <div>
            <h3 style={{ margin:0, fontSize:'1rem', fontWeight:700 }}>{title}</h3>
            <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginTop:3 }}>{subtitle}</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose} disabled={loading}><X size={18}/></button>
        </div>
        <div style={{ padding:'10px 20px 6px', borderBottom:'1px solid var(--border)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>{sel.size} / {candidates.length} profil{sel.size !== 1 ? 's' : ''} selectionnes</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setSel(allSel ? new Set() : new Set(candidates.map(c => String(c.candidate_id))))}>
            {allSel ? 'Tout deselectionner' : 'Tout selectionner'}
          </button>
        </div>
        <div style={{ overflowY:'auto', flex:1 }}>
          {candidates.map((c) => {
            const id = String(c.candidate_id);
            const checked = sel.has(id);
            return (
              <label key={id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 20px', cursor:'pointer', borderBottom:'1px solid var(--border)', background: checked ? 'rgba(96,165,250,0.05)' : 'transparent', transition:'background 0.1s' }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(id)} style={{ width:15, height:15, accentColor:'#60A5FA', flexShrink:0, cursor:'pointer' }}/>
                <div className="avatar avatar-sm" style={{ flexShrink:0 }}>{(c.full_name||'?').slice(0,2).toUpperCase()}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:'0.875rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.full_name || 'Candidat'}</div>
                  <div style={{ fontSize:'0.75rem', color:'var(--text-muted)', display:'flex', gap:8 }}>
                    {c.title && <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.title}</span>}
                    {c.score_overall != null && <span style={{ fontWeight:700, color: c.score_overall >= 75 ? 'var(--success-600)' : c.score_overall >= 50 ? 'var(--warning-600)' : 'var(--danger-600)', flexShrink:0 }}>{c.score_overall}%</span>}
                  </div>
                </div>
              </label>
            );
          })}
        </div>
        <div className="modal-footer" style={{ flexShrink:0 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Annuler</button>
          <button className="btn btn-primary" onClick={handleExport} disabled={sel.size === 0 || loading}>
            {loading
              ? <><div className="spinner" style={{ width:13, height:13 }}/> Chargement des donnees...</>
              : <><Download size={14}/> Exporter Excel ({sel.size})</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Composant TAChatBot ─────────────────────────────────────────────────────
function TAChatBot({ ao, aoId, candidates, onClose, onHardQualValidated }) {
  const STEPS = { SELECT: 'select', QUESTIONS: 'questions', REPORT: 'report' };
  const [step,       setStep]       = React.useState(STEPS.SELECT);
  const [selected,   setSelected]   = React.useState(null);
  const [questions,  setQuestions]  = React.useState([]);
  const [loadingQ,   setLoadingQ]   = React.useState(false);
  const [loadingR,   setLoadingR]   = React.useState(false);
  const [editReport, setEditReport] = React.useState('');
  const [saving,     setSaving]     = React.useState(false);
  const [fromCache,  setFromCache]  = React.useState(false);

  // Candidats dédupliqués
  const uniqueCands = React.useMemo(() => {
    const seen = new Set();
    return candidates.filter(c => {
      if (!c.candidate_id || seen.has(String(c.candidate_id))) return false;
      seen.add(String(c.candidate_id)); return true;
    });
  }, [candidates]);

  // Auto-save debounced quand questions changent
  const saveTimer = React.useRef(null);
  const autoSave = React.useCallback((cand, qs) => {
    if (!cand) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await rtAPI.saveTAQuestions(aoId, cand.candidate_id, qs); }
      catch { /* silent */ }
    }, 1200);
  }, [aoId]);

  const handleSelectCandidate = async (c) => {
    setSelected(c);
    setLoadingQ(true);
    setStep(STEPS.QUESTIONS);
    try {
      // 1. Chercher dans le cache d'abord
      const cacheRes = await rtAPI.getTAQuestions(aoId, c.candidate_id);
      if (cacheRes.data?.exists && cacheRes.data.questions?.length) {
        setQuestions(cacheRes.data.questions.map(q => ({ ...q, reponse_candidat: q.reponse_candidat || '' })));
        setFromCache(true);
        setLoadingQ(false);
        return;
      }
      // 2. Générer via IA (le backend met en cache automatiquement)
      const res = await rtAPI.generateQuestions(aoId, c.candidate_id, 10);
      setQuestions((res.data?.questions || []).map(q => ({ ...q, reponse_candidat: '' })));
      setFromCache(false);
    } catch { toast.error('Erreur generation questions'); setStep(STEPS.SELECT); }
    finally { setLoadingQ(false); }
  };

  const handleQuestionChange = (i, field, value) => {
    setQuestions(prev => {
      const updated = prev.map((q, ii) => ii === i ? { ...q, [field]: value } : q);
      autoSave(selected, updated);
      return updated;
    });
  };

  const handleAddQuestion = () => {
    const updated = [...questions, { question: '', reponse_attendue: '', reponse_candidat: '' }];
    setQuestions(updated);
    autoSave(selected, updated);
  };

  const handleDeleteQuestion = (i) => {
    const updated = questions.filter((_, ii) => ii !== i);
    setQuestions(updated);
    autoSave(selected, updated);
  };

  const handleSaveManual = async () => {
    setSaving(true);
    try {
      await rtAPI.saveTAQuestions(aoId, selected.candidate_id, questions);
      toast.success('Questions sauvegardees');
    } catch { toast.error('Erreur sauvegarde'); }
    finally { setSaving(false); }
  };

  const handleSubmitAnswers = async () => {
    setLoadingR(true);
    // Sauvegarder les réponses avant d'évaluer
    try { await rtAPI.saveTAQuestions(aoId, selected.candidate_id, questions); } catch { /* continue */ }
    try {
      const res = await rtAPI.evaluateAnswers(aoId, selected.candidate_id, questions);
      setEditReport(res.data?.report || '');
      setStep(STEPS.REPORT);
    } catch { toast.error('Erreur generation rapport'); }
    finally { setLoadingR(false); }
  };

  const handleValidate = () => {
    if (!editReport.trim()) { toast.error('Rapport vide'); return; }
    onHardQualValidated(selected.match_id, selected.candidate_id, editReport);
    toast.success('Rapport hard skills soumis !');
  };

  // Styles adaptatifs thème (CSS variables du projet)
  const panel = {
    position:'fixed', bottom:96, right:28, zIndex:9999,
    width:500, maxHeight:'84vh',
    background:'var(--card-bg, #fff)',
    border:'1px solid var(--border, #e5e7eb)',
    borderRadius:16,
    boxShadow:'0 12px 48px rgba(0,0,0,0.18)',
    display:'flex', flexDirection:'column', overflow:'hidden',
    color:'var(--text, #111)',
  };
  const body  = { flex:1, overflowY:'auto', padding:16, background:'var(--card-bg, #fff)' };
  const card  = { marginBottom:14, padding:12, background:'var(--bg-elevated, #f9fafb)', borderRadius:8, border:'1px solid var(--border, #e5e7eb)' };
  const inp   = { width:'100%', padding:'7px 10px', borderRadius:7, border:'1px solid var(--border, #d1d5db)', background:'var(--input-bg, #fff)', color:'var(--text, #111)', fontSize:'0.82rem', boxSizing:'border-box', fontFamily:'inherit' };
  const muted = { fontSize:'0.73rem', color:'var(--text-muted, #6b7280)' };

  return (
    <div style={panel}>
      {/* Header */}
      <div style={{ padding:'13px 16px', borderBottom:'1px solid var(--border, #e5e7eb)', background:'linear-gradient(135deg,#1D4ED8 0%,#60A5FA 100%)', color:'#fff', fontWeight:700, fontSize:'0.95rem', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
        <span style={{ display:'flex', alignItems:'center', gap:8 }}><BrainCircuit size={18}/> Evaluation technique IA</span>
        <button onClick={onClose} style={{ background:'none', border:'none', color:'#fff', cursor:'pointer', fontSize:'1.1rem', lineHeight:1, padding:'2px 6px' }}>✕</button>
      </div>

      <div style={body}>

        {/* STEP 1 — Sélection candidat */}
        {step === STEPS.SELECT && (
          <div>
            <p style={{ ...muted, marginBottom:12 }}>
              Selectionnez un candidat de cet AO pour preparer ses questions techniques :
            </p>
            {uniqueCands.length === 0 && <p style={muted}>Aucun candidat dans cet AO.</p>}
            {uniqueCands.map(c => (
              <div key={c.candidate_id} onClick={() => handleSelectCandidate(c)}
                style={{ padding:'10px 12px', borderRadius:8, border:'1px solid var(--border, #e5e7eb)', marginBottom:6, cursor:'pointer', display:'flex', gap:10, alignItems:'center', background:'var(--bg-elevated, #f9fafb)', transition:'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor='#60A5FA'}
                onMouseLeave={e => e.currentTarget.style.borderColor='var(--border, #e5e7eb)'}
              >
                <div style={{ width:34, height:34, borderRadius:'50%', background:'rgba(96,165,250,0.12)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:'0.8rem', color:'#60A5FA', flexShrink:0 }}>
                  {(c.full_name||'?').slice(0,2).toUpperCase()}
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontWeight:600, fontSize:'0.875rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.full_name || 'Candidat'}</div>
                  <div style={muted}>{c.title || ''}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* STEP 2 — Questions (édition complète) */}
        {step === STEPS.QUESTIONS && (
          <div>
            {loadingQ ? (
              <div style={{ textAlign:'center', padding:'32px 0' }}>
                <div className="spinner" style={{ width:28, height:28, margin:'0 auto 12px' }}/>
                <p style={muted}>Generation des questions...</p>
              </div>
            ) : (
              <>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                  <p style={{ fontWeight:600, fontSize:'0.875rem', margin:0 }}>
                    <span style={{ color:'#60A5FA' }}>{selected?.full_name}</span>
                    {fromCache && <span style={{ ...muted, marginLeft:8, fontWeight:400 }}>✓ questions sauvegardees</span>}
                  </p>
                  <button onClick={handleSaveManual} disabled={saving}
                    style={{ padding:'4px 10px', borderRadius:6, border:'1px solid var(--border, #e5e7eb)', background:'none', cursor:'pointer', fontSize:'0.75rem', color:'var(--text-muted, #6b7280)' }}>
                    {saving ? 'Sauvegarde...' : '💾 Sauvegarder'}
                  </button>
                </div>

                {questions.map((q, i) => (
                  <div key={i} style={card}>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:6, marginBottom:6 }}>
                      <span style={{ fontWeight:600, fontSize:'0.82rem', color:'#60A5FA', flexShrink:0 }}>Q{i+1}</span>
                      <button onClick={() => handleDeleteQuestion(i)}
                        style={{ background:'none', border:'none', cursor:'pointer', color:'#ef4444', fontSize:'0.75rem', padding:'0 4px', flexShrink:0 }} title="Supprimer">
                        ✕
                      </button>
                    </div>
                    <textarea
                      rows={1} placeholder="Question..." value={q.question}
                      onChange={e => { handleQuestionChange(i, 'question', e.target.value); e.target.style.height='auto'; e.target.style.height=e.target.scrollHeight+'px'; }}
                      onFocus={e => { e.target.style.height='auto'; e.target.style.height=e.target.scrollHeight+'px'; }}
                      style={{ ...inp, marginBottom:6, resize:'none', overflow:'hidden', minHeight:36 }}
                    />
                    <textarea
                      rows={1} placeholder="Reponse attendue..." value={q.reponse_attendue}
                      onChange={e => { handleQuestionChange(i, 'reponse_attendue', e.target.value); e.target.style.height='auto'; e.target.style.height=e.target.scrollHeight+'px'; }}
                      onFocus={e => { e.target.style.height='auto'; e.target.style.height=e.target.scrollHeight+'px'; }}
                      style={{ ...inp, marginBottom:6, resize:'none', overflow:'hidden', minHeight:36, color:'var(--text-muted, #6b7280)', fontSize:'0.78rem', fontStyle:'italic' }}
                    />
                    <textarea
                      rows={1} placeholder="Reponse du candidat..." value={q.reponse_candidat}
                      onChange={e => { handleQuestionChange(i, 'reponse_candidat', e.target.value); e.target.style.height='auto'; e.target.style.height=e.target.scrollHeight+'px'; }}
                      onFocus={e => { e.target.style.height='auto'; e.target.style.height=e.target.scrollHeight+'px'; }}
                      style={{ ...inp, resize:'none', overflow:'hidden', minHeight:36 }}
                    />
                  </div>
                ))}

                <button onClick={handleAddQuestion}
                  style={{ width:'100%', padding:'8px', borderRadius:8, border:'2px dashed var(--border, #e5e7eb)', background:'none', cursor:'pointer', fontSize:'0.82rem', color:'var(--text-muted, #6b7280)', marginBottom:10 }}>
                  + Ajouter une question
                </button>

                <div style={{ display:'flex', gap:8 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setStep(STEPS.SELECT); setSelected(null); }}>← Retour</button>
                  <button
                    className="btn btn-primary btn-sm"
                    style={{ flex:1, background:'#60A5FA', borderColor:'#60A5FA', color:'#fff' }}
                    onClick={handleSubmitAnswers}
                    disabled={loadingR || !questions.some(q => q.reponse_candidat.trim())}
                  >
                    {loadingR ? <><div className="spinner" style={{ width:12, height:12, display:'inline-block', marginRight:6 }}/>Generation...</> : 'Generer le rapport IA →'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* STEP 3 — Rapport IA */}
        {step === STEPS.REPORT && (
          <div>
            <p style={{ fontWeight:600, fontSize:'0.875rem', marginBottom:6 }}>
              Rapport Hard Skills — <span style={{ color:'#60A5FA' }}>{selected?.full_name}</span>
            </p>
            <p style={{ ...muted, marginBottom:10 }}>
              Modifiez si necessaire puis validez pour soumettre la prequalification hard skills.
            </p>
            <textarea rows={13} value={editReport} onChange={e => setEditReport(e.target.value)}
              style={{ ...inp, resize:'vertical', fontSize:'0.8rem', lineHeight:1.6 }}
            />
            <div style={{ display:'flex', gap:8, marginTop:10 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setStep(STEPS.QUESTIONS)}>← Modifier reponses</button>
              <button
                className="btn btn-primary btn-sm"
                style={{ flex:1, background:'#16A34A', borderColor:'#16A34A', color:'#fff' }}
                onClick={handleValidate}
                disabled={!editReport.trim()}
              >
                ✓ Valider — Prequalification Hard Skills
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
